// Persist a landing click only after Shopify has granted marketing permission.
(function captureGclidOnLoad() {
  try {
    const state = window.HappyDayConsent?.getState();
    if (state?.ready !== true) return;
    if (state.marketingAllowed !== true) {
      if (state.marketingAllowed === false) localStorage.removeItem('gclid');
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const gclid = urlParams.get('gclid');
    if (gclid) {
      localStorage.setItem('gclid', gclid);
    }
  } catch (error) {
    // Silently fail
  }
})();

// Callback mode detection used to disable discount calls when viewing order confirmation
function isOrderCallbackMode() {
  if (window.orderCallbackMode) return true;
  try {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('t');
    if (t && /^[a-f0-9]{64}$/i.test(t)) return true;
    const parts = window.location.pathname.split('/').filter(Boolean);
    const cbIndex = parts.findIndex(x => x === 'callback');
    if (cbIndex !== -1 && /^[a-f0-9]{64}$/i.test(parts[cbIndex + 1] || '')) return true;
  } catch (_) { }
  return false;
}

// Suppress redirects when voucher extend/exchange flows are active
function isVoucherFlowActive() {
  try {
    const extendPopup = document.querySelector('#extend-voucher-popup');
    const exchangePopup = document.querySelector('#exchange-voucher-popup');
    const extendActive = extendPopup && (extendPopup.dataset?.popupActive === 'true' || !!(extendPopup.offsetParent || extendPopup.getClientRects().length));
    const exchangeActive = exchangePopup && (exchangePopup.dataset?.popupActive === 'true' || !!(exchangePopup.offsetParent || exchangePopup.getClientRects().length));
    return !!(extendActive || exchangeActive);
  } catch (_) {
    return false;
  }
}

// Tracking parameter capture utility
const TrackingCapture = {
  // Get Google Click ID from URL, _gcl_aw cookie, or localStorage
  getGclid() {
    if (this.getConsentStatus()._consent_marketing !== 'GRANTED') return '';
    // First check URL
    const urlParams = new URLSearchParams(window.location.search);
    const gclidFromUrl = urlParams.get('gclid');
    
    if (gclidFromUrl) {
      // Store for future use
      localStorage.setItem('gclid', gclidFromUrl);
      return gclidFromUrl;
    }
    
    // Check _gcl_aw cookie (Google stores gclid here)
    try {
      const gclAwCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_gcl_aw='));
      
      if (gclAwCookie) {
        // Format: _gcl_aw=GCL.TIMESTAMP.GCLID
        const value = gclAwCookie.split('=')[1];
        const parts = value.split('.');
        if (parts.length >= 3) {
          const gclid = parts.slice(2).join('.'); // Everything after the timestamp
          if (gclid) {
            localStorage.setItem('gclid', gclid);
            return gclid;
          }
        }
      }
    } catch (error) {
      // Silently fail
    }
    
    // Fall back to stored value
    return localStorage.getItem('gclid') || '';
  },

  // Get GA Client ID from _ga cookie
  getGaClientId() {
    try {
      const gaCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_ga='));
      
      if (gaCookie) {
        // _ga cookie format: GA1.2.XXXXXXXXXX.YYYYYYYYYY
        // We want the last two parts: XXXXXXXXXX.YYYYYYYYYY
        const parts = gaCookie.split('.');
        if (parts.length >= 4) {
          return `${parts[2]}.${parts[3]}`;
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  },

  // Get GA Session ID from _ga_XXXXXXXXXX cookie
  getGaSessionId() {
    try {
      // Find cookie that starts with _ga_ and contains session data
      const cookies = document.cookie.split('; ');
      for (const cookie of cookies) {
        if (cookie.startsWith('_ga_') && !cookie.startsWith('_ga=')) {
          const value = cookie.split('=')[1];
          if (!value) continue;
          
          // Try new GS2 format first: GS2.1.s1763655975$o1$g0$t1763656125$j60$l0$h48656020
          if (value.includes('GS2')) {
            const gs2Parts = value.split('.');
            if (gs2Parts.length >= 3) {
              // Extract session start timestamp from s parameter
              const dataString = gs2Parts[2];
              const sMatch = dataString.match(/s(\d+)/);
              if (sMatch && sMatch[1]) {
                return sMatch[1]; // Return session start timestamp as ID
              }
            }
          }
          
          // Fallback to old GS1 format: GS1.1.SESSIONID.TIMESTAMP
          if (value.includes('GS1')) {
            const gs1Parts = value.split('.');
            if (gs1Parts.length >= 3) {
              return gs1Parts[2]; // Return the session ID
            }
          }
          
          // Generic fallback - try to extract any GS format
          if (value.includes('GS')) {
            const parts = value.split('.');
            if (parts.length >= 3) {
              return parts[2];
            }
          }
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  },

  // Get User Agent
  getUserAgent() {
    return navigator.userAgent || '';
  },

  // Get current timestamp in ISO format
  getTimestamp() {
    return new Date().toISOString();
  },

  // Use the same Shopify-derived snapshot as GTM. No independent cookie parser.
  getConsentStatus() {
    const denied = {
      '_consent_marketing': 'DENIED',
      '_consent_analytics': 'DENIED',
      '_consent_preferences': 'DENIED',
      '_consent_sale_of_data': 'DENIED'
    };
    try {
      const state = window.HappyDayConsent?.getState();
      if (state?.ready !== true) return denied;
      return {
        '_consent_preferences': state.preferencesAllowed === true ? 'GRANTED' : 'DENIED',
        '_consent_analytics': state.analyticsAllowed === true ? 'GRANTED' : 'DENIED',
        '_consent_marketing': state.marketingAllowed === true ? 'GRANTED' : 'DENIED',
        '_consent_sale_of_data': state.saleOfDataAllowed === true ? 'GRANTED' : 'DENIED'
      };
    } catch (error) {
      return denied;
    }
  },

  // Capture all tracking parameters
  getAllTrackingParams() {
    const consentData = this.getConsentStatus();
    // Optional storage/cookie access must never discard the purpose choices.
    const optional = (read) => { try { return read() || ''; } catch (_) { return ''; } };
    // Clear only our cache on an actual choice, never Shopify/Google cookies.
    optional(() => {
      const state = window.HappyDayConsent?.getState();
      if (state?.ready === true && state.marketingAllowed === false) localStorage.removeItem('gclid');
    });
    return {
      '_gclid': consentData._consent_marketing === 'GRANTED' ? optional(() => this.getGclid()) : '',
      '_ga_client_id': consentData._consent_analytics === 'GRANTED' ? optional(() => this.getGaClientId()) : '',
      '_ga_session_id': consentData._consent_analytics === 'GRANTED' ? optional(() => this.getGaSessionId()) : '',
      '_user_agent': optional(() => this.getUserAgent()),
      '_tracking_timestamp': this.getTimestamp(),
      ...consentData
    };
  },

  // Save tracking params to cart attributes
  saveToCart(expectedToken) {
    // A slow earlier grant must not overwrite a later rejection.
    const previous = this._saveQueue || Promise.resolve();
    this._saveQueue = previous.then(async () => {
      try {
        if (expectedToken && (await CartUtils.fetchCart())?.token !== expectedToken) return false;
        const attributes = this.getAllTrackingParams();
        // Monotonic within this page, even for multiple choices in one millisecond.
        this._observedAt = Math.max(Date.now(), (this._observedAt || 0) + 1);
        attributes._consent_observed_at = String(this._observedAt);
        const result = await window.apiService.post('/cart/update.js', {
          attributes
        });
        if (expectedToken) {
          // Shopify's text/javascript cart response reaches ApiService as JSON text.
          const cart = typeof result === 'string' ? JSON.parse(result) : result;
          if (cart?.token !== expectedToken) return false;
          const current = this.getConsentStatus();
          if (Object.keys(current).some(key => current[key] !== attributes[key])) return false;
          return { ...current, observed_at: this._observedAt };
        }
        return true;
      } catch (error) {
        console.warn('[Tracking] Could not save cart consent attributes.');
        return false;
      }
    });
    return this._saveQueue;
  },

  async prepareCheckoutConsent(cartToken, timeoutMs = 1500) {
    // The handoff is independent of Storefront cache and contains no customer data.
    const failedAt = Math.max(Date.now(), (this._observedAt || 0) + 1);
    const denied = { _consent_marketing: 'DENIED', _consent_analytics: 'DENIED',
      _consent_preferences: 'DENIED', _consent_sale_of_data: 'DENIED', observed_at: failedAt };
    if (!cartToken) return denied;
    let timer;
    try {
      return await Promise.race([
        this.saveToCart(cartToken).then(value => value || denied),
        new Promise(resolve => { timer = setTimeout(() => resolve(denied), timeoutMs); })
      ]);
    } catch (_) {
      return denied;
    } finally {
      clearTimeout(timer);
    }
  }
};

// Account popups and booking pages load cart.js without a <cart-items> element.
// Keep changes on the Shopify cart after Qliro mounts; never reinitialize payment.
if (!TrackingCapture._consentListenerBound) {
  TrackingCapture._consentListenerBound = true;
  document.addEventListener('hd:consent-changed', () => TrackingCapture.saveToCart());
}

// Shared utility for cart fetching to avoid duplication
const CartUtils = {
  async fetchCart() {
    try {
      const result = await apiService.get('/cart.js', {
        headers: {
          'Accept': 'application/json'
        }
      });

      // Handle case where apiService returns a string instead of parsed JSON
      let cartData = result;
      if (typeof result === 'string') {
        try {
          cartData = JSON.parse(result);
        } catch (parseError) {
          return null;
        }
      }

      return cartData;
    } catch (error) {
      return null;
    }
  },

  // Backup cart contents to localStorage for recovery
  async backupCart() {
    try {
      const cart = await this.fetchCart();
      if (!cart || !cart.items || cart.items.length === 0) {
        return null;
      }

      const backup = {
        items: cart.items.map(item => ({
          id: item.id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
          key: item.key,
          title: item.title
        })),
        note: cart.note || '',
        attributes: cart.attributes || {},
        timestamp: Date.now()
      };

      localStorage.setItem('cart_backup', JSON.stringify(backup));
      return backup;
    } catch (error) {
      return null;
    }
  },

  // Restore cart from backup
  async restoreCart() {
    try {
      const backupStr = localStorage.getItem('cart_backup');
      if (!backupStr) {
        return false;
      }

      const backup = JSON.parse(backupStr);
      
      // Check if backup is not too old (30 minutes)
      const backupAge = Date.now() - backup.timestamp;
      if (backupAge > 30 * 60 * 1000) {
        localStorage.removeItem('cart_backup');
        return false;
      }

      if (!backup.items || backup.items.length === 0) {
        localStorage.removeItem('cart_backup');
        return false;
      }

      // Restore items to cart
      const itemsToAdd = backup.items.map(item => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties
      }));

      const response = await window.apiService.post('/cart/add.js', {
        items: itemsToAdd
      });

      if (response) {
        // Recovery data is commercial state, never evidence of current visitor permission.
        const restoredAttributes = Object.fromEntries(Object.entries(backup.attributes || {})
          .filter(([key]) => !key.startsWith('_consent_') &&
            !['_gclid', '_ga_client_id', '_ga_session_id'].includes(key)));
        // Update cart note and attributes if they exist
        if (backup.note || Object.keys(restoredAttributes).length > 0) {
          await window.apiService.post('/cart/update.js', {
            note: backup.note,
            attributes: restoredAttributes
          });
        }

        // Clear backup after successful restoration
        localStorage.removeItem('cart_backup');
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  },

  // Clear cart backup
  clearBackup() {
    try {
      localStorage.removeItem('cart_backup');
    } catch (error) {
      // Silently fail
    }
  },

  isCdeAddon(item) {
    const props = item?.properties || {};
    if (props._delivery_group_id) return false;
    if (props._cde_managed === '1' || props._shipping_speed) return true;
    if (props._item_type === 'shipping' || props._item_type === 'giftbox') return true;
    const ids = document.querySelector('cart-items')?.dataset?.cdeProductIds || '';
    if (ids && item?.product_id && ids.includes(`|${item.product_id}|`)) return true;
    return false;
  },

  /** CDE property stamps change the line key. Buttons may still hold the old key. */
  findLineItem(cart, lineId) {
    const items = cart?.items || [];
    if (!lineId) return null;
    const exact = items.find((i) => i.key === lineId);
    if (exact) return exact;
    const variantId = String(lineId).split(':')[0];
    return items.find((i) =>
      !this.isCdeAddon(i) && String(i.variant_id || i.id) === variantId
    ) || items.find((i) => String(i.variant_id || i.id) === variantId) || null;
  },

  /**
   * The summary's cart subtotal: experiences (data-original-total) plus the active delivery tab's
   * packaging, ribbon and shipping (data-cde-extra), minus applied gift cards (data-gift-card-total).
   */
  renderSubtotal(el) {
    if (!el) return;
    const read = (name) => parseInt(el.getAttribute(name) || '0', 10) || 0;
    const total = Math.max(0, read('data-original-total') + read('data-cde-extra') - read('data-gift-card-total'));
    el.textContent = Shopify.formatMoney(total, window.theme.moneyFormat);
  },

  /**
   * Keep legacy grouped accessories and protection separate from named experiences. CDE-managed
   * extras already have rows in the active delivery tab. This classification is display-only.
   */
  summaryItemKind(item) {
    if (this.isCdeAddon(item)) return null;
    const type = String(item.product_type || '').toLowerCase();
    if (item.gift_card || type === 'giftcard' || type === 'gift_card') return 'gift-cards';
    const addonTypes = ['giftbox', 'shipping', 'bow', 'ribbon', 'protection', 'extended_protection'];
    if (addonTypes.includes(item.properties?._item_type) || item.handle === 'extended-protection') return 'addons';
    return 'experiences';
  },

  /**
   * Named lines show the product name (× quantity when above one) and original line total.
   * Discounts stay in separate rows and are deducted only once. Gift cards keep their own row.
   */
  renderSummaryItemRows(cartData) {
    const lines = { experiences: [], addons: [] };
    let giftCards = 0;
    (cartData?.items || []).forEach((item) => {
      if (!(item.quantity > 0)) return;
      const kind = this.summaryItemKind(item);
      if (kind === 'gift-cards') giftCards += item.original_line_price ?? item.line_price ?? 0;
      else if (kind) lines[kind].push(item);
    });
    const money = (amount) => Shopify.formatMoney(amount, window.theme.moneyFormat);
    Object.entries(lines).forEach(([kind, items]) => {
      const container = document.querySelector(`[data-summary-${kind}]`);
      if (!container) return;
      container.hidden = items.length === 0;
      const rows = items.map((item) => {
        const amount = item.original_line_price ?? item.line_price ?? 0;
        const row = document.createElement('div');
        row.className = 'cde__subtotal-row cde__summary-item';
        row.setAttribute('data-summary-item', item.key || '');
        const title = document.createElement('span');
        title.className = 'u-s3';
        title.setAttribute('data-summary-title', '');
        const name = item.product_title || item.product?.title || item.title || '';
        title.textContent = name + (item.quantity > 1 ? ` × ${item.quantity}` : '');
        const value = document.createElement('span');
        value.className = 'u-s3';
        value.setAttribute('data-summary-value', '');
        value.textContent = money(amount);
        row.append(title, value);
        return row;
      });
      container.replaceChildren(...rows);
    });
    const giftCardRow = document.querySelector('[data-summary-gift-cards]');
    if (giftCardRow) {
      giftCardRow.hidden = giftCards === 0;
      const value = giftCardRow.querySelector('[data-summary-value]');
      if (value) value.textContent = money(giftCards);
    }
  },

  getExperienceTotal(cartData) {
    const addonTotal = (cartData?.items || []).reduce((sum, item) => {
      if (!this.isCdeAddon(item)) return sum;
      return sum + (item.final_line_price ?? item.line_price ?? 0);
    }, 0);
    return Math.max(0, (cartData?.total_price || 0) - addonTotal);
  }
};

class DeliveryEditor {
  constructor(popupSelector) {
    this.popupEl = document.querySelector(popupSelector);
    if (!this.popupEl) {
      return;
    }
    this.delegateElement = new Delegate(document.body);
    this.init();
  }

  init() {
    this.delegateElement.on('click', '[data-action="edit-popup"]', this._handleEditClick.bind(this));

  }


  async _handleEditClick(event, button) {
    // Prevent any default browser action (like following a link)
    event.preventDefault();

    // Get the group ID from the button's data attribute
    const groupId = button.dataset.groupId;

    // Call your main edit logic and wait for it to complete
    await this.startEdit(groupId);
  }


  async startEdit(groupId) {
    const cart = await CartUtils.fetchCart();
    if (!cart) return;

    const mainItem = this._findMainItem(cart, groupId);
    if (!mainItem) {
      return;
    }

    // 1. Reconstruct all the data needed for the edit
    const editData = this._reconstructEditData(cart, mainItem);

    // 2. Tell the <delivery-popup> to prepare itself
    this.popupEl.dataset.editGroup = groupId;
    editData.editGroup = groupId; // Ensure group ID is included in edit data
    if (typeof this.popupEl.restoreAndOpen === 'function') {
      await this.popupEl.restoreAndOpen(editData);
      // restoreAndOpen now handles opening the popup internally
    } else {
      return;
    }
  }

  _reconstructEditData(cart, mainItem) {
    const props = mainItem.properties;
    const groupItems = cart.items.filter(i => i.properties?._delivery_group_id === props._delivery_group_id);
    const giftboxItem = groupItems.find(i => i.properties?._item_type === 'giftbox');
    
    // Note: Bows are now variants of giftboxes, not separate items
    // Get bow info from giftbox item properties
    let bowVariantId = null;
    let bowVariantName = null;
    let bowPrice = 0;
    let giftboxBasePrice = 0;

    if (giftboxItem) {
      bowVariantId = String(giftboxItem.variant_id);
      bowVariantName = giftboxItem.properties?.Bow || null;
      
      // To get the bow price, we need to calculate: giftboxVariantPrice - baseVariantPrice
      // For now, we'll fetch this from the product data
      // The popup will recalculate prices when loading bow variants
      giftboxBasePrice = giftboxItem.original_price;
      // Bow price will be recalculated by the popup based on variant selection
    }

    const editData = {
      variantId: mainItem.variant_id,
      quantity: mainItem.quantity,
      isGiftbox: props._giftbox === 'true',
      collectionHandle: props._giftbox_collection || '',
      state: {
        choices: {
          delivery_type: props._delivery_type,
          delivery_person: props._delivery_person,
          giftbox_variant: bowVariantId,  // This is the selected bow variant
          bow_variant: bowVariantId,       // Same as giftbox_variant (bow is variant of giftbox)
          bow_variant_name: bowVariantName,
          giftbox_product: giftboxItem ? String(giftboxItem.product_id) : null,
          giftbox_product_handle: giftboxItem ? giftboxItem.handle : null
        },
        prices: {
          giftbox: giftboxBasePrice,
          bow: bowPrice,  // Will be recalculated by popup
        },
        packAll: props._pack_all === 'true'
      },
      shipping: {
        recipient_name: props._recipient_name || '',
        recipient_email: props._recipient_email || '',
        recipient_address: props._recipient_address || '',
        recipient_city: props._recipient_city || '',
        recipient_zip: props._recipient_zip || '',
        // Greeting is stored in giftbox item if present, otherwise in main item
        greeting_message: giftboxItem?.properties?.Greeting || mainItem.properties?.Greeting || '',
        schedule_delivery: Boolean(props._scheduled_delivery),
        delivery_datetime: props._scheduled_date || ''
      }
    };
    
    return editData;
  }

  _findMainItem(cart, groupId) {
    return cart.items.find(item =>
      item.properties?._delivery_group_id === groupId && !item.properties?._item_type
    );
  }
}

// --- Cart Discount Manager ---
class CartDiscountManager {
  constructor(cartItems, config = {}) {
    this.cartItems = cartItems;
    this.element = cartItems.element;
    this.isProcessingDiscount = false;

    // Configure selectors - use defaults for cart page, custom for popups
    this.selectors = {
      codeInput: config.codeInputSelector || '#cart-discount-code',
      statusDiv: config.statusDivSelector || '#cart-discount-status',
      appliedCodes: config.appliedCodesSelector || '#cart-applied-codes',
      applyBtn: config.applyBtnSelector || '[data-action="apply-discount"]'
    };

    // Initialize FormValidator
    this.initFormValidator();
  }

  initFormValidator() {
    const codeInput = this.element.querySelector(this.selectors.codeInput);
    const inputGroup = codeInput?.closest('.collapsible-form__input-group');
    
    if (!inputGroup || !codeInput) {
      return;
    }

    // Initialize FormValidator - configured to only validate on manual call (button click)
    this.formValidator = new FormValidator(inputGroup, {
      validateOnInput: false,  // Don't validate while typing
      validateOnBlur: false,   // Don't validate on blur
      errorClass: 'field--error',
      fieldSelector: '.field',
      buttonSelector: this.selectors.applyBtn,
      errorOutputSelector: inputGroup.querySelector('.input-validation'),
      showErrorsOnSubmitOnly: true,  // Only show errors when explicitly validated
      disableButton: false
    });

    // Add custom validator for discount codes (only 10-character check)
    if (!codeInput.id) {
      codeInput.id = 'cart-discount-code';
    }

    this.formValidator.addValidator(
      codeInput.id,
      this.validateDiscountCode.bind(this),
      ''
    );
  }

  validateDiscountCode(value) {
    // Only check if this is an old gift card (10 characters)
    // Don't validate empty values or anything else - only the 10-character rule
    if (value && value.trim().length === 10) {
      const message = TranslationUtils.get(
        'sections.cart.discount_old_giftcard_error_html',
        'This looks like an old gift card. <a href="/pages/convert" style="color: var(--primary_text); text-decoration: underline;">Convert it here</a> to use it.'
      );
      return message; // Return error message string
    }

    return true; // Return true for valid
  }

  hasCartItemsForStatusCheck() {
    const itemCount = Number(this.cartItems.currentCartItemCount);
    return Number.isFinite(itemCount) && itemCount > 0;
  }

  bindEvents(delegateElement) {
    delegateElement.on('click', '[data-action="apply-discount"]', this.handleDiscountApply.bind(this));
    delegateElement.on('click', '[data-action="remove-discount"]', this.handleDiscountRemove.bind(this));
    delegateElement.on('keypress', '[data-action="discount-code-keypress"]', this.handleDiscountKeypress.bind(this));

  }

  handleDiscountKeypress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const applyBtn = this.element.querySelector(this.selectors.applyBtn);
      if (applyBtn) {
        applyBtn.click();
      }
    }
  }

  async handleDiscountApply(event) {
    if (event) {
      event.preventDefault();
    }

    if (this.isProcessingDiscount) return;

    const codeInput = this.element.querySelector(this.selectors.codeInput);
    const applyBtn = event.target;

    if (!codeInput || !applyBtn) {
      return;
    }

    // Clean up the code: trim whitespace and remove dashes
    const code = codeInput.value.trim().replace(/-/g, '');
    
    // Clear any previous validation errors using FormValidator's method
    if (this.formValidator) {
      this.formValidator.clearErrors();
    }
    
    if (!code) {
      this.showDiscountStatus(TranslationUtils.get('sections.cart.discount_enter_code', 'Please enter a discount code or gift card'), 'error');
      return;
    }

    // Validate using FormValidator (only on button click)
    if (this.formValidator) {
      this.formValidator.markSubmitted(); // Mark as submitted to trigger validation
      if (!this.formValidator.validateForm(false)) { // false = not silent, show errors
        return;
      }
    }

    if (!this.cartItems.currentCartToken) {
      this.showDiscountStatus(TranslationUtils.get('sections.cart.discount_cart_not_initialized', 'Cart not initialized'), 'error');
      return;
    }

    this.isProcessingDiscount = true;
    this.manualMutationVersion = (this.manualMutationVersion || 0) + 1;
    this.processingDone = new Promise((resolve) => { this.finishProcessing = resolve; });
    applyBtn.disabled = true;
    const originalText = applyBtn.textContent;
    applyBtn.textContent = TranslationUtils.get('sections.cart.applying', 'Applying...');

    try {
      if (isOrderCallbackMode()) {
        this.showDiscountStatus(TranslationUtils.get('sections.cart.not_available_in_callback', 'Discounts are not available on order confirmation.'), 'error');
        return;
      }
      // If auto-ADD already reached the server, let it finish before the customer's request.
      await this.cartItems.campaignAutoApply?.pendingPost?.catch(() => {});
      const data = await window.apiService.post(`${window.proxyPath}/checkout-discounts`, {
        action: 'ADD',
        code: code,
        cart_token: this.cartItems.currentCartToken
      });

      // GTM: custom_discount_code_applied
      if (window.dataLayer) {
        dataLayer.push({ event: 'custom_discount_code_applied', discount_code: code, success: !(data.success === false || data.businessRuleViolation === true) });
      }

      // Handle response based on success flag
      if (data.success === false || data.businessRuleViolation === true) {
        // Handle error with specific error code handling
        const errorResult = this.handleDiscountError(data);
        this.showDiscountStatus(errorResult.message, 'error');
        
        // Handle auto-removal case: if a previous gift card exists, offer to remove it
        if (data.errorCode === 'GIFT_CARD_ALREADY_APPLIED' && data.appliedGiftCards?.length > 0) {
          // Store the code to retry after removal
          const retryCode = code;
          // Could implement auto-retry logic here if desired
        }
      } else {
        // Success - handle the response
        let successMessage = TranslationUtils.get('sections.cart.discount_code_applied', 'Code applied successfully!');
        
        // Check if previous gift card was auto-removed
        if (data.message && data.message.includes('Previous gift card')) {
          successMessage = data.message; // Show the backend message about replacement
        }
        
        // Check for gift card capping
        if (data.cappingInfo && data.cappingInfo.willBeCapped) {
          // Store capping info in sessionStorage for cart display
          try { sessionStorage.setItem('giftCardCappingInfo', JSON.stringify(data.cappingInfo)); } catch (_) { /* Storage is optional. */ }
          
          // Show warning message about capping
          if (data.warning && data.warning.message) {
            this.showDiscountStatus(data.warning.message, 'warning');
          } else {
            // Fallback: Build warning message from cappingInfo
            const originalAmount = Shopify.formatMoney(data.cappingInfo.originalAmount * 100, window.theme.moneyFormat);
            const cappedAmount = Shopify.formatMoney(data.cappingInfo.cappedAmount * 100, window.theme.moneyFormat);
            const restrictedAmount = Shopify.formatMoney(data.cappingInfo.restrictedTotal * 100, window.theme.moneyFormat);
            
            const warningMessage = TranslationUtils.get('sections.cart.gift_card_capped_warning', 
              'Gift card ({{ original_amount }}) will be partially applied ({{ capped_amount }}) to eligible products only. Restricted products ({{ restricted_amount }}) will be charged at full price.')
              .replace('{{ original_amount }}', originalAmount)
              .replace('{{ capped_amount }}', cappedAmount)
              .replace('{{ restricted_amount }}', restrictedAmount);
            
            this.showDiscountStatus(warningMessage, 'warning');
          }
        } else {
          // No capping - clear any stored capping info and show normal success
          try { sessionStorage.removeItem('giftCardCappingInfo'); } catch (_) { /* Storage is optional. */ }
          this.showDiscountStatus(successMessage, 'success');
        }
        
        codeInput.value = '';

        // Update applied codes display with the response data
        if (data.appliedGiftCards) {
          this.updateAppliedCodes(data);
        }

        // Handle Qliro snippet if present (only when gift card is successfully applied)
        if (this.cartItems.qliroManager) {
          this.cartItems.qliroManager.markSnippetStale({ reload: true });
        }

        // Clear any cached original total to ensure fresh calculation after discount
        const subtotalElement = this.cartItems.element.querySelector('.cart-totals__row--total .cart-totals__value');
        if (subtotalElement) {
          subtotalElement.removeAttribute('data-original-total');
        }

        // Use HTML refresh for discounts to update server-side discount display
        await this.cartItems.refreshAfterUpdate(true);
      }
    } catch (error) {
      // Handle network errors
      const errorMessage = error.message && error.message.includes('Network') 
        ? TranslationUtils.get('sections.cart.discount_network_error', 'Network error. Please check your connection and try again.')
        : TranslationUtils.get('sections.cart.discount_apply_error', 'Error applying code. Please try again.');
      this.showDiscountStatus(errorMessage, 'error');
    } finally {
      this.isProcessingDiscount = false;
      this.finishProcessing?.();
      applyBtn.disabled = false;
      applyBtn.textContent = originalText;
    }
    // A failed manual attempt must also release a campaign skipped during processing.
    await this.cartItems.applyCampaignCode?.();
  }

  /**
   * Handle discount error responses with specific error codes
   * @param {Object} data - Response data from API
   * @returns {Object} - {message: string, canRetry: boolean, errorCode: string}
   */
  handleDiscountError(data) {
    const errorCode = data.errorCode;
    const baseMessage = data.message || TranslationUtils.get('sections.cart.discount_code_invalid', 'Code not found or invalid');

    // Map error codes to user-friendly messages with suggestions
    const errorMessages = {
      'GIFT_CARD_EXPIRED': {
        message: data.message || TranslationUtils.get('sections.cart.gift_card_expired', 'This gift card has expired.'),
        suggestion: TranslationUtils.get('sections.cart.gift_card_expired_suggestion', 'Please use a different gift card.'),
        canRetry: false
      },
      'GIFT_CARD_DISABLED': {
        message: data.message || TranslationUtils.get('sections.cart.gift_card_disabled', 'This gift card has been disabled.'),
        suggestion: TranslationUtils.get('sections.cart.gift_card_disabled_suggestion', 'Please contact support if you believe this is an error.'),
        canRetry: false
      },
      'GIFT_CARD_NO_BALANCE': {
        message: data.message || TranslationUtils.get('sections.cart.gift_card_no_balance', 'This gift card has no remaining balance.'),
        suggestion: TranslationUtils.get('sections.cart.gift_card_no_balance_suggestion', 'Please use a different payment method.'),
        canRetry: false
      },
      'GIFT_CARD_NOT_FOUND': {
        message: data.message || TranslationUtils.get('sections.cart.gift_card_not_found', 'Gift card not found or invalid.'),
        suggestion: TranslationUtils.get('sections.cart.gift_card_not_found_suggestion', 'Please check the code and try again.'),
        canRetry: true
      },
      'GIFT_CARD_CODE_NOT_FOUND': {
        message: data.message || TranslationUtils.get('sections.cart.gift_card_code_not_found', 'Cannot apply gift card code, wrong code.'),
        suggestion: TranslationUtils.get('sections.cart.gift_card_code_not_found_suggestion', 'Use the gift card link sent to your email instead.'),
        canRetry: false
      },
      'GIFT_CARD_ALREADY_APPLIED': {
        message: data.message || TranslationUtils.get('sections.cart.gift_card_already_applied', 'Only one gift card can be applied at a time.'),
        suggestion: TranslationUtils.get('sections.cart.gift_card_already_applied_suggestion', 'Please remove the existing gift card first.'),
        canRetry: true
      },
      'DISCOUNT_CODE_NOT_VALID': {
        message: TranslationUtils.get('sections.cart.discount_code_not_valid', 'Discount code is not valid or cannot be applied to this cart.'),
        suggestion: TranslationUtils.get('sections.cart.discount_code_not_valid_suggestion', 'Please check the code and ensure your cart meets the requirements.'),
        canRetry: true
      },
      'VALIDATION_ERROR': {
        message: data.message || TranslationUtils.get('sections.cart.validation_error', 'Failed to validate gift card.'),
        suggestion: TranslationUtils.get('sections.cart.validation_error_suggestion', 'Please try again later.'),
        canRetry: true
      },
      'INTERNAL_SERVER_ERROR': {
        message: TranslationUtils.get('sections.cart.internal_server_error', 'Something went wrong on our end.'),
        suggestion: TranslationUtils.get('sections.cart.internal_server_error_suggestion', 'Please try again later.'),
        canRetry: true
      },
      'NOT_FOUND': {
        message: data.message || TranslationUtils.get('sections.cart.cart_not_found', 'Cart expired or invalid.'),
        suggestion: TranslationUtils.get('sections.cart.cart_not_found_suggestion', 'Please refresh the page and try again.'),
        canRetry: false
      }
    };

    // Get error info or use generic message
    const errorInfo = errorMessages[errorCode] || {
      message: baseMessage,
      suggestion: '',
      canRetry: true
    };

    // Combine message with suggestion if available
    let fullMessage = errorInfo.message;
    if (errorInfo.suggestion) {
      fullMessage += ' ' + errorInfo.suggestion;
    }

    return {
      message: fullMessage,
      canRetry: errorInfo.canRetry,
      errorCode: errorCode || 'UNKNOWN'
    };
  }

  async handleDiscountRemove(event) {
    if (event) {
      event.preventDefault();
    }

    if (this.isProcessingDiscount) return;

    const removeBtn = event.target;
    const codeToRemove = removeBtn.dataset.codeToRemove;
    const giftCardIdToRemove = removeBtn.dataset.giftcardIdToRemove;

    if (!codeToRemove && !giftCardIdToRemove) {
      return;
    }

    if (!this.cartItems.currentCartToken) {
      this.showDiscountStatus(TranslationUtils.get('sections.cart.discount_cart_not_initialized', 'Cart not initialized'), 'error');
      return;
    }

    this.isProcessingDiscount = true;
    this.manualMutationVersion = (this.manualMutationVersion || 0) + 1;
    this.processingDone = new Promise((resolve) => { this.finishProcessing = resolve; });
    // Record the intent before any refresh (or an ambiguous REMOVE response) can auto-add again.
    if (codeToRemove) this.cartItems.campaignAutoApply?.rememberRemoval(codeToRemove);
    removeBtn.disabled = true;
    const originalText = removeBtn.textContent;
    removeBtn.textContent = TranslationUtils.get('sections.cart.removing', 'Removing...');
    let removalSucceeded = false;

    try {
      if (isOrderCallbackMode()) {
        this.showDiscountStatus(TranslationUtils.get('sections.cart.not_available_in_callback', 'Discounts are not available on order confirmation.'), 'error');
        return;
      }
      let requestBody;
      if (codeToRemove) {
        requestBody = {
          action: 'REMOVE',
          code: codeToRemove,
          cart_token: this.cartItems.currentCartToken
        };
      } else {
        // For gift cards, use the same REMOVE action but with gift_card_id
        requestBody = {
          action: 'REMOVE',
          code: giftCardIdToRemove,
          cart_token: this.cartItems.currentCartToken
        };
      }

      await this.cartItems.campaignAutoApply?.pendingPost?.catch(() => {});
      const data = await window.apiService.post(`${window.proxyPath}/checkout-discounts`, requestBody);

      // Handle response based on success flag
      if (data.success === false) {
        // Handle error with specific error code handling
        const errorResult = this.handleDiscountError(data);
        this.showDiscountStatus(errorResult.message, 'error');
      } else {
        // Success - show confirmation message
        this.showDiscountStatus(TranslationUtils.get('sections.cart.discount_removed', 'Code removed successfully!'), 'success');

        // Clear gift card capping info when gift card is removed
        try { sessionStorage.removeItem('giftCardCappingInfo'); } catch (_) { /* Storage is optional. */ }

        // Handle Qliro snippet if present (update checkout after removal)
        if (this.cartItems.qliroManager) {
          this.cartItems.qliroManager.markSnippetStale({ reload: true });
        }

        // Clear any cached original total to ensure fresh calculation after discount removal
        const subtotalElement = this.cartItems.element.querySelector('.cart-totals__row--total .cart-totals__value');
        if (subtotalElement) {
          subtotalElement.removeAttribute('data-original-total');
        }

        // Use HTML refresh for discount removal to update server-side display
        await this.cartItems.refreshAfterUpdate(true);
        removalSucceeded = true;
      }
    } catch (error) {
      // Handle network errors
      const errorMessage = error.message && error.message.includes('Network')
        ? TranslationUtils.get('sections.cart.discount_network_error', 'Network error. Please check your connection and try again.')
        : TranslationUtils.get('sections.cart.discount_remove_error', 'Error removing code. Please try again.');
      this.showDiscountStatus(errorMessage, 'error');
    } finally {
      this.isProcessingDiscount = false;
      this.finishProcessing?.();
      removeBtn.disabled = false;
      removeBtn.textContent = originalText;
    }

    // Removing a gift card or another code releases the campaign. An explicitly removed campaign
    // remains opted out. Run after finally because the HTML refresh skips while processing is set.
    if (removalSucceeded) await this.cartItems.applyCampaignCode?.();
  }

  showDiscountStatus(message, type) {
    // Use toast notifications for discount status messages
    if (toastNotifications) {
      if (type === 'error') {
        toastNotifications.error(message);
      } else if (type === 'success') {
        toastNotifications.success(message);
      } else {
        toastNotifications.info(message);
      }
    }
  }

  updateAppliedCodes(responseData) {
    const container = this.element.querySelector(this.selectors.appliedCodes);

    // --- Extract applied gift cards from all possible response shapes ---
    let appliedGiftCards = [];

    // 1. GET_STATUS (new shape)
    if (responseData?.data?.cart?.appliedGiftCards) {
      appliedGiftCards = responseData.data.cart.appliedGiftCards || [];
    }
    // 2. ADD/REMOVE (new shape)
    else if (responseData?.appliedGiftCards) {
      appliedGiftCards = responseData.appliedGiftCards || [];
    }
    // 3. Fallback: discounts.giftCards (legacy/mixed)
    else if (responseData?.discounts?.giftCards) {
      appliedGiftCards = responseData.discounts.giftCards || [];
    }

    // If null, treat as empty array
    if (!Array.isArray(appliedGiftCards)) {
      appliedGiftCards = [];
    }
    // CampaignAutoApply waits while a gift card is applied.
    this.appliedGiftCards = appliedGiftCards;
    if (!container) return;

    // --- Preserve existing server-side discount code HTML ---
    const existingDiscounts = container.querySelectorAll('.cart-totals__row--discount:not([data-gift-card])');
    let existingDiscountsHTML = '';
    existingDiscounts.forEach(discount => {
      existingDiscountsHTML += discount.outerHTML;
    });

    // --- Check for gift card capping info ---
    let cappingInfo = null;
    try {
      const cappingInfoStr = sessionStorage.getItem('giftCardCappingInfo');
      if (cappingInfoStr) {
        cappingInfo = JSON.parse(cappingInfoStr);
      }
    } catch (error) {
      // Silently fail - capping info not available
    }

    // --- Generate gift card HTML ---
    let giftCardsHTML = '';
    let totalGiftCardAmount = 0;

    appliedGiftCards.forEach((gc, index) => {
      // Use shared utility for gift card data extraction
      const { lastChars, giftCardId, amount } = SharedCartUtils.extractGiftCardData(gc);

      // Use capped amount if available, otherwise use original amount
      let displayAmount = amount;
      let isCapped = false;
      let cappedBadgeHTML = '';
      
      if (cappingInfo && cappingInfo.willBeCapped) {
        // Use the capped amount instead of original amount
        displayAmount = cappingInfo.cappedAmount * 100; // Convert from SEK to cents
        isCapped = true;
        
        // Add "Partially applied" badge
        const cappedBadgeText = TranslationUtils.get('sections.cart.gift_card_capped_badge', 'Partially applied');
        cappedBadgeHTML = `<span class="gift-card-capped-badge" style="margin-left: 8px; padding: 2px 6px; background: var(--warning-color, #fbbf24); color: var(--warning-text, #000); border-radius: 4px; font-size: 0.75rem; font-weight: 500;">${cappedBadgeText}</span>`;
      }

      totalGiftCardAmount += displayAmount;

      const giftCardDisplay = displayAmount > 0 ? `-${Shopify.formatMoney(displayAmount, window.theme.moneyFormat)}` : '';
      giftCardsHTML += `
        <div class="cart-totals__row cart-totals__row--discount" data-gift-card="true" data-is-capped="${isCapped}">
          <span class="cart-totals__label u-s3">
            Gift Card (...${lastChars})${cappedBadgeHTML}
            <button class="cart-discount__remove-link u-s4" data-action="remove-discount" data-giftcard-id-to-remove="${giftCardId}" title="Remove gift card ending in ${lastChars}">Remove code</button>
          </span>
          <span class="cart-totals__value cart-totals__value--discount u-s3">${giftCardDisplay}</span>
        </div>
      `;
    });

    // --- Combine server-side discounts + client-side gift cards ---
    container.innerHTML = existingDiscountsHTML + giftCardsHTML;

    // --- Show or hide container based on content ---
    const hasDiscounts = existingDiscounts.length > 0 || appliedGiftCards.length > 0;
    container.style.display = hasDiscounts ? 'block' : 'none';

    // --- Update cart total to account for gift cards ---
    this.updateCartTotalWithGiftCards(totalGiftCardAmount);
  }

  updateCartTotalWithGiftCards(giftCardAmount) {
    // Find the subtotal element
    const subtotalElement = this.element.querySelector('.cart-totals__row--total .cart-totals__value');
    if (!subtotalElement) return;

    // If there are no gift cards to apply, don't modify the subtotal
    // This prevents overwriting the correct discounted total from server-side discount codes
    if (giftCardAmount === 0) {
      // Still store the original total if not already set, for future gift card applications
      if (!subtotalElement.getAttribute('data-original-total')) {
        const currentText = subtotalElement.textContent;
        const cleanText = currentText.replace(/[^\d.]/g, '');
        const numericValue = parseFloat(cleanText);
        const originalTotal = Math.round(numericValue * 100);
        if (originalTotal > 0) {
          subtotalElement.setAttribute('data-original-total', originalTotal.toString());
        }
      }
      // data-original-total already carries server-side discount codes; only drop a removed gift card.
      subtotalElement.setAttribute('data-gift-card-total', '0');
      if (subtotalElement.getAttribute('data-original-total')) CartUtils.renderSubtotal(subtotalElement);
      return;
    }

    // Get the original cart total from Shopify (already includes discount allocations)
    const originalTotalText = subtotalElement.getAttribute('data-original-total');
    let originalTotal = 0;

    if (originalTotalText) {
      originalTotal = parseInt(originalTotalText, 10);
    } else {
      // First time: extract from current text and store original
      const currentText = subtotalElement.textContent;

      // Parse formatted money string like "1 392 kr" -> 139200 cents
      // Remove spaces and currency, then multiply by 100 to get cents
      const cleanText = currentText.replace(/[^\d.]/g, ''); // Keep digits and decimal points
      const numericValue = parseFloat(cleanText);
      originalTotal = Math.round(numericValue * 100); // Convert to cents

      if (originalTotal > 0) {
        subtotalElement.setAttribute('data-original-total', originalTotal.toString());
      } else {
        return;
      }
    }

    // Both originalTotal and giftCardAmount should be in cents
    // Final total: cart.total_price (with discounts) + delivery extras - gift card amounts
    subtotalElement.setAttribute('data-gift-card-total', String(giftCardAmount));
    CartUtils.renderSubtotal(subtotalElement);

  }

  updateAppliedCodesFromCart(cartData) {

    // Since we're using hybrid approach (server-side discounts + client-side gift cards),
    // we only need to fetch gift cards from our custom endpoint
    // Server-side discounts are already rendered by Liquid
    this.loadExistingDiscounts();
  }

  async loadExistingDiscounts() {

    if (!this.cartItems.currentCartToken) {
      return;
    }

    if (!this.hasCartItemsForStatusCheck()) {
      this.clearAppliedCodesDisplay();
      this.appliedGiftCards = [];
      this.statusUnavailable = false;
      return true;
    }

    try {
      if (isOrderCallbackMode()) {
        return; // Skip polling discounts in callback mode
      }
      // Fetch current cart state from backend to get applied discounts
      const data = await window.apiService.post(`${window.proxyPath}/checkout-discounts`, {
        action: 'GET_STATUS',
        cart_token: this.cartItems.currentCartToken
      });

      // Check if the request was successful
      if (data.success === false) {
        // Don't show error to user for background status checks
        this.statusUnavailable = true;
        this.statusRetryable = CampaignAutoApply.isTransientFailure(data);
        console.warn('[Cart] Discount status was refused');
        return false;
      }

      // Update the applied codes display
      this.updateAppliedCodes(data);
      this.statusUnavailable = false;
      return true;

    } catch (error) {
      this.statusUnavailable = true;
      const status = Number(error?.status);
      this.statusRetryable = !status || status >= 500 || status === 408 || status === 429;
      console.warn('[Cart] Could not load discount status');
      return false;
    }
  }

  clearDiscountForm() {
    // Clear discount input
    const codeInput = this.element.querySelector(this.selectors.codeInput);
    if (codeInput) {
      codeInput.value = '';
    }

    // Hide status message
    const statusDiv = this.element.querySelector(this.selectors.statusDiv);
    if (statusDiv) {
      statusDiv.style.display = 'none';
      statusDiv.textContent = '';
    }

    // Clear applied codes
    const appliedCodes = this.element.querySelector(this.selectors.appliedCodes);
    if (appliedCodes) {
      appliedCodes.innerHTML = '';
    }
  }

  clearAppliedCodesDisplay() {
    const statusDiv = this.element.querySelector(this.selectors.statusDiv);
    if (statusDiv) {
      statusDiv.style.display = 'none';
      statusDiv.textContent = '';
    }

    const appliedCodes = this.element.querySelector(this.selectors.appliedCodes);
    if (appliedCodes) {
      appliedCodes.innerHTML = '';
    }
  }
}

// --- Cart Qliro Manager ---
// Qliro is only updated once the customer has stopped changing things for this long; every change
// restarts the wait. "Updating checkout..." shows immediately, so the wait costs nothing visible.
const QLIRO_UPDATE_PAUSE_MS = 1000;

class CartQliroManager {
  constructor(element, options = {}) {
    this.element = element;
    this.isQliroInitializing = false;
    this.currentCartToken = null;
    this.checkoutContext = options.context || 'default';
    this.staleSessionRetryCount = 0;
    this.maxStaleSessionRetries = 1; // Only retry once to avoid loops
    this.snippetStale = true;
    this._ensurePromise = null;
    // Qliro order the widget on the page was rendered for; null while none is mounted.
    this.mountedOrderId = null;
    this._refreshTimer = null;
    this._refreshWanted = false;
    this._forceReload = false;
    this._onOrderUpdated = null;
    this.initialPreparation = false;
  }

  beginInitialPreparation() {
    this.initialPreparation = true;
    clearTimeout(this._refreshTimer);
  }

  endInitialPreparation() {
    if (!this.initialPreparation) return;
    this.initialPreparation = false;
    clearTimeout(this._refreshTimer);
    // All preparation writes are included in the first snapshot; none need a second mount.
    this._forceReload = false;
    this._refreshWanted = false;
  }

  async fetchCart() {
    return await CartUtils.fetchCart();
  }

  // Handle stale Qliro session by backing up cart, clearing cookies, and restoring
  async handleStaleQliroSession() {
    try {
      // Step 1: Backup current cart contents before clearing
      const backup = await CartUtils.backupCart();

      // Step 2: Clear cart and cookies using existing utility
      await SharedCartUtils.clearCartAndCookies();

      // Small delay to ensure cookies are cleared
      await new Promise(resolve => setTimeout(resolve, 300));

      // Step 3: Restore cart contents if backup exists
      if (backup && backup.items && backup.items.length > 0) {
        const restored = await CartUtils.restoreCart();
        
        if (restored) {
          // Step 4: Get new cart token
          const newCart = await this.fetchCart();
          if (newCart && newCart.token) {
            this.currentCartToken = newCart.token;
            
            // GTM: custom_qliro_session_recovered
            if (window.dataLayer) {
              dataLayer.push({ event: 'custom_qliro_session_recovered', cart_value: (newCart.total_price || 0) / 100 });
            }
            // Recovery successful - no notification needed, will auto-reinitialize
            return true;
          }
        } else {
          if (toastNotifications) {
            toastNotifications.error('Unable to restore cart. Please refresh the page.');
          }
          return false;
        }
      } else {
        // No notification needed - cart was empty anyway
        return true;
      }

      return false;
    } catch (error) {
      if (toastNotifications) {
        toastNotifications.error('Failed to refresh checkout. Please reload the page.');
      }
      return false;
    }
  }

  // Check if error response indicates stale Qliro session
  // Any non-success response (not 200) is considered a stale session
  isStaleQliroSession(errorData) {
    if (!errorData) return false;
    
    // Check for specific Qliro error response code
    if (errorData.responseCode === 'ORDER_EXPIRED') {
      return true;
    }
    
    // If success is explicitly false, it's a stale session
    if (errorData.success === false) return true;
    
    // Check for error messages that indicate stale session
    // Common patterns: 
    // - "too much time has passed"
    // - "Unable to update as too much time"
    // - "order is already completed"
    // - "admin api should be used"
    const errorMessage = errorData.message || errorData.error || '';
    if (typeof errorMessage === 'string') {
      const lowerMessage = errorMessage.toLowerCase();
      
      if (lowerMessage.includes('too much time') || 
          lowerMessage.includes('unable to update') ||
          lowerMessage.includes('order is already completed') ||
          lowerMessage.includes('admin api should be used')) {
        return true;
      }
    }
    
    // If there's an error or message property (general catch-all)
    if (errorData.error || errorData.message) return true;
    
    return false;
  }

  /**
   * The cart changed. A checkout already on the page is updated in place once the customer pauses;
   * without one (still loading, or its first load failed) it is loaded then. `reload` rebuilds the
   * widget instead, for changes made to the Qliro order by another request (discount codes, gift cards). `immediate` skips the
   * debounce and returns the refresh, for a caller that holds the widget locked until it is done.
   */
  markSnippetStale({ reload = false, immediate = false } = {}) {
    this.snippetStale = true;
    if (reload) this._forceReload = true;
    if (this.initialPreparation) return Promise.resolve();
    if (immediate) return this.ensureSnippet();
    this.setUpdating(true);
    clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => {
      this.ensureSnippet().catch((e) => console.warn('[Qliro] refresh failed', e));
    }, QLIRO_UPDATE_PAUSE_MS);
  }

  /**
   * Bring the checkout up to date with the cart: mount it once on page load, after that only a real
   * cart change does anything. Nothing here depends on scrolling.
   */
  async ensureSnippet() {
    if (this.initialPreparation) return;
    clearTimeout(this._refreshTimer);
    this._refreshWanted = true;
    if (this._ensurePromise) return this._ensurePromise;

    const run = async () => {
      // A change made while one refresh was running gets its own pass afterwards.
      while (this._refreshWanted) {
        this._refreshWanted = false;
        if (!this.snippetStale && this.mountedOrderId) { this.setUpdating(false); return; }
        const cart = await this.fetchCart();
        // A failed read is not a completed refresh: CDE must keep payment locked for retry.
        if (!cart) {
          this.setUpdating(false);
          throw new Error('Could not read cart before refreshing checkout');
        }
        if (!cart?.token || !(cart.item_count > 0)) { this.setUpdating(false); return; }
        this.currentCartToken = cart.token;
        if (this.mountedOrderId && !this._forceReload) {
          await this.refreshInPlace();
        } else {
          this._forceReload = false;
          await this.updateAfterCartChange();
        }
      }
    };
    // Cleared in .finally(), which always runs after this assignment; a try/finally inside run()
    // would clear it first whenever run() returns before its first await.
    this._ensurePromise = run().finally(() => { this._ensurePromise = null; });
    return this._ensurePromise;
  }

  /**
   * "Updating checkout..." in place of the widget from the moment the customer changes something
   * until the Qliro order update starts (debounce and cart save). Then the widget shows again,
   * locked, and Qliro's own loading state covers the rest. The widget is only collapsed, never detached: moving an iframe
   * reloads it and loses what the customer typed.
   */
  setUpdating(on) {
    const container = this.element.querySelector('#qliro-checkout-container');
    if (!container) return;
    const show = on && !!this.mountedOrderId;
    container.classList.toggle('is-updating', show);
    container.querySelector(':scope > .cart-qliro__updating')?.remove();
    if (show) {
      const message = document.createElement('p');
      message.className = 'cart-qliro__loading cart-qliro__updating';
      message.textContent = TranslationUtils.get('sections.cart.updating_checkout', 'Updating checkout...');
      container.prepend(message);
    }
  }

  /**
   * Qliro's documented update flow: lock the widget, update the order on the server, wait for the
   * widget to pick the order up, unlock. The iframe stays, so whatever the customer has typed or
   * picked in it is kept. Falls back to rebuilding the widget when the order itself was replaced.
   */
  /**
   * checkout-init removed the campaign code (campaign over / not enough left / over the per-customer
   * limit): tell the customer in
   * the store language and show the cart without it. The notice only comes while the cart still
   * carries the code, so the refresh cannot repeat it.
   */
  handleCampaignNotice(data) {
    const notice = data?.campaignNotice;
    if (!notice) return;
    this.element?.campaignAutoApply?.handleNotice(notice);
    CampaignAutoApply.toastRefusal(notice.code, notice.remaining, notice.limit);
    if (typeof this.element?.refreshAfterUpdate === 'function') {
      Promise.resolve(this.element.refreshAfterUpdate(true)).catch(() => {
        console.warn('[Campaign] Could not refresh cart after checkout refusal');
      });
    }
  }

  async refreshInPlace() {
    const q1 = window.q1;
    try { q1?.lock?.(); } catch (e) { /* ignore */ }
    // The update starts now: Qliro's own locked/loading state takes over from the text.
    this.setUpdating(false);
    let rebuilt = false;
    // Cleared before the request, so a change made while it runs marks the widget stale again.
    this.snippetStale = false;
    try {
      const data = await window.apiService.post(`${window.proxyPath}/checkout-init`, await this.buildCheckoutInitBody());
      this.handleCampaignNotice(data);
      const sameOrder = data?.success === true && data?.qliroSnippet
        && String(data.qliroOrderId) === String(this.mountedOrderId);
      if (!sameOrder) {
        // New, expired or failed order: rebuild, which also runs the stale-session recovery.
        rebuilt = true;
        await this.updateAfterCartChange();
        return;
      }
      if (data.qliroOrderUpdated && q1) await this.waitForOrderUpdated(q1);
    } catch (error) {
      rebuilt = true;
      await this.updateAfterCartChange();
    } finally {
      this.setUpdating(false);
      if (!rebuilt) {
        try { q1?.unlock?.(); } catch (e) { /* ignore */ }
        // unlock() also releases the recipient-details gate's lock; put it back if still needed.
        const de = this.element?.deliveryExperience;
        if (de) {
          de._recipientGateLocked = false;
          de.applyRecipientDetailsGate({ show: false });
        }
      }
    }
  }

  /**
   * Ask the widget to sync with the updated order, and resolve once it reports it (or after a
   * timeout, so a missed event never leaves it locked). onOrderUpdated() is what starts the sync,
   * so it is called for every update: bound only once, the widget followed the first update and
   * then kept showing the old total (verified against Qliro's test environment).
   */
  waitForOrderUpdated(q1, timeoutMs = 10000) {
    return new Promise((resolve) => {
      const timer = setTimeout(() => done(), timeoutMs);
      const done = () => {
        clearTimeout(timer);
        if (this._onOrderUpdated === done) this._onOrderUpdated = null;
        resolve();
      };
      this._onOrderUpdated = done;
      try {
        // Earlier registrations call the same field, which only ever holds the current waiter.
        q1.onOrderUpdated(() => this._onOrderUpdated?.());
      } catch (e) {
        done();
      }
    });
  }

  async initialize(loader = true) {
    try {
      const cart = await this.fetchCart();

      if (cart && cart.token) {
        this.currentCartToken = cart.token;

        if (cart.item_count > 0) {
          this.initializeQliroCheckout(false, loader);
        }
      }
    } catch (error) {
      // Silently fail
    }
  }

  async updateAfterCartChange() {
    try {
      const qliroContainer = this.element.querySelector('#qliro-checkout-container');

      if (qliroContainer) {

        // Clear any existing Qliro content completely
        this.mountedOrderId = null;
        qliroContainer.innerHTML = '<p class="cart-qliro__loading" data-qliro-rebuilding></p>';
        qliroContainer.firstElementChild.textContent = TranslationUtils.get('sections.cart.updating_checkout', 'Updating checkout...');

        // Add a small delay to ensure previous Qliro instance is cleaned up
        await new Promise(resolve => setTimeout(resolve, 100));

        // Re-initialize Qliro with updated cart (force reinitialize)
        await this.initializeQliroCheckout(true); // Pass force flag
      }
    } catch (error) {
      // If Qliro fails, show a fallback message instead of getting stuck
      const qliroContainer = this.element.querySelector('#qliro-checkout-container');
      if (qliroContainer) {
        // Use toast notification for Qliro failure
        if (toastNotifications) {
          toastNotifications.warning('Checkout temporarily unavailable. Please refresh the page.');
        }
      }
    }
  }

  async initializeQliroCheckout(forceReinitialize = false, loader) {
    const qliroContainer = this.element.querySelector('#qliro-checkout-container');

    if (!qliroContainer) {
      return;
    }

    // Prevent concurrent initialization attempts (unless forced)
    if (!forceReinitialize && this.isQliroInitializing) {
      return;
    }

    this.isQliroInitializing = true;

    // Only update the loading message if not already set by updateAfterCartChange
    if (!qliroContainer.querySelector('[data-qliro-rebuilding]') && loader) {
      qliroContainer.innerHTML = '<p class="cart-qliro__loading">Fetching Qliro details...</p>';
    }

    const qliroInitEndpoint = `${window.proxyPath}/checkout-init`;

    // Reset the global flag to allow re-initialization after cart changes
    window.qliroCheckoutInitialized = false;

    if (!this.currentCartToken) {
      qliroContainer.innerHTML = '<p class="cart-qliro__loading">Cart is empty. Add items to use Qliro.</p>';
      this.isQliroInitializing = false;
      return;
    }

    try {
      // Cleared before the request, so a cart change made while it runs still gets its update.
      this.snippetStale = false;
      const requestBody = await this.buildCheckoutInitBody();

      const data = await window.apiService.post(qliroInitEndpoint, requestBody);
      this.handleCampaignNotice(data);

      if (data?.success === true && data?.qliroSnippet) {
        this.injectQliroSnippet(data.qliroSnippet, qliroContainer);
        window.qliroCheckoutInitialized = true;
        this.mountedOrderId = data.qliroOrderId || null;
        // Reset retry count on success
        this.staleSessionRetryCount = 0;

        // GTM: custom_qliro_checkout_loaded
        if (window.dataLayer) {
          try {
            const baseUrl = window.routes?.localeRootUrl || '';
            const cartResp = await fetch(baseUrl + '/cart.js');
            const cartData = await cartResp.json();
            if (typeof window.syncKnownCartStateFromCart === 'function') {
              window.syncKnownCartStateFromCart(cartData);
            } else if (typeof window.updateKnownCartState === 'function') {
              window.updateKnownCartState(cartData);
            } else {
              const itemCount = Number(cartData?.item_count || 0);
              const itemLength = Array.isArray(cartData?.items) ? cartData.items.length : 0;
              const cartStateMeta = typeof window.buildCartStateMeta === 'function'
                ? window.buildCartStateMeta()
                : { updated_at: new Date().toISOString(), updated_at_ms: Date.now() };

              window.cart = cartData;
              window.__cartState = {
                isLoaded: true,
                itemCount,
                isEmpty: itemCount === 0 && itemLength === 0,
                ...cartStateMeta
              };
            }
            dataLayer.push({ event: 'custom_qliro_checkout_loaded', cart_value: (cartData.total_price || 0) / 100, cart_items_count: cartData.item_count || 0, currency: window.Shopify?.currency?.active || 'SEK' });
          } catch (_) {}
        }
      } else {
        // Check if this is a stale session error
        if (this.isStaleQliroSession(data) && this.staleSessionRetryCount < this.maxStaleSessionRetries) {
          this.staleSessionRetryCount++;
          
          // Clear loading message before recovery
          const existingLoadingMessages = qliroContainer.querySelectorAll('.cart-qliro__loading');
          existingLoadingMessages.forEach(message => message.remove());
          
          // Show recovery message
          qliroContainer.innerHTML = '<p class="cart-qliro__loading">Refreshing checkout session...</p>';
          
          // Attempt to handle stale session
          const recovered = await this.handleStaleQliroSession();
          
          if (recovered) {
            // Retry initialization with new cart token
            this.isQliroInitializing = false;
            await this.initializeQliroCheckout(true, true);
            return;
          }
        }
        
        const errorMessage = data?.message || 'Qliro snippet was not found in the backend response';

        // Clear loading message on error
        const existingLoadingMessages = qliroContainer.querySelectorAll('.cart-qliro__loading');
        existingLoadingMessages.forEach(message => message.remove());

        // Use toast notification for Qliro errors
        if (toastNotifications) {
          toastNotifications.error(`Error loading Qliro Checkout: ${errorMessage}`);
        }
      }
    } catch (error) {
      // Check if the error response indicates stale session
      // Extract error data from ApiError structure
      let errorData = null;
      try {
        // For ApiError, error.response contains the parsed JSON response
        // Also check error.message which may contain the error text
        if (error.response && typeof error.response === 'object') {
          errorData = error.response;
        } else if (error.message) {
          // If error.response is a string or doesn't exist, create a structure with message
          errorData = { message: error.message };
        } else {
          errorData = error;
        }
        
        // Copy responseCode from error object to errorData if it exists
        // ApiError stores responseCode as a direct property
        if (error.responseCode && !errorData.responseCode) {
          errorData.responseCode = error.responseCode;
        }
      } catch (e) {
        // Fallback to error itself
        errorData = { message: error?.message || 'Unknown error' };
      }

      if (errorData && this.isStaleQliroSession(errorData) && this.staleSessionRetryCount < this.maxStaleSessionRetries) {
        this.staleSessionRetryCount++;
        
        // Clear loading message before recovery
        const existingLoadingMessages = qliroContainer.querySelectorAll('.cart-qliro__loading');
        existingLoadingMessages.forEach(message => message.remove());
        
        // Show recovery message
        qliroContainer.innerHTML = '<p class="cart-qliro__loading">Refreshing checkout session...</p>';
        
        // Attempt to handle stale session
        const recovered = await this.handleStaleQliroSession();
        
        if (recovered) {
          // Retry initialization with new cart token
          this.isQliroInitializing = false;
          await this.initializeQliroCheckout(true, true);
          return;
        }
      }

      // Clear loading message on error
      const existingLoadingMessages = qliroContainer.querySelectorAll('.cart-qliro__loading');
      existingLoadingMessages.forEach(message => message.remove());

      // Use toast notification for JavaScript errors
      if (toastNotifications) {
        toastNotifications.error(`Error loading Qliro Checkout: ${error.message}`);
      }
    } finally {
      this.isQliroInitializing = false;
    }
  }

  /** checkout-init request body: cart token, CDE tab, CDE addon lines and the chosen delivery. */
  async buildCheckoutInitBody() {
    const cartToken = this.currentCartToken;
    const trackingConsent = await TrackingCapture.prepareCheckoutConsent(cartToken);
    // Get locale from window.routes.locale (set in theme_config.liquid)
    const locale = window.routes?.locale || '';
    const activeTab = document.querySelector('.cde__tab.is-active')?.dataset.tab;
    const cdeTabActive = activeTab && activeTab !== 'digital';

    const requestBody = {
      cart_token: cartToken,
      tracking_consent: trackingConsent,
      context: this.checkoutContext,
      location: (window.location ? (window.location.pathname + window.location.search + window.location.hash) : ''),
      cde_tab: activeTab || 'digital'
    };

    // Only include locale if it exists
    if (locale) {
      requestBody.locale = locale;
    }

    // Pass CDE addon lines from AJAX cart so giftbox is not dropped when Storefront omits it.
    // On gift/premium tab this is required — abort checkout-init if fetch fails.
    try {
      const ajaxCart = await CartUtils.fetchCart();
      if (ajaxCart?.items?.length) {
        requestBody.ajax_cart_items = ajaxCart.items
          .filter((item) => CartUtils.isCdeAddon(item))
          .map((item) => ({
            id: item.id,
            variant_id: item.variant_id,
            quantity: item.quantity,
            price: item.price,
            line_price: item.line_price,
            final_line_price: item.final_line_price,
            title: item.title,
            product_title: item.product_title,
            properties: item.properties || {}
          }));
      }

      const cdeShipping = CartDeliveryExperience.buildCdeShippingPayload(ajaxCart);
      if (cdeShipping) {
        requestBody.cde_shipping = cdeShipping;
      }
    } catch (e) {
      if (cdeTabActive) {
        // Giftbox lines are required for this tab — abort so Qliro isn't charged incorrectly
        console.error('[CDE] ajax_cart_items fetch failed on gift/premium tab — aborting checkout-init', e);
        throw e;
      }
      // Digital tab: non-fatal, backend has Storefront cart
    }

    return requestBody;
  }

  injectQliroSnippet(snippetHTML, containerElement) {
    if (!containerElement) {
      return;
    }
    if (!snippetHTML || typeof snippetHTML !== 'string') {
      // Use toast notification for invalid Qliro data error
      if (toastNotifications) {
        toastNotifications.error('Error: Invalid Qliro data received.');
      }
      return;
    }

    // Clear any existing loading messages before injecting new content
    const existingLoadingMessages = containerElement.querySelectorAll('.cart-qliro__loading');
    existingLoadingMessages.forEach(message => message.remove());

    // Use DOMParser to safely parse the HTML string
    const parser = new DOMParser();
    const doc = parser.parseFromString(snippetHTML, 'text/html');

    // Find the #qliro-root div within the parsed snippet
    const qliroRoot = doc.getElementById('qliro-root');
    if (qliroRoot) {
      containerElement.appendChild(qliroRoot);
    }

    // Chain q1Ready so CDE can sync cart when the customer interacts with Qliro
    const prevQ1Ready = window.q1Ready;
    window.q1Ready = (q1) => {
      window.q1 = q1;
      if (typeof prevQ1Ready === 'function') prevQ1Ready(q1);
      this.element?.deliveryExperience?.setupQliroInteractionSync(q1);
    };

    // Find and handle script tags
    const scripts = doc.querySelectorAll('script');

    scripts.forEach((originalScript, index) => {
      const newScript = document.createElement('script');

      // Copy attributes (src, type, async, etc.)
      originalScript.getAttributeNames().forEach(attrName => {
        const attrValue = originalScript.getAttribute(attrName);
        newScript.setAttribute(attrName, attrValue);
      });

      // Copy inline script content if present
      if (originalScript.textContent) {
        newScript.textContent = originalScript.textContent;
      }

      document.head.appendChild(newScript);
    });

    // Setup Qliro postMessage listener after scripts are loaded
    setTimeout(() => {
      this.setupQliroShippingListener();
    }, 500);
  }

  setupQliroShippingListener() {
    // Prevent duplicate listeners
    if (this.postMessageListenerAdded) {
      return;
    }
    this.postMessageListenerAdded = true;

    // Listen for postMessage events from Qliro iframe
    window.addEventListener('message', (event) => {
      // Skip if no data
      if (!event.data) return;

      // For string messages, try to parse as JSON
      let messageData = event.data;
      if (typeof event.data === 'string') {
        try {
          messageData = JSON.parse(event.data);
        } catch (e) {
          return;
        }
      }

      // Now check if it's an object
      if (typeof messageData !== 'object') return;

      // Check for Qliro shipping change event
      // Format: {"message": {"action": "qco:bootstrap:shipping_method_changed", "shipping": {...}}}
      if (messageData.message && 
          messageData.message.action === 'qco:bootstrap:shipping_method_changed' &&
          messageData.message.shipping) {
        
        const shippingData = messageData.message.shipping;
        const shippingMethod = {
          name: shippingData.methodName || 'Shipping',
          price: shippingData.totalShippingPrice || shippingData.price || 0
        };

        // GTM: add_shipping_info
        if (window.dataLayer) {
          dataLayer.push({ ecommerce: null });
          dataLayer.push({
            event: 'add_shipping_info',
            ecommerce: {
              currency: window.Shopify?.currency?.active || 'SEK',
              shipping_tier: shippingMethod.name || 'Shipping'
            }
          });
        }
      }
    }, false);
  }
}

// --- Cart UI Manager ---
class CartUIManager {
  constructor(cartItems) {
    this.cartItems = cartItems;
    this.element = cartItems.element;
  }

  showCartUpdating(isUpdating) {
    const cartContainer = document.querySelector('.cart-container');
    if (!cartContainer) return;

    if (isUpdating) {
      // Add updating class for visual feedback
      cartContainer.classList.add('cart-container--updating');
      
      // Add overlay loader if it doesn't exist
      if (!cartContainer.querySelector('.cart-container__loader')) {
        const loader = document.createElement('div');
        loader.className = 'cart-container__loader';
        loader.innerHTML = `
          <div class="cart-container__loader-content">
            <div class="cart-container__spinner">
              <svg viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
              </svg>
            </div>
            <span class="cart-container__loader-text">Updating...</span>
          </div>
        `;
        cartContainer.appendChild(loader);
      }
    } else {
      // Remove updating class
      cartContainer.classList.remove('cart-container--updating');
      
      // Remove loader
      const loader = cartContainer.querySelector('.cart-container__loader');
      if (loader) {
        loader.remove();
      }
    }
  }

  async updateCartTotalsFromData(cartData) {

    // Find the subtotal value element
    const subtotalElement = this.element.querySelector('.cart-totals__row--total .cart-totals__value');
    if (!subtotalElement) return;

    // Store the experience-only total; the delivery tab's addons and shipping are added as data-cde-extra
    const originalTotal = CartUtils.getExperienceTotal(cartData);
    subtotalElement.setAttribute('data-original-total', originalTotal.toString());
    CartUtils.renderSubtotal(subtotalElement);

    // After updating the original total, we need to re-apply gift card adjustments
    // This will be done by calling loadExistingDiscounts() which will call updateAppliedCodes()
  }
}

// --- Cart Delivery Manager ---
class CartDeliveryManager {
  constructor(cartItems) {
    this.cartItems = cartItems;
    this.element = cartItems.element;
  }

  bindEvents(delegateElement) {
    delegateElement.on('click', '[data-action="delete-item"]', this.handleDeleteClick.bind(this));
    delegateElement.on('click', '[data-action="edit-delivery-group"]', this.handleEditClick.bind(this));
    delegateElement.on('click', '[data-action="remove-delivery-group"]', this.handleRemoveClick.bind(this));
  }

  handleDeleteClick(event) {
    if (event) {
      event.preventDefault();
    }
    const groupId = event.target.dataset.groupId;

    if (!groupId) {
      return;
    }
    this.removeDeliveryGroup(groupId);
  }

  handleEditClick(event) {
    if (event) {
      event.preventDefault();
    }
    const groupId = event.target.dataset.groupId;

    if (!groupId) {
      return;
    }

    // Find the delivery editor instance and trigger edit
    const deliveryPopup = document.querySelector('delivery-popup');
    if (deliveryPopup && deliveryPopup.deliveryEditor) {
      deliveryPopup.deliveryEditor.startEdit(groupId);
    }
  }

  handleRemoveClick(event) {
    if (event) {
      event.preventDefault();
    }
    const groupId = event.target.dataset.groupId;

    if (!groupId) {
      return;
    }

    this.removeDeliveryGroup(groupId);
  }

  async removeDeliveryGroup(groupId) {
    if (!groupId) {
      return;
    }

    try {
      // Show updating feedback
      this.cartItems.uiManager.showCartUpdating(true);

      // Get current cart to find items in the group
      const cart = await this.cartItems.fetchCart();
      if (!cart) {
        this.cartItems.uiManager.showCartUpdating(false);
        return;
      }

      const groupItems = cart.items.filter(item =>
        item.properties && item.properties._delivery_group_id === groupId
      );

      if (groupItems.length === 0) {
        this.cartItems.uiManager.showCartUpdating(false);
        return;
      }

      // Create updates object to set all group items to quantity 0
      const updates = {};
      groupItems.forEach(item => {
        updates[item.key] = 0;
      });

      // The last experience is leaving: the cart's gift box must not stay behind on its own
      const experiencesLeft = cart.items.some(item =>
        !CartUtils.isCdeAddon(item) && item.properties?._delivery_group_id !== groupId
      );
      if (!experiencesLeft) {
        cart.items.forEach(item => {
          if (CartUtils.isCdeAddon(item)) updates[item.key] = 0;
        });
      }

      const updatedCart = await window.apiService.post('/cart/update.js', { updates });

      if (!updatedCart) {
        this.cartItems.uiManager.showCartUpdating(false);
        return;
      }

      // Use HTML refresh since items are being removed
      await this.cartItems.refreshAfterUpdate(true);
    } catch (error) {
      this.cartItems.uiManager.showCartUpdating(false); // Hide loading in case of error
    }
  }
}

// --- Cart Gift Message ---
// Shopify rejects a gift card recipient message longer than 200 characters, and the
// greeting is written straight to recipientAttributes.message when the order is created.
// https://shopify.dev/docs/storefronts/themes/product-merchandising/gift-cards
const GIFT_MESSAGE_MAX_CHARS = 200;

class CartGiftMessage {
  constructor(cartItems) {
    this.cartItems = cartItems;
    this.currentLineId = null;
    this.quantity = 1;
    this.activeIndex = 0;
    this.messages = [''];
    this.firstEnteredIndex = null;
    this.appliedAll = false;
    this.maxChars = GIFT_MESSAGE_MAX_CHARS;
    this._trackingMessageOpen = false;
    this._trackingMessageStarted = false;
    this._trackingMessageSaved = false;
    this.handlePopupClosedBound = this.handlePopupClosed.bind(this);
    this.cacheElements();
  }

  cacheElements() {
    const popup = document.getElementById('cart-gift-message-popup');
    if (popup && popup !== this.popup) {
      this.popup?.removeEventListener('popup:closed', this.handlePopupClosedBound);
      this.popup = popup;
      this.popup.addEventListener('popup:closed', this.handlePopupClosedBound);
    } else {
      this.popup = popup;
    }
    this.textarea = this.popup?.querySelector('[data-gift-message-textarea]');
    this.charCount = this.popup?.querySelector('[data-gift-message-char-count]');
    this.titleEl = this.popup?.querySelector('[data-gift-message-title]');
    this.tabsEl = this.popup?.querySelector('[data-gift-message-tabs]');
    this.multiEl = this.popup?.querySelector('[data-gift-message-multi]');
    this.navEl = this.popup?.querySelector('[data-gift-message-nav]');
    this.applyBtn = this.popup?.querySelector('[data-gift-message-apply]');
    this.stepEl = this.popup?.querySelector('[data-gift-message-step]');
    this.prevBtn = this.popup?.querySelector('[data-action="gift-message-prev"]');
    this.nextBtn = this.popup?.querySelector('[data-action="gift-message-next"]');
    this.saveBtn = this.popup?.querySelector('[data-action="save-gift-message"]');
    this.saveTextEl = this.saveBtn?.querySelector('.ButtonText');
    this.maxChars = parseInt(this.popup?.dataset.maxChars, 10) || GIFT_MESSAGE_MAX_CHARS;
    if (this.textarea) this.textarea.maxLength = this.maxChars;
  }

  bindEvents(delegateElement) {
    this.cacheElements();
    delegateElement.on('click', '[data-action="open-gift-message"]', this.handleOpen.bind(this));
    delegateElement.on('click', '[data-action="save-gift-message"]', this.handleSave.bind(this));
    delegateElement.on('click', '[data-action="gift-message-tab"]', this.handleTabClick.bind(this));
    delegateElement.on('click', '[data-action="gift-message-prev"]', this.handlePrev.bind(this));
    delegateElement.on('click', '[data-action="gift-message-next"]', this.handleNext.bind(this));
    delegateElement.on('click', '[data-action="gift-message-apply-all"]', this.handleApplyAll.bind(this));
    delegateElement.on('input', '[data-gift-message-textarea]', this.handleInput.bind(this));
  }

  interpolate(template, vars) {
    let text = template || '';
    Object.entries(vars).forEach(([key, value]) => {
      const replacement = value == null ? '' : String(value);
      text = text
        .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), replacement)
        .replace(new RegExp(`\\{${key}\\}`, 'g'), replacement);
    });
    return text;
  }

  parseMessages(btn) {
    const qty = Math.max(1, parseInt(btn.dataset.quantity, 10) || 1);
    let messages = [];
    const raw = btn.dataset.messages;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) messages = parsed.map((m) => String(m ?? ''));
      } catch (err) {
        messages = [];
      }
    }
    if (!messages.length && btn.dataset.message) messages = [btn.dataset.message];
    while (messages.length < qty) messages.push('');
    if (messages.length > qty) messages = messages.slice(0, qty);
    return { qty, messages };
  }

  handleOpen(event) {
    const btn = event.target.closest('[data-action="open-gift-message"]');
    if (!btn) return;
    this.cacheElements();
    this.currentLineId = btn.dataset.lineId;
    const parsed = this.parseMessages(btn);
    this.quantity = parsed.qty;
    this.messages = parsed.messages;
    this.activeIndex = 0;
    this.appliedAll = false;
    this.firstEnteredIndex = this.messages.findIndex((m) => m.trim());
    if (this.firstEnteredIndex < 0) this.firstEnteredIndex = null;

    const productTitle = btn.dataset.productTitle || '';
    if (this.titleEl) {
      this.titleEl.textContent = this.interpolate(
        this.popup.dataset.i18nTitle,
        { product: productTitle }
      ) || productTitle;
    }

    this.renderMulti();
    this.syncTextarea();
    this.syncSaveButton();
    this.open();
  }

  renderMulti() {
    const isMulti = this.quantity > 1;
    this.popup?.classList.toggle('gift-message-popup--multi', isMulti);
    if (this.multiEl) this.multiEl.hidden = !isMulti;
    if (this.navEl) this.navEl.hidden = !isMulti;
    if (!isMulti) return;

    this.renderTabs();
    this.syncApplyButton();
    this.syncNav();
  }

  renderTabs() {
    if (!this.tabsEl) return;
    const voucherTpl = this.popup?.dataset.i18nVoucher || 'Voucher {{ number }}';
    this.tabsEl.innerHTML = this.messages.map((_, i) => {
      const number = String(i + 1).padStart(2, '0');
      const label = this.interpolate(voucherTpl, { number });
      const active = i === this.activeIndex;
      return `<button type="button"
        class="gift-message-popup__tab u-s3${active ? ' is-active' : ''}"
        role="tab"
        aria-selected="${active}"
        data-action="gift-message-tab"
        data-index="${i}">${label}</button>`;
    }).join('');

    const activeTab = this.tabsEl.querySelector('.is-active');
    activeTab?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }

  syncApplyButton() {
    if (!this.applyBtn) return;
    if (this.appliedAll) {
      this.applyBtn.textContent = this.popup?.dataset.i18nApplied || 'Applied';
    } else {
      this.applyBtn.textContent = this.interpolate(
        this.popup?.dataset.i18nApply,
        { count: this.quantity }
      );
    }
  }

  syncNav() {
    if (this.prevBtn) this.prevBtn.disabled = this.activeIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.activeIndex >= this.quantity - 1;
    if (this.stepEl) {
      this.stepEl.textContent = this.interpolate(
        this.popup?.dataset.i18nStep,
        { current: this.activeIndex + 1, total: this.quantity }
      );
    }
  }

  syncTextarea() {
    const value = this.messages[this.activeIndex] || '';
    if (this.textarea) this.textarea.value = value;
    this.syncCharCount(value.length);
  }

  syncCharCount(len) {
    if (!this.charCount) return;
    const remaining = Math.max(0, this.maxChars - len);
    this.charCount.textContent = this.interpolate(
      this.popup?.dataset.i18nChars,
      { count: remaining }
    );
  }

  hasAnyMessage() {
    return this.messages.some((m) => m.trim());
  }

  syncSaveButton() {
    if (!this.saveBtn) return;
    const enabled = this.hasAnyMessage();
    if (enabled) this.saveBtn.removeAttribute('disabled');
    else this.saveBtn.setAttribute('disabled', 'disabled');
    if (this.saveTextEl) {
      const key = this.quantity > 1 ? 'i18nSavePlural' : 'i18nSave';
      this.saveTextEl.textContent = this.popup?.dataset[key]
        || (this.quantity > 1 ? 'Save messages' : 'Save message');
    }
  }

  commitActive() {
    if (!this.textarea) return;
    this.messages[this.activeIndex] = this.textarea.value;
  }

  setActiveIndex(index) {
    if (index < 0 || index >= this.quantity || index === this.activeIndex) return;
    const previous = this.activeIndex;
    this.commitActive();
    this.activeIndex = index;
    this.renderTabs();
    this.syncNav();
    this.syncTextarea();
    this.textarea?.focus();
    this.trackMessage('step_changed', { from_voucher_index: previous + 1, to_voucher_index: index + 1 });
  }

  handleTabClick(event) {
    const tab = event.target.closest('[data-action="gift-message-tab"]');
    if (!tab) return;
    this.setActiveIndex(parseInt(tab.dataset.index, 10));
  }

  handlePrev() {
    this.setActiveIndex(this.activeIndex - 1);
  }

  handleNext() {
    this.setActiveIndex(this.activeIndex + 1);
  }

  handleApplyAll() {
    this.commitActive();
    let sourceIndex = this.firstEnteredIndex;
    if (sourceIndex == null || !this.messages[sourceIndex]?.trim()) {
      sourceIndex = this.messages.findIndex((m) => m.trim());
    }
    if (sourceIndex < 0) return;
    const source = this.messages[sourceIndex];
    this.messages = this.messages.map(() => source);
    this.appliedAll = true;
    this.syncTextarea();
    this.syncApplyButton();
    this.syncSaveButton();
    this.trackMessage('apply_all');
  }

  handleInput() {
    const value = this.textarea?.value || '';
    this.messages[this.activeIndex] = value;
    this.syncCharCount(value.length);
    if (value.trim() && this.firstEnteredIndex == null) {
      this.firstEnteredIndex = this.activeIndex;
    }
    if (!value.trim() && this.firstEnteredIndex === this.activeIndex) {
      const next = this.messages.findIndex((m) => m.trim());
      this.firstEnteredIndex = next < 0 ? null : next;
    }
    this.syncSaveButton();
    if (this._trackingMessageOpen && !this._trackingMessageStarted) {
      this._trackingMessageStarted = true;
      this.trackMessage('started');
    }
  }

  trackMessage(action, properties = {}) {
    if (!this._trackingMessageOpen) return;
    this.cartItems.deliveryExperience?.trackDeliveryEvent('custom_cart_delivery_message', {
      message_action: action, voucher_count: this.quantity, voucher_index: this.activeIndex + 1,
      message_saved: this._trackingMessageSaved, failure_stage: '', from_voucher_index: 0, to_voucher_index: 0,
      ...properties
    });
  }

  open() {
    if (!this.popup || !window.globalPopup?.openPopup) return;
    window.globalPopup.openPopup(this.popup, false);
    this._trackingMessageOpen = true;
    this._trackingMessageStarted = false;
    this._trackingMessageSaved = false;
    this.trackMessage('opened');
    requestAnimationFrame(() => this.textarea?.focus());
  }

  close() {
    if (!this.popup || this.popup.getAttribute('aria-expanded') !== 'true') return;
    window.globalPopup?.closePopup(this.popup);
  }

  handlePopupClosed() {
    this.trackMessage('closed', { message_saved: this._trackingMessageSaved });
    this._trackingMessageOpen = false;
    this.popup?.classList.remove('gift-message-popup--multi');
    this.currentLineId = null;
  }

  async handleSave() {
    if (!this.currentLineId || !this.hasAnyMessage()) return;
    this.commitActive();
    this.trackMessage('save_attempt');
    await this.updateMessages(this.messages);
  }

  async updateMessages(messages) {
    const saveBtn = this.saveBtn;
    if (saveBtn) saveBtn.setAttribute('disabled', 'disabled');

    let persisted = false;
    try {
      const cart = await this.cartItems.fetchCart();
      // The delivery box re-stamps lines (new key) while the popup is open; find the line again.
      const item = CartUtils.findLineItem(cart, this.currentLineId);
      if (!item) {
        this.trackMessage('save_failed', { failure_stage: 'missing_line' });
        this.syncSaveButton();
        return;
      }

      const updatedProperties = Object.assign({}, item.properties);
      Object.keys(updatedProperties).forEach((key) => {
        if (key === '_gift_messages' || /^_gift_message_\d+$/.test(key)) {
          delete updatedProperties[key];
        }
      });

      const trimmed = messages.map((m) => (m || '').trim());
      const firstFilled = trimmed.find((m) => m) || '';
      updatedProperties._gift_message = firstFilled;
      if (this.quantity > 1) {
        updatedProperties._gift_messages = JSON.stringify(trimmed);
      }

      await window.apiService.post('/cart/change.js', {
        id: item.key,
        quantity: item.quantity,
        properties: updatedProperties
      });

      persisted = true;
      this._trackingMessageSaved = true;
      this.trackMessage('saved');
      this.close();
      await this.cartItems.refreshAfterUpdate(true);
    } catch (err) {
      if (persisted) {
        this.cartItems.deliveryExperience?.trackDeliveryEvent('custom_cart_delivery_error', { error_type: 'message_refresh' });
      } else {
        this.trackMessage('save_failed', { failure_stage: 'save' });
      }
      console.error('Gift message update failed:', err);
      this.syncSaveButton();
    }
  }
}

// --- Cart Delivery Experience ---
const CDE_RECIPIENT_PROPERTIES = Object.freeze([
  '_recipient_email', '_recipient_name', '_recipient_address',
  '_recipient_city', '_recipient_zip', '_recipient_full_address'
]);
const CDE_SCHEDULE_PROPERTIES = Object.freeze(['_scheduled_delivery', '_scheduled_date']);
const CDE_STOCKHOLM_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Stockholm', calendar: 'gregory', numberingSystem: 'latn',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
});
const CDE_POSTAL_FIELDS = Object.freeze(['recipient_address', 'recipient_city', 'recipient_zip']);
const CDE_PHYSICAL_REQUIRED_FIELDS = Object.freeze([
  'recipient_first_name', 'recipient_last_name', ...CDE_POSTAL_FIELDS
]);

class CartDeliveryExperience {
  constructor(cartItems) {
    this.cartItems = cartItems;
    this.qliroSyncTimer = null;
    this.cdeSelectionDirty = false;
    this.cdeSyncInFlight = false;
    this.pendingCdeSync = false;
    this._cdeSyncPromise = null;
    this._cdeSeeding = false;
    this._qliroInteractionQ1 = null;
    this.productSlidesCache = {};
    this.variantsMapCache = {};
    this.preloadedImages = new Set();
    this._cdeAbort = false;
    this._deliveryFormStarted = false;
    this._trackedPackAll = new Map();
    this._deliveryContinuePromise = null;
  }

  bindEvents(delegateElement) {
    // Tab switching
    delegateElement.on('click', '[data-action="switch-delivery-tab"]', this.handleTabSwitch.bind(this));
    // Delivery speed radios
    delegateElement.on('change', '[data-action="select-delivery-speed"]', this.handleSpeedChange.bind(this));
    delegateElement.on('click', '.cde__speed-options .cde__radio', this.handleSpeedChange.bind(this));
    // Packaging product selection
    delegateElement.on('change', '[data-action="select-giftbox-product"]', this.handleGiftboxSelect.bind(this));
    // Giftbox sub-step navigation
    delegateElement.on('click', '[data-action="giftbox-to-ribbon"]', this.handleToRibbon.bind(this));
    delegateElement.on('mouseenter', '[data-action="giftbox-to-ribbon"]', this.handleRibbonPreload.bind(this));
    delegateElement.on('focus', '[data-action="giftbox-to-ribbon"]', this.handleRibbonPreload.bind(this));
    delegateElement.on('click', '[data-action="ribbon-to-design"]', this.handleToDesign.bind(this));
    delegateElement.on('click', '[data-action="giftbox-confirm"]', this.handleConfirm.bind(this));
    delegateElement.on('click', '[data-action="giftbox-skip-ribbon"]', this.handleConfirm.bind(this));
    // Address toggling
    delegateElement.on('click', '[data-action="deliver-to-recipient"]', this.handleDeliverToRecipient.bind(this));
    delegateElement.on('click', '[data-action="deliver-to-self"]', this.handleDeliverToSelf.bind(this));
    delegateElement.on('change', '[data-action="toggle-cde-schedule"]', this.handleScheduleToggle.bind(this));
    delegateElement.on('focusin', '[data-delivery-field="delivery_datetime"]', () => this.syncSchedulePlaceholder(true));
    delegateElement.on('blur', '[data-delivery-field="delivery_datetime"]', () => this.syncSchedulePlaceholder(), true);
    if (!this._scheduleRecheckHandler) {
      this._scheduleRecheckHandler = () => this.applyRecipientDetailsGate({ show: true });
      document.addEventListener('visibilitychange', this._scheduleRecheckHandler);
    }
    // Recipient details use line-item properties, as in the development delivery popup.
    delegateElement.on('input', '[data-delivery-field]', this.handleAddressFieldInput.bind(this));
    delegateElement.on('focusin', '[data-delivery-field]', this.handleRecipientFormFocus.bind(this));
    const markFieldTouched = (event) => {
      const field = event.target?.dataset?.deliveryField;
      if (!field) return;
      if (!this._recipientTouchedFields) this._recipientTouchedFields = new Set();
      this._recipientTouchedFields.add(field);
      this.applyRecipientDetailsGate({ show: true });
      this.trackRecipientFieldValidation(event.target);
    };
    delegateElement.on('change', '[data-delivery-field]', markFieldTouched);
    delegateElement.on('blur', '[data-delivery-field]', markFieldTouched, true);
    // Bow variant selection
    delegateElement.on('change', '[data-action="select-bow-variant"]', this.handleBowSelect.bind(this));
    // Pack all vouchers checkbox
    delegateElement.on('change', '[data-action="toggle-pack-all"]', this.handlePackAll.bind(this));
    // Seed from the rendered controls without counting defaults or section refreshes as choices.
    document.querySelectorAll('[data-giftbox-panel]').forEach((el) => {
      const panel = el.dataset.giftboxPanel;
      this._trackedPackAll.set(panel, this.getPackAllChecked(panel));
    });
    // Cache product slides + restore ribbon substep if saved
    document.querySelectorAll('[data-giftbox-carousel]').forEach((el) => {
      const panel = el.dataset.giftboxCarousel;
      const track = el.querySelector('[data-cde-carousel-track]');
      if (panel && track && !this.productSlidesCache[panel]) {
        this.productSlidesCache[panel] = track.innerHTML;
      }
    });
    document.querySelectorAll('[data-giftbox-panel]').forEach((el) => {
      const panel = el.dataset.giftboxPanel;
      const selected = this.getSelectedGiftbox(panel);
      if (selected?.handle) this.preloadVariantImages(panel, selected.handle);
    });
    this._cdeSeeding = true;
    this.mountSharedParts(document.querySelector('.cde__panel.is-active'));
    this.syncAllShippingSubtotalLabels();
    this.seedAllGiftboxSubtotals();
    this.refreshSummaryTotal();
    this.applyAdminShippingRates();
    requestAnimationFrame(() => {
      const activePanel = document.querySelector('.cde__panel.is-active');
      this.reinitCdeSwipers(activePanel);
      const checked = activePanel?.querySelector('[data-action="select-giftbox-product"]:checked, [data-action="select-bow-variant"]:checked');
      if (checked) this.slideCdeCarouselToEl(checked);
      this._cdeSeeding = false;
      this.setupDeliveryTracking();
    });
  }

  /** Comparable dimensions, but new event names keep the two delivery funnels independent. */
  getDeliveryTrackingContext() {
    const tab = document.querySelector('.cde__tab.is-active')?.dataset.tab || 'digital';
    const recipientFields = document.querySelector('[data-recipient-fields]');
    return {
      delivery_source: 'cart',
      delivery_tab: tab,
      delivery_type: tab === 'digital' ? 'voucher' : 'giftbox',
      delivery_person: recipientFields && !recipientFields.hidden ? 'gift_receiver' : 'buyer',
      delivery_step: this.getDeliveryTrackingStep(),
      currency: window.Shopify?.currency?.active || 'SEK',
      // Delivery applies to the whole cart; clear any single-product context left by the popup.
      product_name: '',
      product_id: ''
    };
  }

  getDeliveryTrackingStep() {
    const panel = this.getActiveGiftPanelId();
    if (!panel) return 'digital';
    return document.querySelector(`[data-giftbox-panel="${panel}"]`)?.dataset.substep === 'ribbon' ? 'ribbon' : 'packaging';
  }

  getDeliveryTrackingState() {
    this._deliveryTracking ||= { viewed: false, checkoutViewed: false, completed: false, abandoned: false, lastEvent: '', attempt: 0 };
    return this._deliveryTracking;
  }

  /** Never pass recipient values, gift messages, raw errors or cart properties here. */
  trackDeliveryEvent(event, properties = {}) {
    if (this._cdeSeeding || typeof window.dataLayer?.push !== 'function'
      || isOrderCallbackMode() || !document.querySelector('[data-cart-delivery-experience]')) return false;
    try {
      const context = this.getDeliveryTrackingContext();
      const state = this.getDeliveryTrackingState();
      // An interaction also establishes exposure if it occurs before the observer callback.
      if (!state.viewed) {
        window.dataLayer.push({ event: 'custom_cart_delivery_viewed', ...context });
        state.viewed = true;
      }
      if (event === 'custom_cart_delivery_viewed') return true;
      const defaults = event === 'custom_cart_delivery_error'
        ? { error_type: '', invalid_field: '', validation_trigger: '', http_status: 0, attempt_number: 0 } : {};
      window.dataLayer.push({ event, ...context, ...defaults, ...properties });
      if (event !== 'custom_cart_delivery_abandoned') state.lastEvent = event;
      return true;
    } catch (error) {
      // A broken analytics integration must not prevent saving delivery choices or checkout.
      console.warn('[CDE] Delivery tracking failed', error);
      return false;
    }
  }

  setupDeliveryTracking() {
    const root = document.querySelector('[data-cart-delivery-experience]');
    if (!root || isOrderCallbackMode()) return;
    const state = this.getDeliveryTrackingState();
    if (!this._deliveryExitHandler) {
      this._deliveryExitHandler = () => {
        if (!state.viewed || state.completed || state.checkoutViewed || state.abandoned) return;
        state.abandoned = this.trackDeliveryEvent('custom_cart_delivery_abandoned', {
          last_event: state.lastEvent,
          exit_reason: 'pagehide'
        });
      };
      window.addEventListener?.('pagehide', this._deliveryExitHandler);
    }
    const checkout = this.cartItems.element.querySelector('#qliro-checkout-container');
    if (this._deliveryTrackingRoot === root && this._deliveryTrackingCheckout === checkout) return;
    this._deliveryViewObserver?.disconnect();
    this._deliveryTrackingRoot = root;
    this._deliveryTrackingCheckout = checkout;
    if (typeof window.IntersectionObserver !== 'function') {
      this.trackDeliveryEvent('custom_cart_delivery_viewed');
      return;
    }
    this._deliveryViewObserver = new window.IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target === root) this.trackDeliveryEvent('custom_cart_delivery_viewed');
        if (entry.target === checkout && !checkout.hidden && !state.checkoutViewed) {
          if (this.trackDeliveryEvent('custom_cart_delivery_checkout_viewed')) state.checkoutViewed = true;
        }
      }
    });
    this._deliveryViewObserver.observe(root);
    if (checkout) this._deliveryViewObserver.observe(checkout);
  }

  destroyDeliveryTracking() {
    this._deliveryViewObserver?.disconnect();
    if (this._deliveryExitHandler) window.removeEventListener?.('pagehide', this._deliveryExitHandler);
    this._deliveryExitHandler = null;
    this._deliveryTrackingRoot = null;
    this._deliveryTrackingCheckout = null;
  }

  handleRecipientFormFocus(event) {
    const fields = document.querySelector('[data-recipient-fields]');
    if (this._deliveryFormStarted || !fields || fields.hidden || event.target.disabled) return;
    // One form start per cart component lifetime, including when its sections are refreshed.
    this._deliveryFormStarted = this.trackDeliveryEvent('custom_cart_delivery_form_started');
  }

  trackRecipientFieldValidation(input, { report = true } = {}) {
    const field = input?.dataset?.deliveryField;
    if (!['recipient_email', ...CDE_PHYSICAL_REQUIRED_FIELDS].includes(field)) return;
    this._recipientTrackedInvalid ||= new Set();
    if (input.getAttribute?.('aria-invalid') !== 'true') {
      this._recipientTrackedInvalid.delete(field);
    } else if (report && !this._recipientTrackedInvalid.has(field)) {
      this._recipientTrackedInvalid.add(field);
      this.trackDeliveryEvent('custom_cart_delivery_error', {
        error_type: 'validation', invalid_field: field, validation_trigger: 'field'
      });
    }
  }

  /**
   * Qliro charges the Shopify Admin rate each delivery option links to (rate_index), not the price
   * typed into the theme setting. Show that same price once the backend has it; the theme price
   * stays as the placeholder until then, or if the request fails.
   */
  applyAdminShippingRates() {
    if (!window.proxyPath || !window.apiService) return;
    this._adminRatesPromise = this._adminRatesPromise
      || window.apiService.get(`${window.proxyPath}/cde-shipping-rates`).catch(() => null);
    this._adminRatesPromise.then((data) => {
      const rates = Array.isArray(data?.rates) ? data.rates : [];
      if (!rates.length) return;
      const freeLabel = window.theme?.strings?.cart?.delivery_free
        || document.querySelector('.cde__free')?.textContent?.trim()
        || 'FREE';
      let changed = false;
      document.querySelectorAll('.cde__radio-input[data-action="select-delivery-speed"]').forEach((input) => {
        const rate = rates.find((r) => String(r.rateIndex) === String(input.dataset.rateIndex));
        const price = parseInt(rate?.priceOre, 10);
        if (!rate || !Number.isFinite(price) || price === parseInt(input.dataset.price || '0', 10)) return;
        input.dataset.price = String(price);
        const priceEl = input.closest('.cde__radio')?.querySelector('.cde__radio-price');
        if (priceEl) {
          priceEl.classList.toggle('cde__free', price === 0);
          priceEl.textContent = price === 0 ? freeLabel : this.formatCdeMoney(price);
        }
        changed = true;
      });
      if (!changed) return;
      this.seedAllGiftboxSubtotals();
      this.refreshSummaryTotal();
    });
  }

  formatCdeMoney(ore) {
    const n = parseInt(ore, 10) || 0;
    if (n === 0) return '0 kr';
    return window.Shopify?.formatMoney?.(n, window.theme?.moneyFormat) || `${n / 100} kr`;
  }

  getSavedGiftboxSelection() {
    const cde = document.querySelector('[data-cart-delivery-experience]');
    return {
      productId: cde?.dataset.cdeGiftboxProduct || '',
      variantId: cde?.dataset.cdeGiftboxVariant || ''
    };
  }

  setSavedGiftboxSelection(productId, variantId) {
    const cde = document.querySelector('[data-cart-delivery-experience]');
    if (!cde) return;
    if (productId != null) cde.dataset.cdeGiftboxProduct = String(productId || '');
    if (variantId != null) cde.dataset.cdeGiftboxVariant = String(variantId || '');
  }

  ribbonDeltaForVariant(panel, variantId) {
    const selected = this.getSelectedGiftbox(panel);
    const variants = (selected?.handle && this.getVariantsMap(panel)?.[selected.handle]) || [];
    if (!variants.length) return 0;
    // Same base as the packaging price: the cheapest variant that can be bought.
    const buyable = variants.filter((v) => v.available || v.inventory_policy === 'continue');
    const minPrice = Math.min(...(buyable.length ? buyable : variants).map((v) => Number(v.price)));
    const match = variants.find((v) => String(v.id) === String(variantId));
    return match ? Math.max(0, Number(match.price) - minPrice) : 0;
  }

  setRibbonSubtotal(panel, delta) {
    const ribbonEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-ribbon]`);
    if (!ribbonEl) return;
    const n = Math.max(0, parseInt(delta, 10) || 0);
    ribbonEl.dataset.rawPrice = String(n);
    ribbonEl.textContent = this.formatCdeMoney(n);
  }

  /** Ensure packaging/shipping/ribbon raw prices exist so delsumma can sum after first paint. */
  seedAllGiftboxSubtotals() {
    document.querySelectorAll('[data-giftbox-subtotal]').forEach((subtotal) => {
      const panel = subtotal.dataset.giftboxSubtotal;
      if (!panel) return;

      const packagingEl = subtotal.querySelector('[data-subtotal-packaging]');
      const selectedCard = document.querySelector(
        `[data-giftbox-carousel="${panel}"] .cde-card[data-giftbox-product] .cde-card__input:checked`
      )?.closest('[data-giftbox-product]');
      if (packagingEl && selectedCard?.dataset.productPrice != null) {
        packagingEl.dataset.rawPrice = String(parseInt(selectedCard.dataset.productPrice, 10) || 0);
      }

      const saved = this.getSavedGiftboxSelection();
      const selected = this.getSelectedGiftbox(panel);
      if (saved.variantId && selected?.id && (!saved.productId || saved.productId === String(selected.id))) {
        this.setRibbonSubtotal(panel, this.ribbonDeltaForVariant(panel, saved.variantId));
      }

      const checkedSpeed = document.querySelector(`[data-speed-group="${panel}"] input:checked`);
      if (checkedSpeed) {
        const price = parseInt(checkedSpeed.dataset.price || '0', 10);
        const shippingEl = subtotal.querySelector('[data-subtotal-shipping]');
        if (shippingEl) {
          shippingEl.dataset.rawPrice = String(price);
          shippingEl.textContent = this.formatCdeMoney(price);
        }
      }
      this.updateGiftboxTotal(panel);
    });
  }

  getShippingSubtotalLabel(selectedRadio, labelEl) {
    const short = selectedRadio?.dataset?.shortTitle?.trim();
    if (short) return short;
    return labelEl?.dataset?.defaultLabel
      || document.querySelector('[data-cart-delivery-experience]')?.dataset.deliveryLabel
      || 'Delivery';
  }

  syncAllShippingSubtotalLabels() {
    document.querySelectorAll('[data-giftbox-subtotal]').forEach((subtotal) => {
      const panel = subtotal.dataset.giftboxSubtotal;
      const labelEl = subtotal.querySelector('[data-subtotal-shipping-label]');
      const checked = document.querySelector(`[data-speed-group="${panel}"] input:checked`);
      if (labelEl && checked) {
        labelEl.textContent = this.getShippingSubtotalLabel(checked, labelEl);
      }
    });
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────
  async handleTabSwitch(event) {
    const btn = event.target.closest('[data-action="switch-delivery-tab"]');
    if (!btn || btn.disabled) return;
    const tab = btn.dataset.tab;
    const previousTab = document.querySelector('.cde__tab.is-active')?.dataset.tab;

    document.querySelectorAll('.cde__tab').forEach(t => {
      const active = t.dataset.tab === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active);
    });
    document.querySelectorAll('.cde__panel').forEach(p => {
      p.classList.toggle('is-active', p.dataset.panel === tab);
    });

    const panelId = ({ gift_packaging: 'gift', premium_gift_packaging: 'premium' })[tab];
    const activePanel = document.querySelector(`.cde__panel[data-panel="${tab}"]`);
    this.mountSharedParts(activePanel);
    this.refreshSummaryTotal();
    requestAnimationFrame(() => {
      this.reinitCdeSwipers(activePanel);
      // Gift ↔ Premium: only packaging swiper differs — select first card there.
      // Do not re-sync delivery speed (already shared; re-sync caused a visible flash).
      if (panelId) this.selectFirstGiftboxCard(panelId);
    });

    // Each delivery type has different required recipient details.
    this.markCdeSelectionDirty();
    this.applyRecipientDetailsGate({ show: true });

    this.scheduleCdeCartSync();
    if (tab !== previousTab) this.trackDeliveryEvent('custom_cart_delivery_type_selected');
  }

  /**
   * cart-delivery-experience.liquid opens the Gift packaging tab when the product page chose the
   * gift box (cart attribute _delivery_preference). Once the cart has settled, run that tab's click
   * path, so the box line, line addressing, shipping attributes and Qliro follow exactly as after a
   * customer's click. In a frame: bindEvents' seeding frame (no sync is scheduled while seeding)
   * runs first.
   */
  applyRenderedTab() {
    requestAnimationFrame(() => {
      const tab = document.querySelector('.cde__tab.is-active');
      if (tab && tab.dataset.tab !== 'digital') this.handleTabSwitch({ target: tab });
    });
  }

  /** Select the first packaging card in this panel's swiper (Gift ↔ Premium differ only here). */
  selectFirstGiftboxCard(panel) {
    const giftboxEl = document.querySelector(`[data-giftbox-panel="${panel}"]`);
    if (!giftboxEl) return;

    if (giftboxEl.dataset.substep === 'ribbon') {
      this.setGiftboxSubstep(panel, 'design');
      this.restoreProductSlides(panel);

      const ribbonEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-ribbon]`);
      if (ribbonEl) {
        ribbonEl.dataset.rawPrice = '0';
        ribbonEl.textContent = '0 kr';
      }
      this.updateGiftboxTotal(panel);
    }

    const firstInput = giftboxEl.querySelector(
      '[data-giftbox-carousel] .cde-card[data-giftbox-product] .cde-card__input:not(:disabled)'
    );
    if (!firstInput) return;

    // Already on first — don't re-fire selection (avoids subtotal flicker)
    if (firstInput.checked && giftboxEl.dataset.substep !== 'ribbon') return;

    firstInput.checked = true;
    this.selectGiftbox(firstInput);
  }

  // ── Delivery speed ────────────────────────────────────────────────────────
  handleSpeedChange(event) {
    const radio = event.target.closest('.cde__radio');
    const input = radio?.querySelector('.cde__radio-input[data-action="select-delivery-speed"]')
      || (event.target.matches?.('.cde__radio-input[data-action="select-delivery-speed"]') ? event.target : null);
    if (!input || input.disabled) return;
    const wasSelected = radio?.classList.contains('is-selected');
    const price = parseInt(input.dataset.price || '0', 10);
    this.syncDeliverySpeed(input.value, price);
    // Both label clicks and radio changes reach this handler. The visual state is updated
    // synchronously above, so the second event (and re-clicking the selection) stays quiet.
    if (!wasSelected) {
      this.trackDeliveryEvent('custom_cart_delivery_speed_selected', {
        delivery_speed: input.value,
        shipping_tier: radio?.querySelector('.cde__radio-name')?.textContent.trim() || input.dataset.shortTitle || '',
        shipping_rate_index: parseInt(input.dataset.rateIndex, 10) || 0,
        shipping_price: Number.isFinite(price) ? price : 0
      });
    }
  }

  syncDeliverySpeed(value, price, { persist = true } = {}) {
    // If price not supplied, read it from the first matching radio we find
    if (price === undefined) {
      const radio = document.querySelector(`.cde__speed-options input[value="${value}"]`);
      price = parseInt(radio?.dataset.price || '0', 10);
    }
    const money = window.Shopify?.formatMoney?.(price, window.theme?.moneyFormat) || `${price / 100} kr`;
    const priceText = price === 0 ? '0 kr' : money;

    document.querySelectorAll('.cde__speed-options').forEach((group) => {
      const panel = group.dataset.speedGroup;
      group.querySelectorAll('.cde__radio').forEach((r) => {
        const radio = r.querySelector('input');
        const match = radio?.value === value;
        r.classList.toggle('is-selected', match);
        if (radio) radio.checked = match;
      });

      const selectedRadio = group.querySelector('input:checked') || group.querySelector(`input[value="${value}"]`);
      const shippingLabelEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-shipping-label]`);
      if (shippingLabelEl && selectedRadio) {
        shippingLabelEl.textContent = this.getShippingSubtotalLabel(selectedRadio, shippingLabelEl);
      }

      const shippingEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-shipping]`);
      if (shippingEl) {
        shippingEl.dataset.rawPrice = String(price);
        shippingEl.textContent = priceText;
      }
      if (panel) this.updateGiftboxTotal(panel);
    });

    if (persist) {
      this.markCdeSelectionDirty();
      this.scheduleCdeCartSync();
    }
  }

  // ── Giftbox product selection ─────────────────────────────────────────────
  handleGiftboxSelect(event) {
    this.selectGiftbox(event.target, { trackSelection: true });
  }

  selectGiftbox(input, { trackSelection = false } = {}) {
    if (!input || input.disabled) return;
    const productId = input.value;
    const panel = input.closest('[data-giftbox-carousel]')?.dataset.giftboxCarousel
      || input.closest('[data-panel]')?.dataset.panel;
    if (!panel) return;
    const card = document.querySelector(`[data-giftbox-carousel="${panel}"] [data-giftbox-product="${productId}"]`);
    const wasSelected = card?.classList.contains('is-selected');

    // Update card visual state
    document.querySelectorAll(`[data-giftbox-carousel="${panel}"] .cde-card[data-giftbox-product]`).forEach(c => {
      c.classList.toggle('is-selected', c.dataset.giftboxProduct === productId);
    });

    // Update packaging subtotal
    const price = parseInt(card?.dataset.productPrice || '0', 10);
    const packagingEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-packaging]`);
    if (packagingEl) {
      packagingEl.dataset.rawPrice = String(price);
      packagingEl.textContent = price === 0 ? '0 kr' : (window.Shopify?.formatMoney?.(price, window.theme?.moneyFormat) || `${price / 100} kr`);
    }
    this.updateGiftboxTotal(panel);

    const handle = input.dataset.productHandle || card?.dataset.productHandle;
    if (handle) this.preloadVariantImages(panel, handle);

    const saved = this.getSavedGiftboxSelection();
    if (saved.productId && saved.productId !== String(productId)) {
      this.setSavedGiftboxSelection(productId, '');
      this.setRibbonSubtotal(panel, 0);
      this.updateGiftboxTotal(panel);
    } else if (!saved.productId) {
      this.setSavedGiftboxSelection(productId, saved.variantId || '');
    }

    this.markCdeSelectionDirty();
    this.scheduleCdeCartSync();
    this.slideCdeCarouselToEl(input);
    if (trackSelection && !wasSelected) {
      this.trackDeliveryEvent('custom_cart_delivery_giftbox_selected', {
        giftbox_product_id: String(productId),
        giftbox_product_name: card?.querySelector('.cde-card__name')?.textContent.trim() || '',
        giftbox_price: Number.isFinite(price) ? price : 0
      });
    }
  }

  slideCdeCarouselToEl(el) {
    const slide = el?.closest?.('.swiper-slide');
    const root = slide?.closest('swiper-init');
    const swiper = typeof root?.getSwiper === 'function' ? root.getSwiper()?.swiperElement : null;
    if (!swiper || !slide) return;
    const index = swiper.slides ? [...swiper.slides].indexOf(slide) : -1;
    if (index >= 0) swiper.slideTo(index);
  }

  getVariantsMap(panel) {
    if (this.variantsMapCache[panel]) return this.variantsMapCache[panel];
    try {
      const scriptEl = document.querySelector(`script[data-giftbox-variants="${panel}"]`);
      this.variantsMapCache[panel] = JSON.parse(scriptEl?.textContent || '{}');
    } catch (e) {
      console.error('[CDE] Failed to parse variants map', e);
      this.variantsMapCache[panel] = {};
    }
    return this.variantsMapCache[panel];
  }

  preloadVariantImages(panel, handle) {
    if (!handle) return;
    const variants = this.getVariantsMap(panel)[handle];
    if (!variants?.length) return;

    variants.forEach((variant) => {
      const src = variant.image;
      if (!src || this.preloadedImages.has(src)) return;
      this.preloadedImages.add(src);
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    });
  }

  handleRibbonPreload(event) {
    const panel = event.target.closest('[data-action="giftbox-to-ribbon"]')?.dataset.panel;
    if (!panel) return;
    const selected = this.getSelectedGiftbox(panel);
    if (selected?.handle) this.preloadVariantImages(panel, selected.handle);
  }

  // ── Bow variants (swap into same swiper) ───────────────────────────────────
  getSelectedGiftbox(panel) {
    const checked = document.querySelector(`[name="giftbox_product_${panel}"]:checked`);
    if (!checked) return null;
    const card = checked.closest('[data-giftbox-product]');
    return {
      id: checked.value,
      handle: checked.dataset.productHandle || card?.dataset.productHandle || ''
    };
  }

  loadBowVariants(panel) {
    const selected = this.getSelectedGiftbox(panel);
    const giftboxEl = document.querySelector(`[data-giftbox-panel="${panel}"]`);
    const swiperEl = document.querySelector(`[data-giftbox-carousel="${panel}"]`);
    const track = swiperEl?.querySelector('[data-cde-carousel-track]');

    const fallbackHandle = swiperEl?.querySelector('[data-product-handle]')?.dataset.productHandle;
    const handle = selected?.handle || fallbackHandle;

    if (!handle || !track || !giftboxEl) {
      console.error('[CDE] loadBowVariants: missing handle/track/panel', { panel, handle, track, giftboxEl });
      return;
    }

    // Read pre-rendered variants map from inline <script type="application/json">
    const variantsMap = this.getVariantsMap(panel);
    const allVariants = variantsMap[handle];
    if (!allVariants?.length) {
      console.error('[CDE] No variants found for handle', handle, variantsMap);
      return;
    }

    // Cache product slides before first swap
    if (!this.productSlidesCache[panel]) {
      this.productSlidesCache[panel] = track.innerHTML;
    }

    // A variant is truly unavailable only if inventory is depleted AND continue-selling is off
    const isTrulyUnavailable = (v) => !v.available && v.inventory_policy !== 'continue';

    const bowVariants = [...allVariants].sort((a, b) => a.price - b.price);
    const firstAvailable = bowVariants.find(v => !isTrulyUnavailable(v));
    const saved = this.getSavedGiftboxSelection();
    const savedOnThisProduct = saved.variantId && bowVariants.some((v) => String(v.id) === String(saved.variantId));
    const restoreId = savedOnThisProduct ? saved.variantId : (firstAvailable ? String(firstAvailable.id) : '');
    const money = (price) => window.Shopify?.formatMoney?.(price, window.theme?.moneyFormat) || `${price / 100} kr`;
    const freeLabel = window.theme?.strings?.cart?.delivery_free
      || document.querySelector('.cde__free')?.textContent?.trim()
      || 'FREE';

    // Price shown = delta above the cheapest variant that can be bought (delta 0 = no-bow / free option)
    const buyable = bowVariants.filter((v) => !isTrulyUnavailable(v));
    const minPrice = Math.min(...(buyable.length ? buyable : bowVariants).map(v => Number(v.price)));

    track.innerHTML = bowVariants.map((variant, i) => {
      const isSelected = restoreId && String(variant.id) === String(restoreId);
        const delta = Math.max(0, Number(variant.price) - minPrice);
        const isFree = delta === 0;
        const priceText = isFree ? freeLabel : `+${money(delta)}`;
        const imageSrc = variant.image || '';
        const unavailable = isTrulyUnavailable(variant);
        return `
        <div class="swiper-slide${unavailable ? ' cde-card--unavailable' : ''}" data-variant-id="${variant.id}">
          <label class="cde-card${isSelected ? ' is-selected' : ''}"
            data-bow-variant="${variant.id}"
            data-variant-price="${delta}"
            data-panel="${panel}">
            <input type="radio" name="bow_variant_${panel}" value="${variant.id}"
              class="cde-card__input"
              data-action="select-bow-variant"
              ${isSelected ? 'checked' : ''}
              ${unavailable ? 'disabled' : ''}>
            ${imageSrc ? `<div class="cde-card__image"><img src="${imageSrc}" alt="${variant.title || ''}" loading="eager" decoding="async"${i < 3 ? ' fetchpriority="high"' : ''}></div>` : ''}
            <span class="cde-card__name u-s2">${variant.title || ''}</span>
            <span class="cde-card__price u-s3${isFree ? ' cde__free' : ''}">${priceText}</span>
          </label>
        </div>`;
    }).join('');

    const restoreVariant = bowVariants.find((v) => restoreId && String(v.id) === String(restoreId)) || firstAvailable;
    if (restoreVariant) {
      const ribbonEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-ribbon]`);
      if (ribbonEl) {
        const delta = Number(restoreVariant.price) - minPrice;
        ribbonEl.dataset.rawPrice = String(delta);
        ribbonEl.textContent = delta === 0 ? '0 kr' : money(delta);
      }
      this.updateGiftboxTotal(panel);
    }

    requestAnimationFrame(() => this.reinitCdeSwipers(swiperEl));
    if (!savedOnThisProduct) this.markCdeSelectionDirty();
  }

  restoreProductSlides(panel) {
    const swiperEl = document.querySelector(`[data-giftbox-carousel="${panel}"]`);
    const track = swiperEl?.querySelector('[data-cde-carousel-track]');
    const html = this.productSlidesCache[panel];
    if (!track || !html) return;
    track.innerHTML = html;
    this.reinitCdeSwipers(swiperEl);
  }

  handleBowSelect(event) {
    const input = event.target;
    if (!input || input.disabled) return;
    const panel = input.closest('[data-giftbox-carousel]')?.dataset.giftboxCarousel
      || input.closest('[data-panel]')?.dataset.panel;
    if (!panel) return;
    const variantId = input.value;
    const card = input.closest('.cde-card');
    const wasSelected = card?.classList.contains('is-selected');
    document.querySelectorAll(`[data-giftbox-carousel="${panel}"] .cde-card[data-bow-variant]`).forEach(c => {
      c.classList.toggle('is-selected', c.dataset.bowVariant === variantId);
    });
    const price = parseInt(card?.dataset.variantPrice || '0', 10);
    const ribbonEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-ribbon]`);
    if (ribbonEl) {
      ribbonEl.dataset.rawPrice = String(price);
      ribbonEl.textContent = price === 0 ? '0 kr' : (window.Shopify?.formatMoney?.(price, window.theme?.moneyFormat) || `${price / 100} kr`);
    }
    this.updateGiftboxTotal(panel);
    this.setSavedGiftboxSelection(this.getSelectedGiftbox(panel)?.id || '', variantId);
    this.markCdeSelectionDirty();
    this.scheduleCdeCartSync();
    this.slideCdeCarouselToEl(input);
    if (!wasSelected) {
      this.trackDeliveryEvent('custom_cart_delivery_bow_selected', {
        bow_variant_id: String(variantId),
        bow_variant_name: card?.querySelector('.cde-card__name')?.textContent.trim() || '',
        bow_price: Number.isFinite(price) ? price : 0
      });
    }
  }

  // ── Sub-step navigation (swap same swiper) ────────────────────────────────
  handleToRibbon(event) {
    const panel = event.target.closest('[data-action="giftbox-to-ribbon"]')?.dataset.panel;
    if (!panel) return;
    const fromStep = this.getDeliveryTrackingStep();
    const selected = this.getSelectedGiftbox(panel);
    if (selected?.handle) this.preloadVariantImages(panel, selected.handle);
    this.setGiftboxSubstep(panel, 'ribbon');
    this.loadBowVariants(panel);
    if (fromStep !== 'ribbon') this.trackDeliveryEvent('custom_cart_delivery_step_changed', {
      from_step: fromStep, to_step: 'ribbon', direction: 'next'
    });
  }

  handleToDesign(event) {
    const panel = event.target.closest('[data-action="ribbon-to-design"]').dataset.panel;
    const fromStep = this.getDeliveryTrackingStep();
    this.setGiftboxSubstep(panel, 'design');
    this.restoreProductSlides(panel);
    this.markCdeSelectionDirty();
    this.scheduleCdeCartSync();
    if (fromStep !== 'packaging') this.trackDeliveryEvent('custom_cart_delivery_step_changed', {
      from_step: fromStep, to_step: 'packaging', direction: 'back'
    });
  }

  handleConfirm(event) {
    const action = event.target.closest('[data-action]')?.dataset.action;
    return this.continueToPayment(action === 'giftbox-skip-ribbon' ? 'skip_ribbon' : 'confirm_ribbon');
  }

  async continueToPayment(action = 'payment_navigation') {
    if (this._deliveryContinuePromise) return this._deliveryContinuePromise;
    clearTimeout(this.qliroSyncTimer);
    const state = this.getDeliveryTrackingState();
    const attempt = ++state.attempt;
    this.trackDeliveryEvent('custom_cart_delivery_continue', { continue_action: action, attempt_number: attempt });
    if (!this.applyRecipientDetailsGate({ force: true })) {
      this.trackDeliveryEvent('custom_cart_delivery_error', {
        error_type: 'validation', invalid_field: this._firstInvalidRecipientField || 'recipient_email',
        validation_trigger: 'continue', attempt_number: attempt
      });
      const input = document.querySelector(`[data-delivery-field="${this._firstInvalidRecipientField || 'recipient_email'}"]`);
      input?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      input?.focus?.({ preventScroll: true });
      return;
    }
    window._cdeAttributesDirty = false;
    const target = this.cartItems.element.querySelector('#qliro-checkout-container');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const needsCartSync = this.cdeSelectionDirty || this.pendingCdeSync || this._cdeSyncPromise;
    this._deliveryActiveAttempt = attempt;
    this._deliveryContinuePromise = (async () => {
      try {
        if (needsCartSync) await this.runCdeCartSync();
        if (this._cdeAbort || this.cdeSelectionDirty || this.pendingCdeSync || !this.validateRecipientDetails({ show: false })) {
          this.trackDeliveryEvent('custom_cart_delivery_error', { error_type: 'continue_interrupted', attempt_number: attempt });
          return;
        }
        if (!state.completed) {
          state.completed = true;
          this.trackDeliveryEvent('custom_cart_delivery_completed', { continue_action: action, attempt_number: attempt });
        }
      } catch (e) {
        window._cdeAttributesDirty = true;
        if (e.scheduleValidation) {
          this.trackDeliveryEvent('custom_cart_delivery_error', { error_type: 'continue_interrupted', attempt_number: attempt });
        } else if (this._deliveryFailedAttempt !== attempt) {
          this.trackDeliveryEvent('custom_cart_delivery_error', { error_type: 'continue_sync', attempt_number: attempt });
        }
        if (!e.scheduleValidation) console.warn('[CDE] continueToPayment sync failed', e);
      }
    })();
    try { await this._deliveryContinuePromise; } finally {
      this._deliveryContinuePromise = null;
      this._deliveryActiveAttempt = 0;
    }
  }

  markCdeSelectionDirty() {
    if (this._cdeSeeding) return;
    this.getDeliveryTrackingState().completed = false;
    this.cdeSelectionDirty = true;
    // An edit during a save needs another pass before checkout can use the saved snapshot.
    if (this._cdeSyncPromise) this.pendingCdeSync = true;
    window._cdeAttributesDirty = true;
  }

  scheduleCdeCartSync() {
    if (this._cdeSeeding) return;
    // Show "Updating checkout..." straight away; the sync itself waits for the customer to pause.
    this.cartItems.qliroManager.setUpdating(true);
    this._lastCdeChangeAt = Date.now();
    clearTimeout(this.qliroSyncTimer);
    this._cdeSyncQueued = true;
    this.qliroSyncTimer = setTimeout(() => {
      this._cdeSyncQueued = false;
      this.runCdeCartSync().catch((e) => {
        if (!e.scheduleValidation) console.warn('[CDE] scheduled sync failed', e);
      });
    }, QLIRO_UPDATE_PAUSE_MS);
  }

  /** Resolve once the customer has made no delivery-box change for QLIRO_UPDATE_PAUSE_MS. */
  async waitForCustomerPause() {
    for (;;) {
      const left = (this._lastCdeChangeAt || 0) + QLIRO_UPDATE_PAUSE_MS - Date.now();
      if (left <= 0) return;
      await new Promise((resolve) => setTimeout(resolve, left));
    }
  }

  async runCdeCartSyncAndRefreshQliro() {
    return this.runCdeCartSync();
  }

  async runCdeCartSync() {
    this.pendingCdeSync = true;
    if (this._cdeSyncPromise) return this._cdeSyncPromise;

    this._cdeSyncPromise = (async () => {
      try { window.q1?.lock?.(); } catch (e) { /* ignore */ }
      try {
        // A change can arrive while the cart is saved or while Qliro is updated; either way it gets
        // another round (a change during the Qliro update used to be flagged and then dropped).
        // Every round first waits for the customer to pause, so quick changes become one update.
        do {
          await this.waitForCustomerPause();
          if (this._cdeAbort) return;
          // The Qliro update unlocks the widget; lock it again for the next round.
          try { window.q1?.lock?.(); } catch (e) { /* ignore */ }
          this.pendingCdeSync = false;
          this.cdeSyncInFlight = true;
          const cart = await this.syncCdeAddonsToCart();
          // Interrupted by a quantity change: the choice is still unsaved, and gets synced after it.
          if (this._cdeAbort) return;
          this.cdeSelectionDirty = false;
          if (cart?.token) this.cartItems.qliroManager.currentCartToken = cart.token;
          this.cartItems.qliroManager.markSnippetStale();
          // Changed again while saving: wait for the pause and save again before touching Qliro.
          if (this.pendingCdeSync || this._cdeAbort) continue;
          // Update the open checkout before it is unlocked, so nobody pays against the old order.
          await this.cartItems.qliroManager.markSnippetStale({ immediate: true });
        } while (this.pendingCdeSync && !this._cdeAbort);
      } catch (e) {
        this.cdeSelectionDirty = true;
        this.cartItems.qliroManager.setUpdating(false);
        if (e.scheduleValidation) {
          // The date picker can still be open. Keep checkout blocked without treating an
          // unfinished field as a failed request or revealing unrelated untouched errors.
          this.applyRecipientDetailsGate({ show: false });
        } else {
          this.trackDeliveryEvent('custom_cart_delivery_error', {
            error_type: e?.refusedPackaging ? 'packaging_unavailable' : 'cart_sync',
            attempt_number: this._deliveryActiveAttempt || 0,
            http_status: Number.isInteger(e?.status) ? e.status : 0
          });
          this._deliveryFailedAttempt = this._deliveryActiveAttempt || 0;
          console.warn('[CDE] Cart/Qliro sync failed', e);
          this.reportCdeSyncFailure(e);
        }
        throw e;
      } finally {
        this.cdeSyncInFlight = false;
        this._cdeSyncPromise = null;
        // The widget on the page now: a rebuild during the sync replaces window.q1.
        try { window.q1?.unlock?.(); } catch (e) { /* ignore */ }
        // unlock() above releases the gate's lock as well — put it back if still invalid.
        this._recipientGateLocked = false;
        this.applyRecipientDetailsGate({ show: false });
      }
    })();

    return this._cdeSyncPromise;
  }

  /**
   * A delivery-box save failed: tell the customer, and keep the box and Qliro honest about what the
   * cart holds. Packaging Shopify refused is dropped and the next one on sale is picked (which saves
   * again). Anything else stays unsaved for "Continue to payment" to retry, and Qliro is brought in
   * line with the cart as it is now.
   */
  reportCdeSyncFailure(e) {
    let msg;
    if (e?.refusedPackaging) {
      this.dropRefusedPackaging(e.refusedPackaging);
      msg = TranslationUtils.get('sections.cart.cde_packaging_sold_out', 'The packaging you chose just sold out. Please check your packaging choice.');
    } else {
      this.cartItems.qliroManager.markSnippetStale();
      msg = TranslationUtils.get('sections.cart.cde_sync_error', 'Could not update your cart options. Please try again.');
    }
    if (toastNotifications) toastNotifications.error(msg);
  }

  /** Treat a packaging variant Shopify refused as sold out for the rest of the visit. */
  dropRefusedPackaging({ panel, variantId }) {
    const map = this.getVariantsMap(panel);
    const handle = Object.keys(map).find((h) => map[h].some((v) => String(v.id) === String(variantId)));
    if (!handle) return;
    map[handle].forEach((v) => {
      if (String(v.id) === String(variantId)) {
        v.available = false;
        v.inventory_policy = 'deny';
      }
    });
    const buyable = map[handle].filter((v) => v.available || v.inventory_policy === 'continue');
    const giftboxEl = document.querySelector(`[data-giftbox-panel="${panel}"]`);
    const track = document.querySelector(`[data-giftbox-carousel="${panel}"] [data-cde-carousel-track]`);
    const packagingEl = document.querySelector(`[data-giftbox-subtotal="${panel}"] [data-subtotal-packaging]`);

    if (buyable.length) {
      // Same packaging, other variants left: its price is now the cheapest of those.
      const price = Math.min(...buyable.map((v) => Number(v.price)));
      const card = document.querySelector(`[data-giftbox-carousel="${panel}"] .cde-card[data-giftbox-product][data-product-handle="${handle}"]`);
      if (card) {
        card.dataset.productPrice = String(price);
        const priceEl = card.querySelector('.cde-card__price');
        if (priceEl) {
          const freeLabel = window.theme?.strings?.cart?.delivery_free
            || document.querySelector('.cde__free')?.textContent?.trim()
            || 'FREE';
          priceEl.classList.toggle('cde__free', price === 0);
          priceEl.textContent = price === 0 ? freeLabel : `+${this.formatCdeMoney(price)}`;
        }
        if (track && giftboxEl?.dataset.substep !== 'ribbon') this.productSlidesCache[panel] = track.innerHTML;
      }
      if (this.getSelectedGiftbox(panel)?.handle === handle && packagingEl) {
        packagingEl.dataset.rawPrice = String(price);
        packagingEl.textContent = this.formatCdeMoney(price);
      }
      // Ribbon step: redraw the ribbons without the sold-out one (and reprice the chosen ribbon).
      if (giftboxEl?.dataset.substep === 'ribbon') this.loadBowVariants(panel);
      this.updateGiftboxTotal(panel);
      this.markCdeSelectionDirty();
      this.scheduleCdeCartSync();
      return;
    }

    // The whole packaging is gone: grey its card out and move to the first one still on sale.
    if (giftboxEl?.dataset.substep === 'ribbon') {
      this.setGiftboxSubstep(panel, 'design');
      this.restoreProductSlides(panel);
    }
    const card = document.querySelector(`[data-giftbox-carousel="${panel}"] .cde-card[data-giftbox-product][data-product-handle="${handle}"]`);
    card?.closest('.swiper-slide')?.classList.add('cde-card--unavailable');
    const input = card?.querySelector('.cde-card__input');
    if (input) {
      input.checked = false;
      input.removeAttribute('checked');
      input.disabled = true;
    }
    card?.classList.remove('is-selected');
    if (track) this.productSlidesCache[panel] = track.innerHTML;
    this.selectFirstGiftboxCard(panel);
    if (!this.getSelectedGiftbox(panel)) {
      // Nothing left on sale: no packaging in the cart, none in the summary.
      if (packagingEl) {
        packagingEl.dataset.rawPrice = '0';
        packagingEl.textContent = this.formatCdeMoney(0);
      }
      this.updateGiftboxTotal(panel);
      this.markCdeSelectionDirty();
      this.scheduleCdeCartSync();
    }
  }

  /** Called from CartQliroManager after q1Ready fires — set up the q1 reference for lock/unlock. */
  setupQliroInteractionSync(q1) {
    if (!q1 || this._qliroInteractionQ1 === q1) return;
    this._qliroInteractionQ1 = q1;
    this._recipientGateLocked = false;
    this.applyRecipientDetailsGate({ show: false });
  }

  async syncCdeIfDirtyAndRefreshQliro() {
    if (!this.cdeSelectionDirty && !this.pendingCdeSync) return;
    await this.runCdeCartSync();
  }

  getActiveGiftPanelId() {
    const tab = document.querySelector('.cde__tab.is-active')?.dataset.tab;
    if (tab === 'premium_gift_packaging') return 'premium';
    if (tab === 'gift_packaging') return 'gift';
    return null;
  }

  getPackAllChecked(panel) {
    const el = panel
      ? document.querySelector(`[data-giftbox-panel="${panel}"] [data-action="toggle-pack-all"]`)
      : null;
    return el ? !!el.checked : true;
  }

  getExperienceQty(cart) {
    if (cart?.items) {
      return cart.items.reduce((n, i) => n + (CartUtils.isCdeAddon(i) ? 0 : (i.quantity || 0)), 0);
    }
    return Math.max(1, parseInt(document.querySelector('.cart-count')?.textContent, 10) || 1);
  }

  getCdeRecipientProps() {
    // The printer/fulfilment flow already reads the popup's address properties from experience
    // lines. Use that same contract; checkout's billing address belongs to the buyer.
    const readField = (name) => String(document.querySelector(`[data-delivery-field="${name}"]`)?.value || '').trim();
    const fieldsEl = document.querySelector('[data-recipient-fields]');
    const recipientVisible = fieldsEl && !fieldsEl.hidden;
    if (!recipientVisible) {
      return { _delivery_person: 'self' };
    }
    const email = readField('recipient_email');
    const name = `${readField('recipient_first_name')} ${readField('recipient_last_name')}`.trim();
    const props = { _delivery_person: 'recipient' };
    // A malformed address must not reach the line item: the backend would create the voucher without a recipient.
    if (email && CartDeliveryExperience.isValidEmail(email)) props._recipient_email = email;
    if (name) props._recipient_name = name;
    if (this.getActiveGiftPanelId()) {
      const address = readField('recipient_address');
      const city = readField('recipient_city');
      const zip = readField('recipient_zip');
      if (address) props._recipient_address = address;
      if (city) props._recipient_city = city;
      if (zip) props._recipient_zip = zip;
      if (address && city && zip) props._recipient_full_address = `${address}, ${city} ${zip}`;
    }
    if (this.isScheduleEnabled()) {
      const schedule = this.getScheduleValidation();
      // Never turn an enabled but unusable schedule into an immediate-delivery cart snapshot.
      if (!schedule.valid) {
        const error = new Error('Invalid scheduled voucher delivery');
        error.scheduleValidation = true;
        throw error;
      }
      props._scheduled_delivery = 'true';
      props._scheduled_date = schedule.iso;
    }
    return props;
  }

  /** Replace only delivery-owned properties so switching to self/digital removes stale addresses. */
  replaceCdeLineProperties(properties, next) {
    const updated = { ...properties };
    [...CDE_RECIPIENT_PROPERTIES, ...CDE_SCHEDULE_PROPERTIES, '_delivery_person', '_pack_all'].forEach((key) => { delete updated[key]; });
    return Object.assign(updated, next);
  }

  async postCartJs(path, body) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const text = await res.text().catch(() => '');
    if (!res.ok) {
      const error = new Error(`[CDE] ${path} failed (${res.status}): ${text}`);
      error.status = res.status;
      throw error;
    }
    try { return JSON.parse(text); } catch { return null; }
  }

  cartAttributesDiffer(current, patch) {
    const cur = current || {};
    return Object.keys(patch).some((k) => String(cur[k] ?? '') !== String(patch[k] ?? ''));
  }

  experienceNeedsStamp(item, extraProps) {
    const p = item?.properties || {};
    const keys = ['_delivery_type', '_delivery_person', '_pack_all', ...CDE_RECIPIENT_PROPERTIES, ...CDE_SCHEDULE_PROPERTIES];
    return keys.some((k) => {
      const want = extraProps[k];
      const have = p[k];
      if (want == null || want === '') return have != null && have !== '';
      return String(have) !== String(want);
    });
  }

  async stampExperienceLineProperties(cart, extraProps) {
    if (this._cdeAbort) return cart;
    let live = cart;
    const pending = (live?.items || []).filter((i) => !CartUtils.isCdeAddon(i) && this.experienceNeedsStamp(i, extraProps));
    for (const item of pending) {
      if (this._cdeAbort) return live;
      const current = (live?.items || []).find((i) => i.key === item.key)
        || CartUtils.findLineItem(live, item.key)
        || item;
      if (!this.experienceNeedsStamp(current, extraProps)) continue;
      const updated = this.replaceCdeLineProperties(current.properties || {}, extraProps);
      live = await this.postCartJs('/cart/change.js', {
        id: current.key,
        quantity: current.quantity,
        properties: updated
      }) || live;
    }
    return live;
  }

  getNoRibbonVariantId(panel) {
    const selected = this.getSelectedGiftbox(panel);
    if (!selected?.handle) return null;
    const variants = this.getVariantsMap(panel)?.[selected.handle];
    if (!variants?.length) return null;

    const isTrulyUnavailable = (v) => !v.available && v.inventory_policy !== 'continue';
    const cheapest = [...variants]
      .sort((a, b) => Number(a.price) - Number(b.price))
      .find((v) => !isTrulyUnavailable(v));
    return cheapest ? String(cheapest.id) : null;
  }

  /**
   * Packaging, ribbon and delivery are not remembered between visits (like the recipient): the
   * page always opens on Digital with nothing chosen. A cart from an earlier visit can still hold
   * its gift box and experience lines addressed for gift box delivery, which would reach Qliro and
   * the order while the page shows Digital. Remove the box and set those lines back to a voucher.
   */
  async clearStaleDeliveryChoice(cart) {
    let live = cart;
    const updates = {};
    (live?.items || []).forEach((i) => { if (CartUtils.isCdeAddon(i)) updates[i.key] = 0; });
    if (Object.keys(updates).length) {
      live = await this.postCartJs('/cart/update.js', { updates }) || live;
    }
    // Each change re-keys the line, so re-find the next stale one from the fresh cart each pass.
    for (let guard = 0; guard < 20; guard += 1) {
      // Lines in a gift box delivery group (added on the product page) keep their addressing.
      const item = (live?.items || []).find((i) => !CartUtils.isCdeAddon(i)
        && !i.properties?._delivery_group_id
        && (i.properties?._delivery_type === 'giftbox' || i.properties?._pack_all != null));
      if (!item) break;
      const props = Object.assign({}, item.properties || {});
      delete props._pack_all;
      props._delivery_type = 'voucher';
      live = await this.postCartJs('/cart/change.js', {
        id: item.key,
        quantity: item.quantity,
        properties: props
      }) || live;
    }
    return live;
  }

  getSelectedGiftboxVariantId(panel) {
    const giftboxEl = document.querySelector(`[data-giftbox-panel="${panel}"]`);
    if (!giftboxEl) return null;

    if (giftboxEl.dataset.substep === 'ribbon') {
      const bowChecked = document.querySelector(`[name="bow_variant_${panel}"]:checked`);
      return bowChecked?.value || this.getNoRibbonVariantId(panel);
    }

    const selected = this.getSelectedGiftbox(panel);
    const saved = this.getSavedGiftboxSelection();
    if (saved.variantId && selected?.id && String(saved.productId) === String(selected.id)) {
      const variants = this.getVariantsMap(panel)?.[selected.handle] || [];
      if (variants.some((v) => String(v.id) === String(saved.variantId))) return String(saved.variantId);
    }
    return this.getNoRibbonVariantId(panel);
  }

  async syncCdeAddonsToCart() {
    if (this._cdeAbort) return;
    const root = this.cartItems?.element;
    if (!root) return;

    let cart = await CartUtils.fetchCart();
    if (this._cdeAbort) return cart;
    // Do not mark delivery options as saved if Shopify's cart could not be read.
    if (!Array.isArray(cart?.items)) throw new Error('Could not read cart before saving delivery options');

    const activeTab = document.querySelector('.cde__tab.is-active')?.dataset.tab;
    // Never raise the error from a background sync; blur/change and "continue" do that.
    this.validateRecipientDetails({ show: false });
    const panel = this.getActiveGiftPanelId();
    const recipientProps = this.getCdeRecipientProps();
    const packAllChecked = this.getPackAllChecked(panel);
    const experienceQty = this.getExperienceQty(cart);
    const attributePatch = this.buildCdeShippingAttributes();
    const digital = activeTab === 'digital' || !panel;
    const experienceExtra = digital
      ? { _delivery_type: 'voucher', ...recipientProps }
      : {
          _delivery_type: 'giftbox',
          _pack_all: packAllChecked ? 'true' : 'false',
          ...recipientProps
        };

    // No experience left (removed in another tab, or with its delivery group): a box is never sold alone
    const desiredVariantId = digital || experienceQty === 0 ? null : this.getSelectedGiftboxVariantId(panel);
    const desiredQty = desiredVariantId ? (packAllChecked ? 1 : experienceQty) : 0;
    const desiredGiftProps = desiredVariantId
      ? {
          _item_type: 'giftbox',
          _cde_managed: '1',
          _delivery_type: 'giftbox',
          _pack_all: packAllChecked ? 'true' : 'false',
          ...recipientProps
        }
      : null;

    const addons = (cart.items || []).filter((i) => CartUtils.isCdeAddon(i));
    const matchingGift = desiredVariantId
      ? addons.find((i) => String(i.variant_id || i.id) === String(desiredVariantId))
      : null;
    const staleAddons = addons.filter((i) => !matchingGift || i.key !== matchingGift.key);

    const updates = {};
    staleAddons.forEach((i) => { updates[i.key] = 0; });
    const needAttrs = this.cartAttributesDiffer(cart.attributes, attributePatch);
    const needUpdates = Object.keys(updates).length > 0;
    if (needUpdates || needAttrs) {
      const body = {};
      if (needUpdates) body.updates = updates;
      if (needAttrs) body.attributes = attributePatch;
      cart = await this.postCartJs('/cart/update.js', body) || cart;
    }

    if (this._cdeAbort) return cart;

    if (desiredVariantId && matchingGift) {
      const liveGift = (cart.items || []).find((i) => i.key === matchingGift.key)
        || (cart.items || []).find((i) =>
          CartUtils.isCdeAddon(i) && String(i.variant_id || i.id) === String(desiredVariantId)
        );
      if (liveGift) {
        const p = liveGift.properties || {};
        const qtyDiff = (liveGift.quantity || 0) !== desiredQty;
        const nextProps = this.replaceCdeLineProperties(p, desiredGiftProps);
        const propsDiff = Object.keys({ ...p, ...nextProps }).some((k) => String(p[k] ?? '') !== String(nextProps[k] ?? ''));
        if (qtyDiff || propsDiff) {
          cart = await this.postCartJs('/cart/change.js', {
            id: liveGift.key,
            quantity: desiredQty,
            properties: nextProps
          }) || cart;
        }
      }
    } else if (desiredVariantId) {
      try {
        await this.postCartJs('/cart/add.js', {
          items: [{
            id: parseInt(desiredVariantId, 10),
            quantity: desiredQty,
            properties: desiredGiftProps
          }]
        });
      } catch (e) {
        // 422: Shopify refused the packaging — it sold out after the page was loaded.
        if (e.status === 422) e.refusedPackaging = { panel, variantId: desiredVariantId };
        throw e;
      }
    }

    // A slow box write can land after the last experience was removed: take the box out again
    if (desiredVariantId) {
      cart = await CartUtils.fetchCart() || cart;
      if (Array.isArray(cart?.items) && this.getExperienceQty(cart) === 0) {
        const orphans = {};
        cart.items.forEach((i) => { if (CartUtils.isCdeAddon(i)) orphans[i.key] = 0; });
        if (Object.keys(orphans).length) cart = await this.postCartJs('/cart/update.js', { updates: orphans }) || cart;
        return cart;
      }
    }

    if (this._cdeAbort) return cart;
    if (!digital && desiredVariantId) {
      this.setSavedGiftboxSelection(this.getSelectedGiftbox(panel)?.id || '', desiredVariantId);
    } else if (digital) {
      this.setSavedGiftboxSelection('', '');
    }
    return this.stampExperienceLineProperties(cart, experienceExtra);
  }

  /**
   * CDE delivery selection for APL → Qliro shipping webhook.
   * Prefer live radio (DOM), fall back to cart attributes.
   * @param {object} [ajaxCart]
   * @returns {{ price_ore: string, title: string, rate_index: string }|null}
   */
  static buildCdeShippingPayload(ajaxCart) {
    const activeTab = document.querySelector('.cde__tab.is-active')?.dataset.tab;
    if (activeTab === 'digital') return null;

    const panel = document.querySelector('.cde__panel.is-active [data-giftbox-panel]')?.dataset?.giftboxPanel
      || document.querySelector('[data-giftbox-panel]')?.dataset?.giftboxPanel;
    const speedRadio = panel
      ? document.querySelector(`[data-giftbox-panel="${panel}"] .cde__radio-input:checked[data-action="select-delivery-speed"]`)
      : document.querySelector('.cde__radio-input:checked[data-action="select-delivery-speed"]');

    if (speedRadio) {
      return {
        price_ore: String(parseInt(speedRadio.dataset.price || '0', 10) || 0),
        title: (speedRadio.dataset.shortTitle || '').trim() || 'Leverans',
        rate_index: String(parseInt(speedRadio.dataset.rateIndex || '1', 10) || 1)
      };
    }

    const attrs = ajaxCart?.attributes || {};
    if (attrs._cde_shipping_price_ore !== undefined && attrs._cde_shipping_price_ore !== '') {
      return {
        price_ore: String(attrs._cde_shipping_price_ore),
        title: attrs._cde_shipping_title || 'Leverans',
        rate_index: String(attrs._cde_shipping_rate_index || '1')
      };
    }

    return null;
  }

  buildCdeShippingAttributes() {
    const activeTab = document.querySelector('.cde__tab.is-active')?.dataset.tab;
    const panel = this.getActiveGiftPanelId();
    const speedRadio = panel
      ? document.querySelector(`[data-giftbox-panel="${panel}"] .cde__radio-input:checked[data-action="select-delivery-speed"]`)
      : null;

    const useShipping = activeTab !== 'digital' && speedRadio;
    const priceOre = useShipping ? String(parseInt(speedRadio.dataset.price || '0', 10) || 0) : '';
    const shortTitle = useShipping
      ? (speedRadio.dataset.shortTitle || '').trim()
      : '';
    const rateIndex = useShipping
      ? String(parseInt(speedRadio.dataset.rateIndex || '1', 10) || 1)
      : '';

    return {
      _cde_tab: activeTab || 'digital',
      // The next cart load opens this tab again (premium counts as gift box: it reopens Gift packaging).
      ...(activeTab ? { _delivery_preference: activeTab === 'digital' ? 'digital' : 'giftbox' } : {}),
      _cde_session_at: '',
      _cde_pack_all: this.getPackAllChecked(panel) ? 'true' : 'false',
      ...(useShipping
        ? {
            _cde_shipping_price_ore: priceOre,
            _cde_shipping_title: shortTitle || 'Leverans',
            _cde_shipping_rate_index: rateIndex || '1',
            _cde_shipping_variant: ''
          }
        : {
            _cde_shipping_price_ore: '',
            _cde_shipping_title: '',
            _cde_shipping_rate_index: '',
            _cde_shipping_variant: ''
          })
    };
  }

  /**
   * Sync CDE delivery selection to cart attributes for APL → Qliro Shipping.
   * Price/title/rate index come from the theme delivery_option block (not a product).
   */
  async syncCdeShippingCartAttributes(cart) {
    try {
      const attributes = this.buildCdeShippingAttributes();
      if (cart && !this.cartAttributesDiffer(cart.attributes, attributes)) return cart;
      return await this.postCartJs('/cart/update.js', { attributes });
    } catch (e) {
      console.warn('[CDE] Could not sync shipping cart attributes', e);
      throw e;
    }
  }

  setGiftboxSubstep(panel, step) {
    const giftboxEl = document.querySelector(`[data-giftbox-panel="${panel}"]`);
    if (!giftboxEl) return;
    giftboxEl.dataset.substep = step;

    const title = giftboxEl.querySelector('[data-cde-carousel-title]');
    if (title) {
      title.textContent = step === 'ribbon'
        ? (title.dataset.titleRibbon || title.textContent)
        : (title.dataset.titleDesign || title.textContent);
    }

    // Packaging step: "Continue to payment" + "Next: Choose ribbon"; ribbon step: Back + continue.
    const skipBtn = giftboxEl.querySelector('[data-action="giftbox-skip-ribbon"]');
    const backBtn = giftboxEl.querySelector('[data-action="ribbon-to-design"]');
    const chooseBtn = giftboxEl.querySelector('[data-action="giftbox-to-ribbon"]');
    const confirmBtn = giftboxEl.querySelector('[data-action="giftbox-confirm"]');
    if (skipBtn) skipBtn.hidden = step === 'ribbon';
    if (backBtn) backBtn.hidden = step !== 'ribbon';
    if (chooseBtn) chooseBtn.hidden = step === 'ribbon';
    if (confirmBtn) confirmBtn.hidden = step !== 'ribbon';
  }

  // ── Address ───────────────────────────────────────────────────────────────
  static isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  /**
   * datetime-local supplies wall time, never the device's timezone. Sample Stockholm offsets
   * on both sides of that date, then round-trip each candidate through Intl. Zero matches is
   * a DST gap; two is an overlap. Neither is safe to normalize or guess for a gift delivery.
   */
  static resolveStockholmSchedule(value, now = Date.now()) {
    if (!value) return { valid: false, error: 'required' };
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(value));
    if (!match) return { valid: false, error: 'invalid' };
    const parts = match.slice(1).map((part) => Number(part || 0));
    const [year, month, day, hour, minute, second] = parts;
    const asUtc = (p) => {
      const date = new Date(0);
      date.setUTCFullYear(p[0], p[1] - 1, p[2]);
      date.setUTCHours(p[3], p[4], p[5], 0);
      return date.getTime();
    };
    const wall = asUtc(parts);
    const date = new Date(wall);
    if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59
      || date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) {
      return { valid: false, error: 'invalid' };
    }
    const inStockholm = (instant) => {
      const values = Object.fromEntries(CDE_STOCKHOLM_FORMATTER.formatToParts(new Date(instant))
        .filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
      return ['year', 'month', 'day', 'hour', 'minute', 'second'].map((key) => values[key]);
    };
    const offsets = new Set([-36, -12, 0, 12, 36].map((hours) => {
      const instant = wall + hours * 3600000;
      return asUtc(inStockholm(instant)) - instant;
    }));
    const candidates = [...offsets].map((offset) => wall - offset)
      .filter((instant) => inStockholm(instant).every((part, index) => part === parts[index]));
    if (candidates.length !== 1) return { valid: false, error: 'local_time' };
    const instant = candidates[0];
    if (instant <= now) return { valid: false, error: 'past' };
    return { valid: true, instant, iso: new Date(instant).toISOString() };
  }

  isScheduleAvailable() {
    const fields = document.querySelector('[data-recipient-fields]');
    return !!fields && !fields.hidden && !this.getActiveGiftPanelId();
  }

  isScheduleEnabled() {
    return this.isScheduleAvailable() && !!document.querySelector('[data-action="toggle-cde-schedule"]')?.checked;
  }

  getScheduleValidation() {
    return CartDeliveryExperience.resolveStockholmSchedule(document.querySelector('[data-delivery-field="delivery_datetime"]')?.value || '');
  }

  syncSchedulePlaceholder(focused = false) {
    const input = document.querySelector('[data-delivery-field="delivery_datetime"]');
    document.querySelector('[data-cde-datetime-placeholder]')?.classList.toggle('hidden', focused || !!input?.value);
  }

  syncScheduleVisibility() {
    const available = this.isScheduleAvailable();
    const toggle = document.querySelector('[data-action="toggle-cde-schedule"]');
    const input = document.querySelector('[data-delivery-field="delivery_datetime"]');
    const block = document.querySelector('[data-cde-schedule]');
    if (block) block.hidden = !available;
    if (toggle) {
      toggle.disabled = !available;
      if (!available) toggle.checked = false;
    }
    const enabled = available && !!toggle?.checked;
    if (input) {
      input.disabled = !enabled;
      input.required = enabled;
      if (!enabled) input.value = '';
    }
    toggle?.setAttribute('aria-expanded', enabled ? 'true' : 'false');
    const field = document.querySelector('[data-cde-schedule-field]');
    if (field) field.hidden = !enabled;
    const help = document.querySelector('[data-cde-schedule-help]');
    if (help) help.hidden = !enabled;
    if (!enabled) {
      this.setRecipientFieldError('delivery_datetime', '');
      this._recipientTouchedFields?.delete('delivery_datetime');
      this.watchScheduleExpiry(null);
    }
    this.syncSchedulePlaceholder();
  }

  handleScheduleToggle() {
    this.syncScheduleVisibility();
    this.markCdeSelectionDirty();
    this.applyRecipientDetailsGate({ show: true });
    this.scheduleCdeCartSync();
  }

  /** Revalidate while payment is open, including when a background tab wakes up late. */
  watchScheduleExpiry(instant) {
    if (this._scheduleExpiryInstant === instant) return;
    clearTimeout(this._scheduleExpiryTimer);
    this._scheduleExpiryInstant = instant;
    if (instant == null) return;
    this._scheduleExpiryTimer = setTimeout(() => {
      this._scheduleExpiryInstant = null;
      if (!this._recipientTouchedFields) this._recipientTouchedFields = new Set();
      this._recipientTouchedFields.add('delivery_datetime');
      this.applyRecipientDetailsGate({ show: true });
    }, Math.min(Math.max(1, instant - Date.now() + 1), 2147483647));
  }

  stopScheduleExpiryWatch() {
    this.watchScheduleExpiry(null);
    if (this._scheduleRecheckHandler) document.removeEventListener('visibilitychange', this._scheduleRecheckHandler);
    this._scheduleRecheckHandler = null;
  }

  setRecipientFieldError(name, message) {
    const input = document.querySelector(`[data-delivery-field="${name}"]`);
    const field = input?.closest?.('.field');
    // The schedule error is outside its bordered field so longer translations cannot overlap.
    const errorEl = (name === 'delivery_datetime' && document.getElementById('cde-schedule-error'))
      || field?.querySelector('[data-recipient-field-error]');
    field?.classList.toggle('field--error', !!message);
    input?.setAttribute?.('aria-invalid', message ? 'true' : 'false');
    if (errorEl) {
      errorEl.textContent = message || '';
      errorEl.setAttribute('aria-hidden', message ? 'false' : 'true');
    }
  }

  /**
   * Digital delivery requires email. Physical delivery matches the popup: email is optional,
   * but an entered email must still be well-formed.
   * With show=false an existing error is only cleared, never raised (used while typing).
   */
  validateRecipientEmail({ show = true } = {}) {
    const input = document.querySelector('[data-delivery-field="recipient_email"]');
    const fieldsEl = document.querySelector('[data-recipient-fields]');
    if (!input || !fieldsEl || fieldsEl.hidden) return true;

    const value = String(input.value || '').trim();
    const i18n = document.querySelector('[data-cart-delivery-experience]')?.dataset || {};
    let message = '';
    if (!value && !this.getActiveGiftPanelId()) message = i18n.i18nRecipientEmailRequired || "Enter the recipient's email address";
    else if (value && !CartDeliveryExperience.isValidEmail(value)) message = i18n.i18nRecipientEmailInvalid || 'Enter a valid email address';

    if (!message || show) this.setRecipientFieldError('recipient_email', message);
    return !message;
  }

  /** Validate the fields shown for this delivery type, keeping untouched-field errors quiet. */
  validateRecipientDetails({ show = false, force = false } = {}) {
    this._firstInvalidRecipientField = null;
    const fields = document.querySelector('[data-recipient-fields]');
    if (!fields || fields.hidden) return true;
    const reveal = (name) => force || !!(show && this._recipientTouchedFields?.has(name));
    let valid = this.validateRecipientEmail({ show: reveal('recipient_email') });
    if (!valid) this._firstInvalidRecipientField = 'recipient_email';
    const physical = !!this.getActiveGiftPanelId();
    const i18n = document.querySelector('[data-cart-delivery-experience]')?.dataset || {};
    for (const name of CDE_PHYSICAL_REQUIRED_FIELDS) {
      const input = document.querySelector(`[data-delivery-field="${name}"]`);
      const missing = physical && !String(input?.value || '').trim();
      const message = missing ? (i18n.i18nRecipientFieldRequired || 'This field is required') : '';
      if (!message || reveal(name)) this.setRecipientFieldError(name, message);
      if (missing) {
        valid = false;
        if (!this._firstInvalidRecipientField) this._firstInvalidRecipientField = name;
      }
    }
    const schedule = this.isScheduleEnabled() ? this.getScheduleValidation() : null;
    let scheduleMessage = '';
    if (schedule && !schedule.valid) {
      scheduleMessage = schedule.error === 'local_time'
        ? (i18n.i18nScheduleLocalTime || 'This Stockholm time does not exist or occurs twice. Choose a different time.')
        : (i18n.i18nScheduleRequired || 'Delivery date and time is required and must be in the future');
      valid = false;
      if (!this._firstInvalidRecipientField) this._firstInvalidRecipientField = 'delivery_datetime';
    }
    if (!scheduleMessage || reveal('delivery_datetime')) this.setRecipientFieldError('delivery_datetime', scheduleMessage);
    this.watchScheduleExpiry(schedule?.valid ? schedule.instant : null);
    return valid;
  }

  syncRecipientFieldVisibility() {
    this.syncScheduleVisibility();
    const physical = !!this.getActiveGiftPanelId();
    const postalFields = document.querySelector('[data-recipient-postal-fields]');
    if (postalFields) postalFields.hidden = !physical;
    const email = document.querySelector('[data-delivery-field="recipient_email"]');
    if (email) email.required = !physical;
    for (const name of CDE_PHYSICAL_REQUIRED_FIELDS) {
      const input = document.querySelector(`[data-delivery-field="${name}"]`);
      if (!input) continue;
      input.required = physical;
      if (CDE_POSTAL_FIELDS.includes(name)) input.disabled = !physical;
      if (!physical) this.setRecipientFieldError(name, '');
    }
  }

  /**
   * The embedded Qliro widget arms itself as soon as it scrolls into view, so gating only the
   * "continue to payment" button is bypassable: the customer can pay in the widget without ever
   * pressing it. Hold the widget locked while the recipient address is missing or malformed.
   * Re-apply after anything that calls q1.unlock(), e.g. a cart sync.
   */
  applyRecipientDetailsGate({ show = false, force = false } = {}) {
    // Never raise the error before the customer has been anywhere near the field; the notice
    // in place of the checkout already explains why they cannot pay yet. `force` is for the
    // explicit attempt to continue.
    const ok = this.validateRecipientDetails({ show, force });
    this.setCheckoutBlocked(!ok);
    // A valid address can still be unsaved during the debounce or a failed request. Keep the
    // payment widget locked until the existing cart/Qliro sync has persisted the latest details.
    const shouldLock = !ok || !!(this.cdeSelectionDirty || this.pendingCdeSync || this.cdeSyncInFlight);
    const q1 = window.q1 || this._qliroInteractionQ1;
    if (!q1) {
      this._recipientGateLocked = shouldLock;
      return ok;
    }
    try {
      if (shouldLock && !this._recipientGateLocked) {
        q1.lock?.();
        this._recipientGateLocked = true;
      } else if (!shouldLock && this._recipientGateLocked) {
        q1.unlock?.();
        this._recipientGateLocked = false;
      }
    } catch (e) {
      /* a locked/unlocked widget is best effort; validation still blocks the button */
    }
    return ok;
  }

  /**
   * Recipient details are deliberately not remembered: the form always opens on "deliver to me".
   * A cart from before that was true can still carry them, and a line item still addressed to
   * someone else would send the voucher there while the form says otherwise. Clear both.
   */
  async clearStaleRecipientState(cart) {
    let live = cart;
    const attrs = live?.attributes || {};
    const staleKeys = ['delivery_person', 'recipient_email', 'recipient_first_name', 'recipient_last_name', ...CDE_POSTAL_FIELDS, 'recipient_full_address', 'schedule_delivery', 'delivery_datetime'];
    if (staleKeys.some((k) => String(attrs[k] || '') !== '')) {
      const cleared = {};
      staleKeys.forEach((k) => { cleared[k] = ''; });
      live = await this.postCartJs('/cart/update.js', { attributes: cleared }) || live;
    }

    const isStale = (item) => {
      const p = item?.properties || {};
      // Old popup groups retain their own delivery details. Only the inline cart starts fresh.
      if (p._delivery_group_id) return false;
      return p._delivery_person === 'recipient' || [...CDE_RECIPIENT_PROPERTIES, ...CDE_SCHEDULE_PROPERTIES].some((key) => p[key] != null);
    };
    // Each change re-keys the line, so re-find the next stale one from the fresh cart each pass.
    for (let guard = 0; guard < 20; guard += 1) {
      const item = (live?.items || []).find(isStale);
      if (!item) break;
      const props = Object.assign({}, item.properties || {});
      [...CDE_RECIPIENT_PROPERTIES, ...CDE_SCHEDULE_PROPERTIES].forEach((key) => { delete props[key]; });
      props._delivery_person = 'self';
      live = await this.postCartJs('/cart/change.js', {
        id: item.key,
        quantity: item.quantity,
        properties: props
      }) || live;
    }
    return live;
  }

  /**
   * Swap the checkout for a short reason while the delivery details are unusable. Locking alone
   * left the widget looking like it was still loading, with nothing to act on.
   */
  setCheckoutBlocked(blocked) {
    const notice = document.querySelector('[data-checkout-blocked]');
    const container = document.getElementById('qliro-checkout-container');
    if (notice) notice.hidden = !blocked;
    if (container) container.hidden = blocked;
  }

  /** Show or hide the recipient block. Shared by both buttons and the on-load restore. */
  setRecipientMode(on) {
    const self = document.querySelector('[data-address-self]');
    const recipient = document.querySelector('[data-address-recipient]');
    const fields = document.querySelector('[data-recipient-fields]');
    if (self) self.hidden = on;
    if (recipient) recipient.hidden = !on;
    if (fields) fields.hidden = !on;
    this.syncRecipientFieldVisibility();
    const heading = document.querySelector('[data-cde-what-title]');
    if (heading) {
      const next = on ? heading.dataset.titleRecipient : heading.dataset.titleSelf;
      if (next) heading.textContent = next;
    }
  }

  handleDeliverToRecipient() {
    const previousPerson = this.getDeliveryTrackingContext().delivery_person;
    this.setRecipientMode(true);
    this.markCdeSelectionDirty();
    // Blocks checkout straight away, but stays quiet: the customer has not touched the field yet,
    // and the notice in place of the checkout is the explanation.
    this.applyRecipientDetailsGate({ show: true });
    // The backend reads recipient details from the line items in the checkout snapshot, so this
    // must re-stamp the lines and refresh the Qliro snapshot like every other CDE change.
    this.scheduleCdeCartSync();
    if (previousPerson !== 'gift_receiver') this.trackDeliveryEvent('custom_cart_delivery_person_selected');
  }

  handleDeliverToSelf() {
    const previousPerson = this.getDeliveryTrackingContext().delivery_person;
    this.setRecipientMode(false);
    document.querySelectorAll('[data-delivery-field]').forEach((el) => { el.value = ''; });
    this._recipientTouchedFields = new Set();
    this._recipientTrackedInvalid = new Set();
    ['recipient_email', ...CDE_PHYSICAL_REQUIRED_FIELDS].forEach((name) => this.setRecipientFieldError(name, ''));
    this.markCdeSelectionDirty();
    this.applyRecipientDetailsGate({ show: true });
    this.scheduleCdeCartSync();
    if (previousPerson !== 'buyer') this.trackDeliveryEvent('custom_cart_delivery_person_selected');
  }

  handleAddressFieldInput(event) {
    if (event?.target?.dataset?.deliveryField === 'delivery_datetime') this.syncSchedulePlaceholder(true);
    // Use the same debounce as every delivery choice, but mark the edit immediately so a
    // quick "Continue" cannot skip it and checkout cannot use the previous recipient address.
    this.markCdeSelectionDirty();
    this.scheduleCdeCartSync();
    this.applyRecipientDetailsGate({ show: false });
    // Re-arm a previously reported error once corrected, without reporting on each keystroke.
    this.trackRecipientFieldValidation(event?.target, { report: false });
  }

  /**
   * Qty/line changes re-render cart HTML (always Digital + first packaging).
   * Keep the live CDE node; only swap copy that depends on experience count.
   */
  patchQtyDependentUi(existingCde, incomingCde) {
    if (!existingCde || !incomingCde) return;

    const existDigital = existingCde.querySelector('[data-panel="digital"]');
    const incomingDigital = incomingCde.querySelector('[data-panel="digital"]');
    if (existDigital && incomingDigital) {
      const existDesc = existDigital.querySelector('.cde__section-desc');
      const incomingDesc = incomingDigital.querySelector('.cde__section-desc');
      if (existDesc && incomingDesc) existDesc.replaceWith(incomingDesc.cloneNode(true));

      const existSubtotal = existDigital.querySelector('.cde__subtotal');
      const incomingSubtotal = incomingDigital.querySelector('.cde__subtotal');
      if (existSubtotal && incomingSubtotal) {
        let node = existSubtotal.nextElementSibling;
        while (node) {
          const next = node.nextElementSibling;
          node.remove();
          node = next;
        }
        let incomingNode = incomingSubtotal.nextElementSibling;
        while (incomingNode) {
          const next = incomingNode.nextElementSibling;
          existDigital.appendChild(incomingNode.cloneNode(true));
          incomingNode = next;
        }
      }
    }

    existingCde.querySelectorAll('[data-giftbox-panel]').forEach((existPanel) => {
      const incomingPanel = incomingCde.querySelector(
        `[data-giftbox-panel="${existPanel.dataset.giftboxPanel}"]`
      );
      if (!incomingPanel) return;

      const subtotal = existPanel.querySelector('[data-giftbox-subtotal]');
      const existPack = existPanel.querySelector('.cde-giftbox__checkbox-row');
      const incomingPack = incomingPanel.querySelector('.cde-giftbox__checkbox-row');
      if (incomingPack && !existPack && subtotal) {
        subtotal.before(incomingPack.cloneNode(true));
      } else if (!incomingPack && existPack) {
        existPack.remove();
      }

      // Gift-message nudge or multi-item note: take the fresh one, so the nudge's
      // "Add / Edit gift message" state follows the cart (as on the digital tab).
      const existDisc = existPanel.querySelector(':scope > .cde__disclaimer');
      const incomingDisc = incomingPanel.querySelector(':scope > .cde__disclaimer');
      if (incomingDisc && existDisc) {
        existDisc.replaceWith(incomingDisc.cloneNode(true));
      } else if (incomingDisc) {
        existPanel.appendChild(incomingDisc.cloneNode(true));
      } else if (existDisc) {
        existDisc.remove();
      }
    });
  }

  // ── Pack all ──────────────────────────────────────────────────────────────
  handlePackAll(event) {
    const panel = event?.target?.closest('[data-giftbox-panel]')?.dataset.giftboxPanel;
    if (panel) this.updateGiftboxTotal(panel);
    this.markCdeSelectionDirty();
    this.scheduleCdeCartSync();
    if (panel && !event.target.disabled) {
      const checked = !!event.target.checked;
      const previous = this._trackedPackAll.get(panel);
      this._trackedPackAll.set(panel, checked);
      if (previous !== checked) this.trackDeliveryEvent('custom_cart_delivery_pack_all_toggled', { pack_all: checked });
    }
  }

  // ── Carousel (Swiper) ────────────────────────────────────────────────────
  reinitCdeSwipers(root) {
    if (!root) return;
    const nodes = root.matches?.('swiper-init')
      ? [root]
      : root.querySelectorAll?.('swiper-init.cde-carousel') || [];
    nodes.forEach((el) => {
      if (typeof el.initSwiper === 'function') {
        el.initSwiper();
      } else {
        // Element not yet upgraded — wait for definition then init
        customElements.whenDefined('swiper-init').then(() => el.initSwiper?.());
      }
    });
  }

  // ── Subtotal ──────────────────────────────────────────────────────────────
  updateGiftboxTotal(panel) {
    const container = document.querySelector(`[data-giftbox-subtotal="${panel}"]`);
    if (!container) return;
    const packaging = parseInt(
      container.querySelector('[data-subtotal-packaging]')?.dataset.rawPrice || '0', 10
    );
    const ribbon = parseInt(
      container.querySelector('[data-subtotal-ribbon]')?.dataset.rawPrice || '0', 10
    );
    const shipping = parseInt(
      container.querySelector('[data-subtotal-shipping]')?.dataset.rawPrice || '0', 10
    );
    const packAllChecked = this.getPackAllChecked(panel);
    const qty = packAllChecked ? 1 : this.getExperienceQty();
    const formatMoney = (ore) => ore === 0
      ? '0 kr'
      : window.Shopify?.formatMoney?.(ore, window.theme?.moneyFormat) || `${ore / 100} kr`;
    const packagingEl = container.querySelector('[data-subtotal-packaging]');
    const ribbonEl = container.querySelector('[data-subtotal-ribbon]');
    if (packagingEl) packagingEl.textContent = formatMoney(packaging * qty);
    if (ribbonEl) ribbonEl.textContent = formatMoney(ribbon * qty);
    const isMulti = !!container.closest('[data-giftbox-panel]')?.querySelector('[data-action="toggle-pack-all"]');
    const qtyText = isMulti ? ` x${qty}` : '';
    const packagingQtyEl = container.querySelector('[data-subtotal-qty="packaging"]');
    const ribbonQtyEl = container.querySelector('[data-subtotal-qty="ribbon"]');
    if (packagingQtyEl) packagingQtyEl.textContent = qtyText;
    if (ribbonQtyEl) ribbonQtyEl.textContent = qtyText;
    // Packaging and ribbon rows are listed only when they cost something (design 30.09);
    // shipping is always listed. Hiding never changes the subtotal, which uses the raw prices.
    const hideRow = (el, amount) => {
      const row = el?.closest('.cde__subtotal-row');
      if (row) row.hidden = amount === 0;
    };
    hideRow(packagingEl, packaging * qty);
    hideRow(ribbonEl, ribbon * qty);

    if (this.getActiveGiftPanelId() === panel) {
      this.setSummaryExtra((packaging + ribbon) * qty + shipping);
    }
  }

  /** Add the active tab's packaging, ribbon and shipping to the cart subtotal. */
  setSummaryExtra(amount) {
    const totalEl = document.querySelector('[data-cde-part="summary-total"] .cart-totals__row--total .cart-totals__value');
    if (!totalEl) return;
    totalEl.setAttribute('data-cde-extra', String(Math.max(0, parseInt(amount, 10) || 0)));
    CartUtils.renderSubtotal(totalEl);
  }

  /** Recompute the subtotal for whichever tab is active; the digital tab adds nothing. */
  refreshSummaryTotal() {
    const panel = this.getActiveGiftPanelId();
    if (panel) this.updateGiftboxTotal(panel);
    else this.setSummaryExtra(0);
  }

  /**
   * The recipient block, the discount code block and the subtotal row exist once and follow the
   * active tab (design 30.09 shows them on every tab). Moving them keeps element lookups unique and
   * keeps whatever the customer typed.
   */
  mountSharedParts(panelEl) {
    if (!panelEl) return;
    ['recipient', 'summary-top', 'summary-total'].forEach((name) => {
      const part = document.querySelector(`[data-cde-part="${name}"]`);
      const slot = panelEl.querySelector(`[data-cde-slot="${name}"]`);
      if (part && slot && part.parentNode !== slot) slot.appendChild(part);
    });
    const giftbox = panelEl.dataset.panel !== 'digital';
    document.querySelectorAll('[data-cde-part="recipient"] [data-note-digital]').forEach((note) => {
      const text = giftbox ? note.dataset.noteGiftbox : note.dataset.noteDigital;
      if (text) note.textContent = text;
    });
    this.syncRecipientFieldVisibility();
  }

  // ── Cart attribute save ───────────────────────────────────────────────────
}

// --- Campaign auto-apply ---
/**
 * Settle the live campaign before checkout mounts. Shopify's current codes are authoritative;
 * a previous success is never an opt-out. Only an explicit code removal suppresses this cart's
 * campaign for the session. Limits retry with fewer units, inactive campaigns on a later run,
 * and transient failures get at most three attempts with fresh-cart reconciliation between them.
 */
class CampaignAutoApply {
  constructor(cartItems) {
    this.cartItems = cartItems;
    this.memory = new Map();
  }

  readConfig() {
    const el = this.cartItems.element.querySelector('[data-campaign-auto-apply]');
    if (!el) return null;
    try {
      this.lastConfig = JSON.parse(el.textContent);
      return this.lastConfig;
    } catch (error) {
      return null;
    }
  }

  /**
   * Refusal toast in the store language, chosen by errorCode (never the backend's English message).
   * ErrorUtils has no placeholder support, so the formatted text is passed as the fallback of a key
   * that is deliberately not in the locales.
   */
  static toastRefusal(errorCode, remaining, limit) {
    const messages = {
      CAMPAIGN_NOT_ACTIVE: ['campaign_not_active', 'The campaign is over, so the campaign price was not applied.'],
      CAMPAIGN_ALLOCATION_EXCEEDED: ['campaign_allocation_exceeded', 'Only {{ remaining }} left at the campaign price, so it could not be applied to your cart. Lower the quantity to get it.'],
      CAMPAIGN_CUSTOMER_LIMIT: ['campaign_customer_limit', 'At most {{ limit }} vouchers per customer at the campaign price, so it could not be applied to your cart. Lower the quantity to get it.']
    };
    const [key, fallback] = messages[errorCode] || ['campaign_not_applied', 'The campaign price could not be applied to your cart.'];
    const message = TranslationUtils.format(`sections.cart.${key}`, fallback, { remaining: remaining ?? '', limit: limit ?? '' });
    ErrorUtils.warning('sections.cart', 'campaign_refusal_formatted', message);
  }

  key(config = this.readConfig()) {
    return config?.code && this.cartItems.currentCartToken
      ? `campaignAutoApply:${this.cartItems.currentCartToken}:${String(config.code).toUpperCase()}` : null;
  }

  remember(key, value) {
    this.memory.set(key, value);
    try { sessionStorage.setItem(key, value); } catch (_) { /* In-memory guards still apply. */ }
  }

  recalled(key) {
    if (this.memory.has(key)) return this.memory.get(key);
    try { return sessionStorage.getItem(key); } catch (_) { return null; }
  }

  additionState() {
    try {
      const state = JSON.parse(sessionStorage.getItem('campaignCartAdds'));
      if (Number.isSafeInteger(state?.generation) && state.generation >= 0 && state.variants && typeof state.variants === 'object') return state;
    } catch (_) { /* No successful addition evidence available. */ }
    return { generation: 0, variants: {} };
  }

  rememberRemoval(code) {
    // Also works while the live blob is absent (paused/sold out).
    const key = this.key({ code });
    if (key) this.remember(`${key}:removed`, JSON.stringify({ generation: this.additionState().generation }));
  }

  isRemovalActive(key, config) {
    const removedKey = `${key}:removed`;
    const marker = this.recalled(removedKey);
    if (!marker) return false;
    const additions = this.additionState();
    let baseline;
    try { baseline = JSON.parse(marker)?.generation; } catch (_) { /* Legacy marker. */ }
    if (!Number.isSafeInteger(baseline) || baseline < 0) {
      // Legacy '1' has no baseline: existing additions cannot undo that explicit choice.
      this.remember(removedKey, JSON.stringify({ generation: additions.generation }));
      return true;
    }
    const readded = (config.variant_ids || []).some((id) => {
      const generation = additions.variants[String(id)];
      return Number.isSafeInteger(generation) && generation > baseline && generation <= additions.generation;
    });
    if (!readded) return true;
    this.memory.delete(removedKey);
    try { sessionStorage.removeItem(removedKey); } catch (_) { /* Conservative if persistence is blocked. */ }
    return false;
  }

  recordRefusal(key, errorCode, units) {
    if (errorCode === 'CAMPAIGN_ALLOCATION_EXCEEDED' || errorCode === 'CAMPAIGN_CUSTOMER_LIMIT') {
      this.remember(`${key}:exceeded`, String(units));
    }
    // Inactive may mean paused: a later cart refresh/visit must be allowed to try again.
    // Gift-card conflicts likewise recover after the customer removes the gift card.
  }

  handleNotice(notice) {
    const config = this.readConfig() || this.lastConfig;
    const key = this.key(config);
    if (key) this.recordRefusal(key, notice.code, config.units);
  }

  // The proxy intentionally returns HTTP 200 for operation failures; inspect its error code too.
  static isTransientFailure(data, failure = null) {
    const status = Number(failure?.status || data?.status || data?.statusCode);
    const code = String(data?.errorCode || failure?.responseCode || '').toUpperCase();
    const retryableCodes = ['INTERNAL_SERVER_ERROR', 'CHECKOUT_SESSION_LOOKUP_FAILED', 'QLIRO_SYNC_FAILED',
      'SERVICE_UNAVAILABLE', 'NETWORK_ERROR', 'TIMEOUT', 'ETIMEDOUT', 'ECONNRESET', 'ECONNREFUSED',
      'EAI_AGAIN', 'REQUEST_TIMEOUT', 'RATE_LIMITED'];
    return status >= 500 || status === 408 || status === 429 || retryableCodes.includes(code)
      || (failure ? !status : !data);
  }

  async run() {
    if (this.inFlight) {
      this.runAgain = true;
      return false;
    }
    this.inFlight = true;
    try {
      let exhaustedTransient = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        this.runAgain = false;
        const before = JSON.stringify([this.readConfig(), this.cartItems.discountManager.isProcessingDiscount]);
        const result = await this.attempt();
        exhaustedTransient = result === 'retry';
        if (result === true) return true;
        const changed = before !== JSON.stringify([this.readConfig(), this.cartItems.discountManager.isProcessingDiscount]);
        if (result !== 'retry' && !(this.runAgain && changed)) return false;
        if (attempt < 2 && result === 'retry') {
          await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
        }
      }
      console.warn('[Campaign] Auto-apply recovery exhausted; retry on the next cart change');
      if (exhaustedTransient) CampaignAutoApply.toastRefusal('CAMPAIGN_APPLY_FAILED');
      return false;
    } finally {
      this.inFlight = false;
      this.runAgain = false;
    }
  }

  hasCampaign(cart, code) {
    return (cart?.discount_codes || []).some((entry) =>
      String(entry.code).toUpperCase() === code && entry.applicable !== false);
  }

  async attempt() {
    const config = this.readConfig();
    const token = this.cartItems.currentCartToken;
    const manager = this.cartItems.discountManager;
    if (!config || config.live !== true || config.manual_code === true || !config.code || !(config.units > 0) || !token) return false;
    if (!(Date.parse(config.ends_at) > Date.now())) return false;
    if ((config.applied_codes || []).length > 0 && !this.reconcileNeeded) return false;
    if ((manager.appliedGiftCards || []).length > 0 || manager.isProcessingDiscount || manager.statusUnavailable) return false;

    const code = String(config.code).toUpperCase();
    const key = this.key(config);
    if (this.isRemovalActive(key, config)) return false;
    const exceededAt = Number(this.recalled(`${key}:exceeded`));
    if (exceededAt > 0 && config.units >= exceededAt) return false;

    const manualVersion = manager.manualMutationVersion || 0;
    let cart;
    try { cart = await this.cartItems.fetchCart(); } catch (_) { /* Retry below. */ }
    if (!cart) {
      console.warn('[Campaign] Could not read cart before auto-apply');
      return 'retry';
    }
    // A customer request can start AND finish while this read is in flight. Discard that older
    // snapshot even if processing is already false again; the next attempt reads the new choice.
    if (manualVersion !== (manager.manualMutationVersion || 0)) return 'retry';
    if ((cart.discount_codes || []).length > 0) {
      if (this.reconcileNeeded && this.hasCampaign(cart, code)) {
        this.reconcileNeeded = false;
        return true;
      }
      return false;
    }
    if (manager.isProcessingDiscount || this.isRemovalActive(key, config)) return false;
    if ((cart.items || []).some((item) => item.properties?._extend_type === 'voucher_exchange')) return false;

    let data, failure;
    try {
      this.pendingPost = window.apiService.post(`${window.proxyPath}/checkout-discounts`, {
        action: 'ADD', code, cart_token: token
      });
      data = await this.pendingPost;
    } catch (error) {
      failure = error;
      data = error?.response;
    } finally {
      this.pendingPost = null;
    }
    if (!failure && data && data.success !== false && data.businessRuleViolation !== true) return true;

    // ADD can update Shopify and then fail syncing the order. Never blindly post it again.
    this.reconcileNeeded = true;
    let freshCart;
    try { freshCart = await this.cartItems.fetchCart(); } catch (_) { /* Next attempt re-reads. */ }
    if (this.hasCampaign(freshCart, code)) {
      this.reconcileNeeded = false;
      return true;
    }

    const errorCode = String(data?.errorCode || '');
    if (data?.businessRuleViolation === true || errorCode.startsWith('CAMPAIGN_')) {
      this.recordRefusal(key, errorCode, config.units);
      CampaignAutoApply.toastRefusal(errorCode, data.remaining, data.limit);
      return false;
    }
    const status = Number(failure?.status || data?.status);
    const transient = CampaignAutoApply.isTransientFailure(data, failure);
    console.warn('[Campaign] Auto-apply did not complete', { transient, status: status || undefined });
    return transient ? 'retry' : false;
  }

}

// --- Main CartItems Class ---
class CartItems extends HTMLElement {
  constructor() {
    super();

    this.element = this;
    this.delegateElement = new Delegate(this.element);
    this.currentCartToken = null;
    this.currentCartItemCount = 0;

    // Initialize managers
    this.discountManager = new CartDiscountManager(this);
    this.qliroManager = new CartQliroManager(this.element, { context: 'default' });
    this.uiManager = new CartUIManager(this);
    this.deliveryManager = new CartDeliveryManager(this);
    this.giftMessage = new CartGiftMessage(this);
    this.deliveryExperience = new CartDeliveryExperience(this);
    this.campaignAutoApply = new CampaignAutoApply(this);

    this.bindEvents();
  }

  connectedCallback() {
    // Define the selector for your popup element.
    const popupSelector = 'delivery-popup';

    // Check if the delivery popup actually exists on the current page before starting the editor.
    const deliveryPopup = document.querySelector(popupSelector);

    if (deliveryPopup) {
      const deliveryEditor = new DeliveryEditor(popupSelector);
      // Store reference to the editor for later access
      deliveryPopup.deliveryEditor = deliveryEditor;
    }

    this.initializeCartFunctionality();
    this.deliveryExperience.setupDeliveryTracking();
  }

  disconnectedCallback() {
    this.deliveryExperience.destroyDeliveryTracking();
    this.deliveryExperience.stopScheduleExpiryWatch();
  }

  bindEvents() {
    // Clear existing event listeners to prevent duplicates after section refresh
    this.delegateElement.off();

    // Delegate events to managers
    this.deliveryManager.bindEvents(this.delegateElement);
    this.discountManager.bindEvents(this.delegateElement);
    this.giftMessage.bindEvents(this.delegateElement);
    this.deliveryExperience.bindEvents(this.delegateElement);

    // Add quantity update handlers
    this.delegateElement.on('click', '[data-action="update-item-quantity"]', this.handleQuantityButtonClick.bind(this));
    this.delegateElement.on('change', '[data-action="update-change-quantity"]', this.handleQuantityInputChange.bind(this));
    this.delegateElement.on('click', '[data-action="remove-item"]', this.handleRemoveItemClick.bind(this));

    // Payment navigation
    this.delegateElement.on('click', '[data-action="payment-nav-back"]', () => {
      const target = this.element.querySelector('.cart-items');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    this.delegateElement.on('click', '[data-action="payment-nav-continue"]', () => this.continueToPayment());
  }

  async continueToPayment() {
    await this.deliveryExperience.continueToPayment();
  }

  async handleQuantityButtonClick(event) {
    if (event) {
      event.preventDefault();
    }
    const button = event.target.closest('[data-action="update-item-quantity"]');
    if (!button) return;
    const quantity = parseInt(button.dataset.quantity);
    const lineId = button.dataset.lineId;
    const isMinus = button.dataset.actionType === 'minus';
    if (!lineId || isNaN(quantity)) {
      return;
    }
    if (isMinus && quantity === 1) {
      // Remove item or group
      const cart = await this.fetchCart();
      const targetItem = CartUtils.findLineItem(cart, lineId);
      if (targetItem && targetItem.properties && targetItem.properties._delivery_group_id) {
        // Remove group
        await this.deliveryManager.removeDeliveryGroup(targetItem.properties._delivery_group_id);
      } else {
        // Remove single item
        await this.updateItemQuantity(lineId, 0);
      }
      return;
    }
    await this.updateItemQuantity(lineId, quantity);
  }

  async handleQuantityInputChange(event) {
    const input = event.target;
    const quantity = parseInt(input.value);
    const lineId = input.dataset.lineId;

    if (!lineId || isNaN(quantity) || quantity < 0) {
      return;
    }

    await this.updateItemQuantity(lineId, quantity);
  }

  async handleRemoveItemClick(event) {
    if (event) {
      event.preventDefault();
    }

    const button = event.target.closest('[data-action="remove-item"]');
    const lineId = button?.dataset.lineId;

    if (!lineId) {
      return;
    }

    // Check if this item is part of a delivery group
    const cart = await this.fetchCart();
    const targetItem = CartUtils.findLineItem(cart, lineId);

    if (targetItem && targetItem.properties && targetItem.properties._delivery_group_id) {
      // Remove entire delivery group
      await this.deliveryManager.removeDeliveryGroup(targetItem.properties._delivery_group_id);
    } else {
      // Remove single item by setting quantity to 0
      await this.updateItemQuantity(lineId, 0);
    }
  }

  async updateItemQuantity(lineId, quantity) {
    const de = this.deliveryExperience;
    // A delivery-box change still waiting for its sync is paused here and picked up again afterwards.
    const resumeCdeSync = !!de && (de._cdeSyncQueued || !!de._cdeSyncPromise);
    let refreshed = false;
    try {

      // Show updating feedback
      this.uiManager.showCartUpdating(true);
      this.qliroManager.setUpdating(true);

      if (de) {
        de._cdeAbort = true;
        clearTimeout(de.qliroSyncTimer);
        if (de._cdeSyncPromise) {
          await Promise.race([
            de._cdeSyncPromise.catch(() => {}),
            new Promise((resolve) => setTimeout(resolve, 1500))
          ]);
        }
      }

      // Check if this item is part of a delivery group and needs special handling
      const cart = await this.fetchCart();
      if (!cart) {
        return;
      }

      const targetItem = CartUtils.findLineItem(cart, lineId);
      if (!targetItem) {
        return;
      }

      const liveKey = targetItem.key;
      let updates = {};

      // Check if this item is part of a delivery group
      const groupId = targetItem.properties?._delivery_group_id;
      if (groupId) {
        const packAll = targetItem.properties?._pack_all === 'true';

        if (packAll) {
          // pack_all = true: Only update the main product quantity
          // Accessories (giftbox/bow) remain at their current quantities
          updates[liveKey] = quantity;
        } else {
          // pack_all = false: Update quantities of all items in the group together
          const groupItems = cart.items.filter(item =>
            item.properties?._delivery_group_id === groupId
          );

          groupItems.forEach(item => {
            updates[item.key] = quantity;
          });

        }
      } else {
        updates[liveKey] = quantity;
        if (!CartUtils.isCdeAddon(targetItem)) {
          const newExpQty = cart.items.reduce((n, i) => {
            if (CartUtils.isCdeAddon(i)) return n;
            return n + (i.key === liveKey ? quantity : (i.quantity || 0));
          }, 0);
          const giftboxes = cart.items.filter((i) =>
            i.properties?._item_type === 'giftbox' && CartUtils.isCdeAddon(i)
          );
          if (giftboxes.length) {
            const packAllOff = giftboxes.some((i) => i.properties?._pack_all === 'false')
              || cart.attributes?._cde_pack_all === 'false';
            giftboxes.forEach((i, idx) => {
              if (newExpQty <= 0) updates[i.key] = 0;
              else if (packAllOff) updates[i.key] = idx === 0 ? newExpQty : 0;
            });
          }
        }
      }

      const updatedCart = await window.apiService.post('/cart/update.js', { updates });

      if (!updatedCart) {
        return;
      }

      // Refresh cart on-the-fly with HTML refresh since quantities changed
      refreshed = true;
      await this.refreshAfterUpdate(true);

    } catch (error) {
      console.warn('[CDE] Quantity update failed', error);
      this.uiManager.showCartUpdating(false); // Hide loading in case of error
      this.qliroManager.setUpdating(false);
    } finally {
      if (de) {
        de._cdeAbort = false;
        if (resumeCdeSync) de.scheduleCdeCartSync();
      }
      // Nothing changed (line gone, update refused): no Qliro update follows, so drop the text.
      if (!refreshed && !resumeCdeSync) this.qliroManager.setUpdating(false);
      this.uiManager.showCartUpdating(false);
    }
  }

  async initializeCartFunctionality() {
    if (isOrderCallbackMode()) return;
    this.qliroManager.beginInitialPreparation?.();
    try {
      // Reassigned below: each on-load reconciliation step returns a fresh cart.
      let cart;
      for (let attempt = 0; attempt < 3 && !cart; attempt++) {
        try { cart = await this.fetchCart(); } catch (_) { /* Bounded initial read recovery. */ }
        if (!cart && attempt < 2) await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
      }
      if (!cart?.token) throw new Error('Could not read cart during initialization');

      if (cart && cart.token) {
        this.currentCartToken = cart.token;
        this.currentCartItemCount = Number(cart.item_count || 0);

        // Fresh visit: drop a gift box (and gift box addressing) left by an earlier visit, before
        // the shipping attributes are written and before Qliro mounts.
        cart = await this.deliveryExperience.clearStaleDeliveryChoice(cart).catch((e) => {
          console.warn('[CDE] Could not clear the previous delivery choice');
          return cart;
        }) || cart;
        this.currentCartItemCount = Number(cart.item_count || 0);

        await this.deliveryExperience.syncCdeShippingCartAttributes(cart).catch((e) => {
          console.warn('[CDE] Could not sync shipping attrs on load');
        });
        cart = await this.deliveryExperience.clearStaleRecipientState(cart).catch((e) => {
          console.warn('[CDE] Could not clear stale recipient state');
          return cart;
        }) || cart;
        try {
          // Load existing applied discount codes and gift cards
          for (let attempt = 0; attempt < 3; attempt++) {
            if (await this.discountManager.loadExistingDiscounts() !== false || this.discountManager.statusRetryable === false) break;
            if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 150 * (attempt + 1)));
          }

          // ADD supports carts without an order. Settle Shopify and its displayed totals first.
          await this.applyCampaignCode();
          await this._campaignApplyPromise;
          // A customer request may have raced any preparation read or ADD. Its result wins.
          while (this.discountManager.isProcessingDiscount && this.discountManager.processingDone) {
            await this.discountManager.processingDone;
            await this.applyCampaignCode();
            await this._campaignApplyPromise;
          }
          this.qliroManager.endInitialPreparation?.();
          await this.qliroManager.ensureSnippet();
        } finally {
          // Gift box chosen on the product page: behave as if the customer clicked the rendered tab
          // (also when a step above failed, so the delivery choice is never lost).
          this.deliveryExperience.applyRenderedTab();
        }
      }
    } catch (error) {
      console.error('[Cart] Initialization failed; checkout may be unavailable');
      if (typeof toastNotifications !== 'undefined') {
        toastNotifications.error(TranslationUtils.get('sections.cart.error_generic', 'Could not prepare checkout. Please refresh the page.'));
      }
    } finally {
      this.qliroManager.endInitialPreparation?.();
    }
  }

  /** Applies the live campaign code (CampaignAutoApply), then refreshes exactly like a manual apply. */
  async applyCampaignCode() {
    if (this._campaignApplyPromise) {
      this.campaignAutoApply.runAgain = true;
      // HTML refresh re-enters here. Initialization separately joins the complete outer refresh.
      return;
    }
    const apply = async () => {
      if (!(await this.campaignAutoApply.run())) return;
      this.qliroManager.markSnippetStale({ reload: true });
      const subtotalElement = this.element.querySelector('.cart-totals__row--total .cart-totals__value');
      if (subtotalElement) subtotalElement.removeAttribute('data-original-total');
      const refreshed = await this.refreshAfterUpdate(true);
      if (refreshed === false && this.qliroManager.initialPreparation) {
        throw new Error('Could not reconcile campaign totals before checkout');
      }
    };
    this._campaignApplyPromise = apply().finally(() => { this._campaignApplyPromise = null; });
    return this._campaignApplyPromise;
  }

  async fetchCart() {
    return await CartUtils.fetchCart();
  }

  async refreshAfterUpdate(needsHTMLRefresh = true) {

    try {
      // Skip all refresh logic in order callback mode
      if (isOrderCallbackMode()) return;

      // First, check if we're on cart page and clean up any exchange products
      await this.cleanupExchangeProductsOnCartPage();

      if (needsHTMLRefresh) {
        // Try section rendering for HTML updates (quantity changes, item removal)

        const success = await this.tryHTMLSectionRefresh();
        if (success) {
          return true;
        }

        window.location.reload();
        return false;
      } else {
        // Use simpler data-only refresh for discounts/totals
        await this.dataOnlyRefresh();
      }

    } catch (error) {
      // Show error message instead of fallback reload
      this.discountManager.showDiscountStatus(`Cart update failed - ${error.message}`, 'error');
      return false;
    } finally {
      // Hide updating feedback
      this.uiManager.showCartUpdating(false);
    }
  }

  async tryHTMLSectionRefresh() {
    try {
      // Fetch updated cart data and cart page HTML in parallel

      // Fetch the cart page directly and parse cart-items from it
      const cartPageUrl = `${window.routes.localeRootUrl}/cart?_=${Date.now()}`;

      const [cartData, cartPageHtml] = await Promise.all([
        CartUtils.fetchCart(),
        fetch(cartPageUrl, {
          headers: {
            'Accept': 'text/html'
          }
        }).then(res => {
          if (!res.ok) {
            return null;
          }
          return res.text();
        })
      ]);

      if (!cartPageHtml) {
        return false;
      }

      if (cartData) {
        this.currentCartItemCount = Number(cartData.item_count || 0);
      }


      // Parse the cart page HTML and extract cart-items content
      if (cartPageHtml && cartPageHtml.length > 0) {

        const parser = new DOMParser();
        const newDoc = parser.parseFromString(cartPageHtml, 'text/html');
        const newCartContent = newDoc.querySelector('cart-items');

        if (newCartContent) {

          // Fetch gift cards BEFORE inserting HTML to prevent visual blinking
          let appliedGiftCards = [];
          let totalGiftCardAmount = 0;
          let giftCardsHTML = '';
          
          if (this.currentCartToken && Number(cartData?.item_count || 0) > 0) {
            try {
              const discountData = await window.apiService.post(`${window.proxyPath}/checkout-discounts`, {
                action: 'GET_STATUS',
                cart_token: this.currentCartToken
              });
              
              // Check if the request was successful
              if (discountData.success === false) {
                // Continue with empty gift cards array
              } else {
                // Extract applied gift cards from response
                if (discountData?.data?.cart?.appliedGiftCards) {
                  appliedGiftCards = discountData.data.cart.appliedGiftCards || [];
                } else if (discountData?.appliedGiftCards) {
                  appliedGiftCards = discountData.appliedGiftCards || [];
                } else if (discountData?.discounts?.giftCards) {
                  appliedGiftCards = discountData.discounts.giftCards || [];
                }
              }
              this.discountManager.appliedGiftCards = appliedGiftCards;
              
              // Check for gift card capping info
              let cappingInfo = null;
              try {
                const cappingInfoStr = sessionStorage.getItem('giftCardCappingInfo');
                if (cappingInfoStr) {
                  cappingInfo = JSON.parse(cappingInfoStr);
                }
              } catch (error) {
                // Silently fail - capping info not available
              }
              
              if (Array.isArray(appliedGiftCards)) {
                appliedGiftCards.forEach(gc => {
                  const { lastChars, giftCardId, amount } = SharedCartUtils.extractGiftCardData(gc);
                  
                  // Use capped amount if available, otherwise use original amount
                  let displayAmount = amount;
                  let isCapped = false;
                  let cappedBadgeHTML = '';
                  
                  if (cappingInfo && cappingInfo.willBeCapped) {
                    // Use the capped amount instead of original amount
                    displayAmount = cappingInfo.cappedAmount * 100; // Convert from SEK to cents
                    isCapped = true;
                    
                    // Add "Partially applied" badge
                    const cappedBadgeText = TranslationUtils.get('sections.cart.gift_card_capped_badge', 'Partially applied');
                    cappedBadgeHTML = `<span class="gift-card-capped-badge" style="margin-left: 8px; padding: 2px 6px; background: var(--warning-color, #fbbf24); color: var(--warning-text, #000); border-radius: 4px; font-size: 0.75rem; font-weight: 500;">${cappedBadgeText}</span>`;
                  }
                  
                  totalGiftCardAmount += displayAmount;
                  
                  // Pre-generate gift card HTML
                  const giftCardDisplay = displayAmount > 0 ? `-${Shopify.formatMoney(displayAmount, window.theme.moneyFormat)}` : '';
                  giftCardsHTML += `
                    <div class="cart-totals__row cart-totals__row--discount" data-gift-card="true" data-is-capped="${isCapped}">
                      <span class="cart-totals__label u-s3">
                        Gift Card (...${lastChars})${cappedBadgeHTML}
                        <button class="cart-discount__remove-link u-s4" data-action="remove-discount" data-giftcard-id-to-remove="${giftCardId}" title="Remove gift card ending in ${lastChars}">Remove code</button>
                      </span>
                      <span class="cart-totals__value cart-totals__value--discount u-s3">${giftCardDisplay}</span>
                    </div>
                  `;
                });
              }
            } catch (error) {
              // Silently continue with empty gift cards
            }
          }

          const newSubtotalElement = newCartContent.querySelector('.cart-totals__row--total .cart-totals__value');
          const displayTotal = CartUtils.getExperienceTotal(cartData);
          if (newSubtotalElement && displayTotal >= 0) {
            newSubtotalElement.setAttribute('data-original-total', displayTotal.toString());
            // Gift cards are subtracted, delivery extras added back by bindEvents() below
            newSubtotalElement.setAttribute('data-gift-card-total', String(totalGiftCardAmount));
            CartUtils.renderSubtotal(newSubtotalElement);
          }
          
          // Pre-insert gift card HTML into the applied codes container
          const appliedCodesContainer = newCartContent.querySelector('#cart-applied-codes');
          if (appliedCodesContainer) {
            // Preserve existing server-side discount codes
            const existingDiscounts = appliedCodesContainer.querySelectorAll('.cart-totals__row--discount:not([data-gift-card])');
            let existingDiscountsHTML = '';
            existingDiscounts.forEach(discount => {
              existingDiscountsHTML += discount.outerHTML;
            });
            appliedCodesContainer.innerHTML = existingDiscountsHTML + giftCardsHTML;
            
            // Show or hide container based on content
            const hasDiscounts = existingDiscounts.length > 0 || giftCardsHTML.length > 0;
            appliedCodesContainer.style.display = hasDiscounts ? 'block' : 'none';
          }

          const existingCde = this.element.querySelector('[data-cart-delivery-experience]');
          const incomingCde = newCartContent.querySelector('[data-cart-delivery-experience]');
          if (existingCde && incomingCde) {
            this.deliveryExperience.patchQtyDependentUi(existingCde, incomingCde);
            // The live delivery box is kept (it holds the customer's choices), so carry over the
            // freshly rendered discount rows and subtotal; the recipient block keeps its input.
            ['summary-top', 'summary-total'].forEach((name) => {
              const live = existingCde.querySelector(`[data-cde-part="${name}"]`);
              const freshPart = incomingCde.querySelector(`[data-cde-part="${name}"]`);
              if (live && freshPart) live.replaceWith(freshPart);
            });
          }

          const existingQliro = this.element.querySelector('#qliro-checkout-container');

          // Replace the inner content but keep the cart-items wrapper. The live Qliro widget is
          // never detached: moving an iframe reloads it, which wiped what the customer had typed.
          const fresh = document.createElement('template');
          fresh.innerHTML = newCartContent.innerHTML;
          const freshQliro = [...fresh.content.children].find((el) => el.id === 'qliro-checkout-container');
          if (existingQliro?.parentNode === this.element && freshQliro) {
            [...this.element.childNodes].forEach((node) => { if (node !== existingQliro) node.remove(); });
            while (fresh.content.firstChild !== freshQliro) existingQliro.before(fresh.content.firstChild);
            freshQliro.remove();
            this.element.append(fresh.content);
          } else {
            this.element.innerHTML = newCartContent.innerHTML;
            if (existingQliro) {
              this.element.querySelector('#qliro-checkout-container')?.replaceWith(existingQliro);
            }
          }

          if (existingCde && incomingCde) {
            const freshCde = this.element.querySelector('[data-cart-delivery-experience]');
            if (freshCde) freshCde.replaceWith(existingCde);
          }

          // Re-bind events after content update
          this.bindEvents();
          
          // Reinitialize FormValidator for discount field after DOM update
          this.discountManager.initFormValidator();

          // Update other components (skip updating totals and loading discounts since already applied)
          await this.updateCartComponents(cartData, true, totalGiftCardAmount > 0, true);
          this.qliroManager.markSnippetStale();

          // Quantity / line / code changes may let the campaign code apply now (fewer units, gift
          // card removed). Guarded: nothing happens while a code is applied or one is being added.
          await this.applyCampaignCode();

          return true;
        }
      }

      return false;

    } catch (error) {
      return false;
    }
  }

  async dataOnlyRefresh() {
    // Fetch updated cart data using shared utility
    const cartData = await CartUtils.fetchCart();

    if (!cartData) {
      throw new Error('Failed to fetch cart data');
    }

    // Update components without HTML changes
    await this.updateCartComponents(cartData);
  }

  async updateCartComponents(cartData, skipTotalsUpdate = false, skipDiscounts = false, skipQliro = false) {
    this.currentCartItemCount = Number(cartData.item_count || 0);
    // Also reconcile named lines after an HTML refresh, where totals are already rendered.
    CartUtils.renderSummaryItemRows(cartData);

    // Update global cart count
    if (window.theme) {
      window.theme.cartCount = cartData.item_count;
    }

    // Update header/nav cart count with the real cart; cart-page count excludes CDE addons
    const experienceCount = (cartData.items || []).reduce(
      (n, item) => n + (CartUtils.isCdeAddon(item) ? 0 : (item.quantity || 0)),
      0
    );
    this.element.querySelectorAll('.cart-count').forEach((element) => {
      element.textContent = experienceCount;
    });
    document.querySelectorAll('[data-cart-count], .header-cart-count').forEach((element) => {
      if (element.classList.contains('header-cart-btn')) return;
      element.textContent = cartData.item_count;
      if (cartData.item_count > 0) {
        element.style.display = '';
      } else {
        element.style.display = 'none';
      }
    });
    window.updateHeaderCartIcon?.(cartData.item_count);

    // Dispatch cart updated event for other components
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: {
        cartData: cartData,
        itemCount: cartData.item_count
      }
    }));

    // Update cart totals from fresh cart data (skip if already set in HTML to prevent blinking)
    if (!skipTotalsUpdate) {
      await this.uiManager.updateCartTotalsFromData(cartData);

      // Preserve original total from API before loading gift cards (to prevent double deduction after HTML refresh)
      const subtotalElement = this.element.querySelector('.cart-totals__row--total .cart-totals__value');
      if (subtotalElement) {
        const displayTotal = CartUtils.getExperienceTotal(cartData);
        subtotalElement.setAttribute('data-original-total', displayTotal.toString());
      }
    }

    // Load existing gift cards and apply adjustments (skip if already applied in HTML)
    if (!skipDiscounts) {
      await this.discountManager.loadExistingDiscounts();
    }

    this.qliroManager.markSnippetStale();

    // Update exchange popup discount summary if popup is open
    const exchangePopup = document.querySelector('#exchange-voucher-popup');
    if (exchangePopup && exchangePopup.style.display !== 'none' && typeof PopupUtils !== 'undefined') {
      const voucherValue = parseFloat(exchangePopup.dataset.voucherValue) || 0;
      await PopupUtils.updateExchangePopupTotals(exchangePopup, cartData, voucherValue);
    }
  }

  // Helper function to clean up exchange products when on cart page
  async cleanupExchangeProductsOnCartPage() {
    try {
      // Only run on cart page and not during exchange flow
      const isOnCartPage = window.location.href.indexOf('/cart') !== -1;
      const isExchangePage = window.location.href.includes('exchange');

      if (!isOnCartPage || isExchangePage) {
        return;
      }

      // Get current cart
      const cart = await window.apiService.get('/cart.js');
      if (!cart || !cart.items) {
        return;
      }

      // Find exchange products in cart (those with "Exchange Product :" in title)
      const exchangeItems = cart.items.filter(item =>
        item.title && item.title.includes('Exchange Product :')
      );

      if (exchangeItems.length === 0) {
        return;
      }

      // Prepare updates to remove exchange products (set quantity to 0)
      const updates = {};
      exchangeItems.forEach(item => {
        updates[item.key] = 0;
      });

      // Remove exchange products
      await window.apiService.post('/cart/update.js', { updates });

    } catch (error) {
      // Don't throw - we don't want to block the cart refresh
    }
  }
}

// Register the custom element
customElements.define('cart-items', CartItems);
